// src/alert/alert.tsx
import {
  CrossIcon,
  ExclamationTriangleIcon,
  InformationFilledIcon,
  ShootingStarIcon,
  TickRoundBackgroundIcon
} from "@vericus/cadmus-icons";

// ../../node_modules/.pnpm/@vanilla-extract+private@1.0.9/node_modules/@vanilla-extract/private/dist/vanilla-extract-private.esm.js
function getVarName(variable) {
  var matches = variable.match(/^var\((.*)\)$/);
  if (matches) {
    return matches[1];
  }
  return variable;
}
function get(obj, path) {
  var result = obj;
  for (var key of path) {
    if (!(key in result)) {
      throw new Error("Path ".concat(path.join(" -> "), " does not exist in object"));
    }
    result = result[key];
  }
  return result;
}
function walkObject(obj, fn) {
  var path = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : [];
  var clone = {};
  for (var key in obj) {
    var _value = obj[key];
    var currentPath = [...path, key];
    if (typeof _value === "string" || typeof _value === "number" || _value == null) {
      clone[key] = fn(_value, currentPath);
    } else if (typeof _value === "object" && !Array.isArray(_value)) {
      clone[key] = walkObject(_value, fn, currentPath);
    } else {
      console.warn('Skipping invalid key "'.concat(currentPath.join("."), '". Should be a string, number, null or object. Received: "').concat(Array.isArray(_value) ? "Array" : typeof _value, '"'));
    }
  }
  return clone;
}

// ../../node_modules/.pnpm/@vanilla-extract+dynamic@2.1.5/node_modules/@vanilla-extract/dynamic/dist/vanilla-extract-dynamic.esm.js
function assignInlineVars(varsOrContract, tokens) {
  var styles = {};
  if (typeof tokens === "object") {
    var _contract = varsOrContract;
    walkObject(tokens, (value2, path) => {
      if (value2 == null) {
        return;
      }
      var varName2 = get(_contract, path);
      styles[getVarName(varName2)] = String(value2);
    });
  } else {
    var _vars = varsOrContract;
    for (var varName in _vars) {
      var value = _vars[varName];
      if (value == null) {
        continue;
      }
      styles[getVarName(varName)] = value;
    }
  }
  Object.defineProperty(styles, "toString", {
    value: function value2() {
      return Object.keys(this).map((key) => "".concat(key, ":").concat(this[key])).join(";");
    },
    writable: false
  });
  return styles;
}

// src/alert/alert.tsx
import clsx from "clsx";

// src/text/text.tsx
import { css as css2, styled } from "styled-components";

// src/styles/animations.ts
import { css, keyframes } from "styled-components";

// src/styles/colors.ts
var lilac500 = "#6355F6";
var colors = {
  // named
  primary: lilac500,
  focus: "rgba(0, 133, 255, 0.30)",
  failure: "#DB0025",
  failureBg: "#FDEDF0",
  success: "#00A854",
  successBg: "#EFFAF6",
  alert: "#F17B2C",
  alertBg: "#FEF3EB",
  link: "#006FDE",
  linkBg: "#EBF1FF",
  divider: "#B7C0D0",
  // navy300
  icon: "#3F5982",
  // navy400
  // black
  black: "#000000",
  black100: "hsl(225, 15%, 27%)",
  black200: "hsl(225, 30%, 15%)",
  // white
  white: "#FFFFFF",
  white100: "hsl(0, 0%, 100%)",
  white200: "hsl(220, 60%, 97%)",
  // grey
  grey50: "hsla(220, 30%, 50%, 0.06)",
  grey100: "hsla(220, 30%, 50%, 0.15)",
  grey200: "hsla(220, 30%, 50%, 0.27)",
  grey300: "hsla(220, 30%, 50%, 0.36)",
  grey400: "hsla(220, 30%, 50%, 0.54)",
  grey500: "hsl(220, 25%, 48%)",
  grey600: "hsl(220, 37%, 36%)",
  // solid grey
  sgrey50: "#F5F7FA",
  sgrey100: "#E6EAF2",
  sgrey200: "#D2D9E7",
  sgrey300: "#C3CDDF",
  sgrey400: "#A5B3CF",
  // red
  red100: "hsl(0, 60%, 98%)",
  red200: "hsl(0, 85%, 95%)",
  red500: "hsl(350, 70%, 50%)",
  red800: "hsl(350, 80%, 25%)",
  redA500: "hsl(350, 100%, 65%)",
  // orange
  orange100: "hsl(20, 60%, 97%)",
  orange200: "hsl(20, 87%, 94%)",
  orange500: "hsl(25, 85%, 50%)",
  orange800: "hsl(10, 40%, 30%)",
  orangeA500: "hsl(25, 100%, 65%)",
  // yellow
  yellow100: "hsl(50, 60%, 97%)",
  yellow200: "hsl(50, 87%, 94%)",
  yellow500: "hsl(44, 100%, 62%)",
  yellow800: "hsl(40, 100%, 25%)",
  yellowA500: "hsl(55, 90%, 70%)",
  // lime
  lime100: "hsl(85, 30%, 96%)",
  lime200: "hsl(90, 50%, 93%)",
  lime500: "hsl(90, 85%, 35%)",
  lime800: "hsl(80, 60%, 15%)",
  limeA500: "hsl(90, 50%, 65%)",
  // green
  green100: "hsl(130, 35%, 96%)",
  green200: "hsl(130, 46%, 92%)",
  green500: "hsl(135, 80%, 35%)",
  green800: "hsl(130, 90%, 15%)",
  greenA500: "hsl(130, 50%, 60%)",
  // teal
  teal100: "hsl(160, 40%, 96%)",
  teal200: "hsl(160, 46%, 92%)",
  teal500: "hsl(160, 90%, 30%)",
  teal700: "hsl(150, 100%, 25%)",
  teal800: "hsl(160, 80%, 20%)",
  tealA500: "hsl(160, 50%, 60%)",
  // cyan
  cyan100: "hsl(185, 40%, 96%)",
  cyan200: "hsl(180, 51%, 92%)",
  cyan500: "hsl(180, 100%, 32%)",
  cyan800: "hsl(180, 90%, 15%)",
  cyanA500: "hsl(180, 55%, 60%)",
  // blue
  blue100: "hsl(210, 55%, 96%)",
  blue200: "hsl(210, 61%, 92%)",
  blue500: "hsl(210, 100%, 45%)",
  blue800: "hsl(220, 80%, 25%)",
  blueA500: "hsl(200, 100%, 50%)",
  // indigo
  indigo100: "hsl(240, 40%, 97%)",
  indigo200: "hsl(240, 58%, 94%)",
  indigo500: "hsl(245, 90%, 65%)",
  indigo800: "hsl(240, 100%, 20%)",
  indigoA500: "hsl(235, 80%, 70%)",
  // purple
  purple100: "hsl(265, 30%, 97%)",
  purple200: "hsl(272, 48%, 94%)",
  purple500: "hsl(280, 100%, 50%)",
  purple800: "hsl(270, 100%, 30%)",
  purpleA500: "hsl(270, 80%, 70%)",
  // pink
  pink100: "hsl(310, 30%, 97%)",
  pink200: "#FCF6FC",
  pink500: "hsl(310, 100%, 40%)",
  pink700: "#D095CE",
  pink800: "hsl(300, 100%, 20%)",
  pinkA500: "hsl(310, 75%, 60%)",
  // wine
  wine100: "hsl(335, 30%, 97%)",
  wine200: "hsl(330, 50%, 93%)",
  wine500: "hsl(330, 80%, 40%)",
  wine800: "hsl(320, 100%, 20%)",
  wineA500: "hsl(340, 100%, 65%)",
  // bluegrey
  bgrey200: "#FDFDFF",
  bgrey300: "#F7F7FF",
  bgrey400: "#F1F2FF",
  bgrey500: "#EBEDFF",
  bgrey700: "#BCBDCC",
  // navy
  navy200: "#E7EAEF",
  navy300: "#B7C0D0",
  navy400: "#3F5982",
  navy500: "#0F3063",
  navy700: "#071831",
  // lilac
  lilac200: "#EFEEFE",
  lilac300: "#D0CCFC",
  lilac400: "#9188F8",
  lilac500,
  lilac700: "#453BAC",
  // new teal
  nteal200: "#E7F5F4",
  nteal300: "#B8E3DE",
  nteal400: "#89D0C9",
  nteal500: "#18CBB8",
  nteal700: "#0F8175",
  // new pink
  npink200: "#FCF6FC",
  npink300: "#F8E4F7",
  npink400: "#EEC0EC",
  npink500: "#E8A6E5",
  npink700: "#D095CE",
  // poppy
  poppy200: "#FEF0EF",
  poppy300: "#FCD3D1",
  poppy400: "#FA9A93",
  poppy500: "#F86F66",
  poppy700: "#B22B22",
  poppy800: "#B6413A",
  poppy900: "#690D07",
  // neutral grey
  neutralgrey50: "rgba(89, 115, 166, 0.06)",
  neutralgrey100: "#5973A626",
  neutralgrey300: "rgba(89, 115, 166, 0.36)",
  neutralgrey400: "#5973A673",
  neutralgrey500: "#5B6F98",
  // neutral black
  neutralBlack300: "#3B404F",
  neutralBlack500: "#0F172E",
  // functional
  // TODO: Remove this two in favour of a shade of green / red
  functionalSuccess: "#00A854",
  functionalFail: "hsla(350, 100%, 43%, 1)",
  functionalAlert: "#FF974D",
  functionalFailBg: "hsla(349, 80%, 96%, 1)",
  functionalAlertBg: "hsla(25, 90%, 96%, 1)"
};

// src/styles/animations.ts
var easeOutCirc = "cubic-bezier(0.075, 0.82, 0.165, 1)";
var easeOutQuart = "cubic-bezier(0.165, 0.84, 0.44, 1)";
var easeInExpo = "cubic-bezier(0.95, 0.05, 0.795, 0.035)";
var fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
var fadeInTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
var fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(18px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
var ping = keyframes`
  from {
    box-shadow: 0 0 0 0px rgba(133,146,173,0.36);
    opacity: 1;
  }
  to {
    box-shadow: 0 0 0 9px rgba(133,146,173,0.36);
    opacity: 0;
  }
`;
var refocus = keyframes`
  from {
    box-shadow: 0 0 0 9px ${colors.focus};
    opacity: 0;
  }
  to {
    box-shadow: 0 0 0 3px ${colors.focus};
    opacity: 1;
  }
`;
var FadeInTop = css`
  animation: ${fadeInTop} 0.6s ${easeOutQuart};
`;
var FadeInLeft = css`
  animation: ${fadeInLeft} 0.6s ${easeOutQuart};
`;
var FadeIn = css`
  animation: ${fadeIn} 0.3s ${easeOutCirc};
`;
var FocusAnimation = css`
  animation: ${ping} 0.6s ${easeOutCirc},
    ${refocus} 0.4s 0.2s ${easeInExpo} forwards;
`;

// src/styles/levels.ts
var levels = {
  one: "0px 1px 2px rgba(33, 35, 41, 0.27), 0px -1px 2px rgba(33, 35, 41, 0.09), 0px 0px 2px rgba(33, 35, 41, 0.09)",
  two: "0 0 1px 0 rgba(71,92,133,0.54), 0 2px 9px -2px rgba(71,92,133,0.15), 0 12px 12px -3px rgba(71,92,133,0.06)",
  three: "0 0 1px 0 rgba(71,92,133,0.27), 0 2px 12px -2px rgba(71,92,133,0.15), 0 18px 18px 0 rgba(71,92,133,0.06)"
};

// src/vanilla-extract/theme.css.ts
var vars = { colors: { primary: "var(--cui-colors-primary)", focus: "var(--cui-colors-focus)", failure: "var(--cui-colors-failure)", failureBg: "var(--cui-colors-failureBg)", success: "var(--cui-colors-success)", successBg: "var(--cui-colors-successBg)", alert: "var(--cui-colors-alert)", alertBg: "var(--cui-colors-alertBg)", link: "var(--cui-colors-link)", linkBg: "var(--cui-colors-linkBg)", divider: "var(--cui-colors-divider)", icon: "var(--cui-colors-icon)", black: "var(--cui-colors-black)", black100: "var(--cui-colors-black100)", black200: "var(--cui-colors-black200)", white: "var(--cui-colors-white)", white100: "var(--cui-colors-white100)", white200: "var(--cui-colors-white200)", grey50: "var(--cui-colors-grey50)", grey100: "var(--cui-colors-grey100)", grey200: "var(--cui-colors-grey200)", grey300: "var(--cui-colors-grey300)", grey400: "var(--cui-colors-grey400)", grey500: "var(--cui-colors-grey500)", grey600: "var(--cui-colors-grey600)", sgrey50: "var(--cui-colors-sgrey50)", sgrey100: "var(--cui-colors-sgrey100)", sgrey200: "var(--cui-colors-sgrey200)", sgrey300: "var(--cui-colors-sgrey300)", sgrey400: "var(--cui-colors-sgrey400)", red100: "var(--cui-colors-red100)", red200: "var(--cui-colors-red200)", red500: "var(--cui-colors-red500)", red800: "var(--cui-colors-red800)", redA500: "var(--cui-colors-redA500)", orange100: "var(--cui-colors-orange100)", orange200: "var(--cui-colors-orange200)", orange500: "var(--cui-colors-orange500)", orange800: "var(--cui-colors-orange800)", orangeA500: "var(--cui-colors-orangeA500)", yellow100: "var(--cui-colors-yellow100)", yellow200: "var(--cui-colors-yellow200)", yellow500: "var(--cui-colors-yellow500)", yellow800: "var(--cui-colors-yellow800)", yellowA500: "var(--cui-colors-yellowA500)", lime100: "var(--cui-colors-lime100)", lime200: "var(--cui-colors-lime200)", lime500: "var(--cui-colors-lime500)", lime800: "var(--cui-colors-lime800)", limeA500: "var(--cui-colors-limeA500)", green100: "var(--cui-colors-green100)", green200: "var(--cui-colors-green200)", green500: "var(--cui-colors-green500)", green800: "var(--cui-colors-green800)", greenA500: "var(--cui-colors-greenA500)", teal100: "var(--cui-colors-teal100)", teal200: "var(--cui-colors-teal200)", teal500: "var(--cui-colors-teal500)", teal700: "var(--cui-colors-teal700)", teal800: "var(--cui-colors-teal800)", tealA500: "var(--cui-colors-tealA500)", cyan100: "var(--cui-colors-cyan100)", cyan200: "var(--cui-colors-cyan200)", cyan500: "var(--cui-colors-cyan500)", cyan800: "var(--cui-colors-cyan800)", cyanA500: "var(--cui-colors-cyanA500)", blue100: "var(--cui-colors-blue100)", blue200: "var(--cui-colors-blue200)", blue500: "var(--cui-colors-blue500)", blue800: "var(--cui-colors-blue800)", blueA500: "var(--cui-colors-blueA500)", indigo100: "var(--cui-colors-indigo100)", indigo200: "var(--cui-colors-indigo200)", indigo500: "var(--cui-colors-indigo500)", indigo800: "var(--cui-colors-indigo800)", indigoA500: "var(--cui-colors-indigoA500)", purple100: "var(--cui-colors-purple100)", purple200: "var(--cui-colors-purple200)", purple500: "var(--cui-colors-purple500)", purple800: "var(--cui-colors-purple800)", purpleA500: "var(--cui-colors-purpleA500)", pink100: "var(--cui-colors-pink100)", pink200: "var(--cui-colors-pink200)", pink500: "var(--cui-colors-pink500)", pink700: "var(--cui-colors-pink700)", pink800: "var(--cui-colors-pink800)", pinkA500: "var(--cui-colors-pinkA500)", wine100: "var(--cui-colors-wine100)", wine200: "var(--cui-colors-wine200)", wine500: "var(--cui-colors-wine500)", wine800: "var(--cui-colors-wine800)", wineA500: "var(--cui-colors-wineA500)", bgrey200: "var(--cui-colors-bgrey200)", bgrey300: "var(--cui-colors-bgrey300)", bgrey400: "var(--cui-colors-bgrey400)", bgrey500: "var(--cui-colors-bgrey500)", bgrey700: "var(--cui-colors-bgrey700)", navy200: "var(--cui-colors-navy200)", navy300: "var(--cui-colors-navy300)", navy400: "var(--cui-colors-navy400)", navy500: "var(--cui-colors-navy500)", navy700: "var(--cui-colors-navy700)", lilac200: "var(--cui-colors-lilac200)", lilac300: "var(--cui-colors-lilac300)", lilac400: "var(--cui-colors-lilac400)", lilac500: "var(--cui-colors-lilac500)", lilac700: "var(--cui-colors-lilac700)", nteal200: "var(--cui-colors-nteal200)", nteal300: "var(--cui-colors-nteal300)", nteal400: "var(--cui-colors-nteal400)", nteal500: "var(--cui-colors-nteal500)", nteal700: "var(--cui-colors-nteal700)", npink200: "var(--cui-colors-npink200)", npink300: "var(--cui-colors-npink300)", npink400: "var(--cui-colors-npink400)", npink500: "var(--cui-colors-npink500)", npink700: "var(--cui-colors-npink700)", poppy200: "var(--cui-colors-poppy200)", poppy300: "var(--cui-colors-poppy300)", poppy400: "var(--cui-colors-poppy400)", poppy500: "var(--cui-colors-poppy500)", poppy700: "var(--cui-colors-poppy700)", poppy800: "var(--cui-colors-poppy800)", poppy900: "var(--cui-colors-poppy900)", neutralgrey50: "var(--cui-colors-neutralgrey50)", neutralgrey100: "var(--cui-colors-neutralgrey100)", neutralgrey300: "var(--cui-colors-neutralgrey300)", neutralgrey400: "var(--cui-colors-neutralgrey400)", neutralgrey500: "var(--cui-colors-neutralgrey500)", neutralBlack300: "var(--cui-colors-neutralBlack300)", neutralBlack500: "var(--cui-colors-neutralBlack500)", functionalSuccess: "var(--cui-colors-functionalSuccess)", functionalFail: "var(--cui-colors-functionalFail)", functionalAlert: "var(--cui-colors-functionalAlert)", functionalFailBg: "var(--cui-colors-functionalFailBg)", functionalAlertBg: "var(--cui-colors-functionalAlertBg)" } };

// src/vanilla-extract/screens.ts
var screens = {
  sm: "640px",
  // => @media (min-width: 640px) { ... }
  md: "768px",
  // => @media (min-width: 768px) { ... }
  lg: "1024px",
  // => @media (min-width: 1024px) { ... }
  xl: "1280px",
  // => @media (min-width: 1280px) { ... }
  "2xl": "1536px",
  // => @media (min-width: 1536px) { ... }};
  // named
  tablet: "640px",
  // => @media (min-width: 640px) { ... }
  laptop: "1024px",
  // => @media (min-width: 1024px) { ... }
  desktop: "1280px"
  // => @media (min-width: 1280px) { ... }
};

// src/vanilla-extract/install-global-theme.ts
import { createGlobalTheme } from "@vanilla-extract/css";
function installGlobalTheme(cuiTheme) {
  createGlobalTheme(":root", vars, { colors: cuiTheme.colors });
}
function buildRootTheme(cuiTheme) {
  const colors2 = cuiTheme.colors;
  const stringCssVars = Object.keys(colors2).reduce(
    (acc, key) => {
      acc += `--cui-colors-${key}: ${colors2[key]}; 
`;
      return acc;
    },
    ""
  );
  return `:root {
    ${stringCssVars}
  }`;
}

// src/styles/theme.ts
var defaultTheme = {
  // Colors
  colorScheme: "light",
  primaryColor: colors.lilac500,
  failColor: "#DB0025",
  linkColor: "#006FDE",
  fail: "#DB0025",
  link: "#006FDE",
  colors,
  screens,
  divider: colors.grey100,
  success: colors.teal500,
  text: {
    accent: colors.lilac500,
    main: colors.black100,
    heading: colors.black200,
    shade1: colors.grey500,
    shade2: colors.grey400
  },
  background: {
    default: colors.white100,
    action1: colors.grey50,
    action2: colors.grey100,
    action3: colors.grey200
  },
  levels,
  focus: `
    box-shadow: 0 0 0 3px ${colors.focus};
  `,
  focusInset: `
    box-shadow: 0 0 0 3px ${colors.focus} inset;
  `
};
function createCuiTheme(overrides = {}) {
  const theme = defaultTheme;
  return { ...theme, ...overrides };
}

// src/styles/typography.ts
var typography = {
  displayOne: {
    fontFamily: "TT Ramillas",
    fontWeight: 400,
    fontSize: "3.75rem",
    // 60px
    lineHeight: "4.5rem",
    // 72px
    letterSpacing: "0px",
    paddingTop: "9px",
    paddingBottom: "9px",
    textTransform: "none",
    element: "h1"
  },
  displayTwo: {
    fontFamily: "TT Ramillas",
    fontWeight: 400,
    fontSize: "3rem",
    // 48px
    lineHeight: "4rem",
    // 64px
    letterSpacing: "0px",
    paddingTop: "9px",
    paddingBottom: "9px",
    textTransform: "none",
    element: "h1"
  },
  displayThree: {
    fontFamily: "TT Ramillas",
    fontWeight: 400,
    fontSize: "2.25rem",
    // 36px
    lineHeight: "3rem",
    // 48px
    letterSpacing: "0px",
    paddingTop: "9px",
    paddingBottom: "9px",
    element: "h1"
  },
  blockquote: {
    fontFamily: "TT Ramillas",
    fontWeight: 400,
    fontSize: "1rem",
    // 16px
    lineHeight: "1.5rem",
    // 24px
    letterSpacing: "0px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "none",
    element: "p"
  },
  headingOne: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "2.5rem",
    // 40px
    lineHeight: "120%",
    letterSpacing: "0px",
    paddingTop: "9px",
    paddingBottom: "9px",
    textTransform: "none",
    element: "h1"
  },
  headingTwo: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "2rem",
    // 32px
    lineHeight: "120%",
    letterSpacing: "0px",
    paddingTop: "6px",
    paddingBottom: "6px",
    textTransform: "none",
    element: "h2"
  },
  headingThree: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "1.5rem",
    // 24px
    lineHeight: "120%",
    letterSpacing: "0px",
    paddingTop: "6px",
    paddingBottom: "6px",
    textTransform: "none",
    element: "h3"
  },
  headingFour: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "1.25rem",
    // 20px
    lineHeight: "120%",
    letterSpacing: "0px",
    paddingTop: "6px",
    paddingBottom: "6px",
    textTransform: "none",
    element: "h4"
  },
  headingFive: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "1rem",
    // 16px
    lineHeight: "150%",
    letterSpacing: "0px",
    paddingTop: "6px",
    paddingBottom: "6px",
    textTransform: "none",
    element: "h5"
  },
  headingSix: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "1rem",
    // 16px
    lineHeight: "150%",
    // 24px
    letterSpacing: "0px",
    paddingTop: "3px",
    paddingBottom: "3px",
    textTransform: "none",
    element: "h6"
  },
  lead: {
    fontFamily: "Open Sauce Two",
    fontWeight: 400,
    fontSize: "1.25rem",
    // 20px
    lineHeight: "160%",
    letterSpacing: "0px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "none",
    element: "p"
  },
  bodyLg: {
    fontFamily: "Open Sauce Two",
    fontWeight: 400,
    fontSize: "1.25rem",
    // 20px
    lineHeight: "160%",
    letterSpacing: "0px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "none",
    element: "p"
  },
  bodyMd: {
    fontFamily: "Open Sauce Two",
    fontWeight: 400,
    fontSize: "1rem",
    //  16px
    lineHeight: "180%",
    letterSpacing: "0px",
    paddingBottom: "0px",
    paddingTop: "0px",
    textTransform: "none",
    element: "p"
  },
  bodySm: {
    fontFamily: "Open Sauce Two",
    fontWeight: 400,
    fontSize: "0.75rem",
    // 12px
    lineHeight: "150%",
    letterSpacing: "0px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "none",
    element: "p"
  },
  bodyLine: {
    fontFamily: "Open Sauce Two",
    fontWeight: 400,
    fontSize: "1rem",
    //  16px
    lineHeight: "150%",
    letterSpacing: "0px",
    paddingBottom: "0px",
    paddingTop: "0px",
    textTransform: "none",
    element: "span"
  },
  systemMd: {
    fontFamily: "Open Sauce Two",
    fontWeight: 400,
    fontSize: "0.75rem",
    // 12px
    lineHeight: "1rem",
    // 16px
    letterSpacing: "0.24px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "none",
    element: "p"
  },
  systemSm: {
    fontFamily: "Open Sauce Two",
    fontWeight: 400,
    fontSize: "0.62rem",
    // 10px
    lineHeight: "1rem",
    // 16px
    letterSpacing: "0.24px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "none",
    element: "p"
  },
  label: {
    fontFamily: "Open Sauce Two",
    fontWeight: 500,
    fontSize: "0.75rem",
    // 12px
    lineHeight: "1rem",
    // 16px
    letterSpacing: "0.6px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "uppercase",
    element: "p"
  },
  ctaLg: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "1rem",
    // 16px
    lineHeight: "125%",
    letterSpacing: "0.8px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "uppercase",
    element: "span"
  },
  ctaMd: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "0.75rem",
    // 12px
    lineHeight: "150%",
    letterSpacing: "0.6px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "uppercase",
    element: "span"
  }
};

// src/styles/avatar-coloursets.ts
var defaultColourset = {
  caret: "#657911",
  bg: "#657911",
  fg: "#FFFFFF"
};
var avatarColoursets = [
  {
    caret: "#657911",
    bg: "#657911",
    fg: "#FFFFFF"
  },
  {
    caret: "#8905F5",
    bg: "#8905F5",
    fg: "#FFFFFF"
  },
  {
    caret: "#281E9D",
    bg: "#281E9D",
    fg: "#FFFFFF"
  },
  {
    caret: "#820049",
    bg: "#820049",
    fg: "#FFFFFF"
  },
  {
    caret: "#A80B00",
    bg: "#A80B00",
    fg: "#FFFFFF"
  },
  {
    caret: "#D45704",
    bg: "#D45704",
    fg: "#FFFFFF"
  },
  {
    caret: "#0008FF",
    bg: "#0008FF",
    fg: "#FFFFFF"
  },
  {
    caret: "#037277",
    bg: "#037277",
    fg: "#FFFFFF"
  },
  {
    caret: "#E0026D",
    bg: "#E0026D",
    fg: "#FFFFFF"
  },
  {
    caret: "#3E2E2C",
    bg: "#3E2E2C",
    fg: "#FFFFFF"
  }
];
function getAvatarColourset(index) {
  if (index === null) return defaultColourset;
  return avatarColoursets[index % avatarColoursets.length] || defaultColourset;
}

// src/text/text.tsx
var textColor = (props) => {
  if (props.color === "inherit") {
    return css2`
      color: inherit;
    `;
  }
  if (props.color) {
    if (props.color in colors) {
      return css2`
        color: ${colors[props.color]};
      `;
    }
    return css2`
      color: ${props.theme.text[props.color]};
    `;
  }
  switch (props.kind) {
    case "displayOne":
    case "displayTwo":
    case "headingOne":
    case "headingTwo":
    case "headingThree":
    case "headingFour":
    case "headingFive":
    case "headingSix":
      return css2`
        color: ${props.theme.text.heading};
      `;
    case "lead":
    case "bodySm":
    case "bodyMd":
    case "bodyLg":
    case "systemMd":
    case "systemSm":
      return css2`
        color: ${props.theme.text.main};
      `;
    case "blockquote":
    case "label":
      return css2`
        color: ${props.theme.text.shade1};
      `;
  }
};
var Text = styled.span.attrs((p) => ({
  as: p.as ?? typography[p.kind].element
}))`
  font-family: ${(p) => typography[p.kind].fontFamily};
  font-weight: ${(p) => p.bold ? "bold" : typography[p.kind].fontWeight};
  font-size: ${(p) => typography[p.kind].fontSize};
  padding-top: ${(p) => p.inline ? 0 : typography[p.kind].paddingTop};
  padding-bottom: ${(p) => p.inline ? 0 : typography[p.kind].paddingBottom};
  margin-top: 0;
  margin-bottom: 0;
  line-height: ${(p) => typography[p.kind].lineHeight};
  letter-spacing: ${(p) => typography[p.kind].letterSpacing};

  ${textColor};
  text-align: ${(p) => p.textAlign ? p.textAlign : "inherit"};

  text-decoration: ${(p) => `${p.underline ? "underline" : ""} ${p.lineThrough ? "line-through" : ""}`};
  font-style: ${(p) => p.italic ? "italic" : "normal"};
  text-transform: ${(p) => p.textTransform ?? typography[p.kind].textTransform};
`;

// src/alert/alert.css.ts
import { createRuntimeFn as _7a468 } from "@vanilla-extract/recipes/createRuntimeFn";
var contentContainer = "fy21v9c";
var dismissButton = "fy21v9d";
var linkContainer = "fy21v9e";
var root = _7a468({ defaultClassName: "fy21v91", variantClassNames: { containerSize: { sm: "fy21v92", md: "fy21v93", lg: "fy21v94" }, background: { info: "fy21v95", error: "fy21v96", warning: "fy21v97", star: "fy21v98", success: "fy21v99", link: "fy21v9a" } }, defaultVariants: { containerSize: "md", background: "info" }, compoundVariants: [] });
var severityIcon = "fy21v9b";

// src/alert/alert.tsx
import { jsx, jsxs } from "react/jsx-runtime";
function getIcon(size, severity) {
  const iconSize = size === "sm" ? 16 : 24;
  const severityConfig = {
    info: /* @__PURE__ */ jsx(InformationFilledIcon, { label: "", size: iconSize }),
    link: /* @__PURE__ */ jsx(InformationFilledIcon, { label: "", size: iconSize }),
    warning: /* @__PURE__ */ jsx(InformationFilledIcon, { label: "", size: iconSize }),
    error: /* @__PURE__ */ jsx(ExclamationTriangleIcon, { label: "", size: iconSize }),
    star: /* @__PURE__ */ jsx(ShootingStarIcon, { label: "", size: iconSize }),
    success: /* @__PURE__ */ jsx(TickRoundBackgroundIcon, { label: "", size: iconSize })
  };
  return severityConfig[severity ?? "info"];
}
function Alert({
  severity = "error",
  children,
  title,
  className,
  overrides = {},
  withCloseButton = false,
  size = "lg",
  withIcon = true,
  linkComp = null,
  closeButtonLabel = "",
  onClose
}) {
  const icon5 = getIcon(size, severity);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: clsx(
        root({ background: severity, containerSize: size }),
        className
      ),
      style: assignInlineVars({
        color: overrides?.fg,
        background: overrides?.bg
      }),
      children: [
        withIcon && /* @__PURE__ */ jsx("div", { className: severityIcon, children: icon5 }),
        /* @__PURE__ */ jsxs("div", { className: contentContainer, children: [
          title && /* @__PURE__ */ jsx(
            Text,
            {
              kind: size === "lg" ? "bodyMd" : "bodySm",
              bold: size === "lg",
              color: overrides?.fg ? "inherit" : "neutralBlack500",
              as: "div",
              children: title
            }
          ),
          children && /* @__PURE__ */ jsx(
            Text,
            {
              kind: size === "sm" ? "bodySm" : "bodyMd",
              as: "div",
              color: overrides?.fg ? "inherit" : "neutralBlack300",
              children
            }
          ),
          linkComp && /* @__PURE__ */ jsx("div", { className: linkContainer, children: linkComp })
        ] }),
        withCloseButton && /* @__PURE__ */ jsx(
          "div",
          {
            className: dismissButton,
            onClick: () => typeof onClose === "function" && onClose(),
            children: /* @__PURE__ */ jsx(CrossIcon, { label: closeButtonLabel, size: 20 })
          }
        )
      ]
    }
  );
}

// src/accordion/index.ts
import * as Accordion2 from "@radix-ui/react-accordion";

// src/accordion/accordion.tsx
import { forwardRef } from "react";
import { ChevronDownIcon } from "@vericus/cadmus-icons";
import * as Accordion from "@radix-ui/react-accordion";
import clsx2 from "clsx";

// src/accordion/accordion.css.ts
var chevron = "_1ut4eu82";
var content = "_1ut4eu83";
var header = "_1ut4eu80";
var trigger = "_1ut4eu81";

// src/accordion/accordion.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var AccordionTrigger = forwardRef((props, forwardedRef) => {
  const {
    children,
    className,
    headerClassName,
    withDownCaret = true,
    ...rest
  } = props;
  return /* @__PURE__ */ jsx2(Accordion.Header, { className: clsx2(header, headerClassName), children: /* @__PURE__ */ jsxs2(
    Accordion.Trigger,
    {
      className: clsx2(trigger, className),
      ...rest,
      ref: forwardedRef,
      children: [
        children,
        withDownCaret && /* @__PURE__ */ jsx2(ChevronDownIcon, { label: "", size: 16, className: chevron })
      ]
    }
  ) });
});
var AccordionContent = forwardRef((props, forwardedRef) => {
  const { children, className, ...rest } = props;
  return /* @__PURE__ */ jsx2(
    Accordion.Content,
    {
      className: clsx2(content, className),
      ...rest,
      ref: forwardedRef,
      children
    }
  );
});
var AccordionItem = forwardRef(
  (props, forwardedRef) => {
    const { children, className, ...rest } = props;
    return /* @__PURE__ */ jsx2(Accordion.Item, { className, ...rest, ref: forwardedRef, children });
  }
);
var AccordionRoot = forwardRef(
  ({ children, className, ...rest }, forwardedRef) => {
    return /* @__PURE__ */ jsx2(Accordion.Root, { className, ...rest, ref: forwardedRef, children });
  }
);

// src/anchored/anchored.tsx
import { styled as styled2 } from "styled-components";
var Anchored = styled2.a.attrs((p) => ({
  ...p.newTab && {
    target: "_blank",
    rel: "noopener noreferrer"
  }
}))`
  text-decoration: none;
  color: inherit;
  display: inline-block;
  width: fit-content;
  position: relative;

  &:focus {
    outline: none;
    z-index: 10;
    ${(p) => p.theme.focus};
  }
`;

// src/block/block.tsx
import { styled as styled3 } from "styled-components";
var Block = styled3.div`
  display: block;
  width: 100%;
  max-width: 1440px;
  padding-left: 48px;
  padding-right: 48px;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
`;

// src/breadcrumb/breadcrumb.tsx
import { ChevronRightIcon } from "@vericus/cadmus-icons";

// src/breadcrumb/breadcrumb.css.ts
import { createRuntimeFn as _7a4682 } from "@vanilla-extract/recipes/createRuntimeFn";
var breadcrumb = _7a4682({ defaultClassName: "c0cdh11", variantClassNames: { active: { true: "c0cdh12", false: "c0cdh13" } }, defaultVariants: {}, compoundVariants: [] });
var container = "c0cdh10";

// src/breadcrumb/breadcrumb.tsx
import { Fragment, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function Breadcrumb({
  items,
  active,
  separator: separator2 = /* @__PURE__ */ jsx3(ChevronRightIcon, { size: 18, label: "", color: vars.colors.sgrey400 }),
  onClick
}) {
  const handleClick = (item3) => {
    if (!onClick) return;
    onClick(item3);
  };
  return /* @__PURE__ */ jsx3("div", { "aria-label": "Breadcrumb", children: /* @__PURE__ */ jsx3("div", { className: container, children: items.map((item3, index) => {
    const isLast = index === items.length - 1;
    const isActive = item3.id === active || item3.label === active;
    return /* @__PURE__ */ jsxs3(Fragment, { children: [
      /* @__PURE__ */ jsxs3(
        "div",
        {
          role: "button",
          tabIndex: 0,
          onClick: () => handleClick(item3),
          className: breadcrumb({ active: isActive }),
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick(item3);
            }
          },
          children: [
            item3.icon,
            /* @__PURE__ */ jsx3(Text, { kind: "systemMd", color: isActive ? "lilac500" : "navy400", children: item3.label })
          ]
        },
        item3.id || item3.label
      ),
      !isLast && separator2
    ] });
  }) }) });
}

// src/button/button.tsx
import { forwardRef as forwardRef2 } from "react";
import clsx3 from "clsx";

// src/icon/icon.tsx
import { styled as styled4 } from "styled-components";
import { Icon as CadmusIcon } from "@vericus/cadmus-icons";
var Icon = styled4(CadmusIcon)`
  fill: ${fillColor};
`;
function fillColor(props) {
  const {
    iconColor = "inherit",
    theme: { colors: colors2 }
  } = props;
  if (iconColor === "inherit" || iconColor === "currentColor") {
    return iconColor;
  }
  return colors2[iconColor];
}

// src/button/button.css.ts
import { createRuntimeFn as _7a4683 } from "@vanilla-extract/recipes/createRuntimeFn";
var button = _7a4683({ defaultClassName: "_1t5zoru5", variantClassNames: { kind: { primary: "_1t5zoru6", secondary: "_1t5zoru7", outline: "_1t5zoru8", ghost: "_1t5zoru9", error: "_1t5zorua" }, size: { sm: "_1t5zorub", md: "_1t5zoruc", lg: "_1t5zorud" }, shape: { square: "_1t5zorue", rectangle: "_1t5zoruf" } }, defaultVariants: { kind: "outline", size: "md", shape: "rectangle" }, compoundVariants: [[{ size: "sm", shape: "square" }, "_1t5zorug"], [{ size: "md", shape: "square" }, "_1t5zoruh"], [{ size: "lg", shape: "square" }, "_1t5zorui"]] });
var icon = "_1t5zoruj";
var marginLeft = "var(--_1t5zoru2)";
var marginRight = "var(--_1t5zoru3)";

// src/button/button.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var Button = forwardRef2(
  (props, ref) => {
    const {
      iconName,
      iconPosition = "left",
      kind = "outline",
      size = "md",
      className,
      icon: icon5,
      rightIcon: rightIcon2,
      marginLeft: marginLeft3,
      marginRight: marginRight3,
      fullWidth: _fullWidth,
      ...buttonProps
    } = props;
    let left = null;
    let right = null;
    if (icon5) {
      left = icon5;
    } else if (iconName && iconPosition === "left") {
      left = /* @__PURE__ */ jsx4("span", { className: icon, children: /* @__PURE__ */ jsx4(Icon, { iconName, style: { fill: "currentColor" } }) });
    }
    if (rightIcon2) {
      right = rightIcon2;
    } else if (iconName && iconPosition === "right") {
      right = /* @__PURE__ */ jsx4("span", { className: icon, children: /* @__PURE__ */ jsx4(Icon, { iconName, style: { fill: "currentColor" } }) });
    }
    return /* @__PURE__ */ jsxs4(
      "button",
      {
        ref,
        className: clsx3(
          button({
            kind,
            size,
            shape: props.children ? "rectangle" : "square"
          }),
          className
        ),
        style: assignInlineVars({
          [marginLeft]: typeof marginLeft3 === "number" ? `${marginLeft3}px` : void 0,
          [marginRight]: typeof marginRight3 === "number" ? `${marginRight3}px` : void 0
        }),
        ...buttonProps,
        children: [
          left,
          props.children && /* @__PURE__ */ jsx4("span", { children: props.children }),
          right
        ]
      }
    );
  }
);

// src/card/card.tsx
import { styled as styled5 } from "styled-components";
var Card = styled5.div`
  display: inline-block;
  background: white;
  min-width: 200px;
  padding: 8px 0px;
  margin: 0;

  /* Reset the base font styles to system */
  font-size: 14px;
  font-weight: normal;
  line-height: 21px;
  letter-spacing: 0.3px;
  text-indent: 0px;

  overflow-y: auto;
  max-height: 360px;
  border-radius: 2px;
  background-color: ${(p) => p.theme.background.default};
  box-sizing: border-box;
  box-shadow: ${levels.two};
`;

// src/checkbox/checkbox.tsx
import { styled as styled6 } from "styled-components";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var Checkbox = (props) => {
  const {
    content: content5,
    className,
    children,
    mixed,
    checked,
    disabled: disabled2,
    colorVariant,
    ...inputProps
  } = props;
  const ariaChecked = checked ? mixed ? "mixed" : true : false;
  return /* @__PURE__ */ jsxs5(
    Label,
    {
      className,
      readOnly: props.readOnly,
      "aria-disabled": disabled2,
      children: [
        /* @__PURE__ */ jsx5(
          HiddenInput,
          {
            type: "checkbox",
            "aria-checked": ariaChecked,
            checked,
            colorVariant,
            disabled: disabled2,
            ...inputProps
          }
        ),
        /* @__PURE__ */ jsx5(Box, {}),
        content5 ? /* @__PURE__ */ jsx5(Text, { kind: "bodyMd", as: "span", children: content5 }) : children
      ]
    }
  );
};
var Label = styled6.label`
  display: inline-flex;
  align-items: center;

  outline: 0;

  position: relative;
  pointer-events: ${(p) => p.readOnly ? "none" : "all"};
`;
var Box = styled6.span`
  flex: none;
  display: inline-block;
  width: 18px;
  height: 18px;
  margin: 6px 9px 6px 0;

  position: relative;

  box-sizing: border-box;
  border: 1px solid ${colors.grey300};
  background: white;
  border-radius: 3px;

  cursor: pointer;

  /* The :before pseudo-element creates the focus and ping outline */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 3px;
    width: 100%;
    height: 100%;
  }
`;
var HiddenInput = styled6.input`
  position: absolute;
  z-index: -1;
  opacity: 0;

  width: 18px; /* Same as Box */
  height: 18px; /* Same as Box */

  cursor: pointer;

  /* Apply hover styles to Box when unchecked */
  &:not(:checked):not([aria-checked="mixed"]) ~ ${Box}:hover {
    background: ${colors.grey50};
  }

  /* Apply active styles to Box when unchecked */
  &:not(:checked) ~ ${Box}:active {
    background: ${colors.grey100};
  }

  &:not(disabled) {
    /* Apply focued styles to Box */
    &:focus ~ ${Box} {
      border-color: ${colors.grey200};
    }

    /* Apply focued styles to Box's focus pseudo-element */
    &:focus ~ ${Box}:before {
      ${FocusAnimation};
    }

    /* Apply checked styles to Box */
    &:checked ~ ${Box} {
      background: ${(p) => p.colorVariant ? colors[p.colorVariant] : colors.primary};
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpolygon fill='%23FFFFFF' points='6.535 9.065 4.48 7.34 3 8.808 6.535 12 13 5.468 11.52 4'/%3E%3C/svg%3E");
    }

    /* Apply indeterminate style to Box */
    &[aria-checked="mixed"] ~ ${Box} {
      background: ${(p) => p.colorVariant ? colors[p.colorVariant] : colors.primary};
      //Super handy https://yoksel.github.io/url-encoder/
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23FFF' d='M3,11 L13,11 C13.5522847,11 14,11.4477153 14,12 C14,12.5522847 13.5522847,13 13,13 L3,13 C2.44771525,13 2,12.5522847 2,12 C2,11.4477153 2.44771525,11 3,11 Z'%3E%3C/path%3E%3C/svg%3E");
      background-position-y: -4px;
      background-repeat: no-repeat;
    }
  }

  /* Apply disabled styles to Box and Text */
  &:disabled ~ ${Box}, &:disabled ~ ${Text} {
    opacity: 0.36;
  }

  &:disabled ~ ${Box} {
    cursor: not-allowed;
  }
  &:read-only {
    pointer-events: none;
  }
`;

// src/chip/chip.tsx
import { styled as styled7 } from "styled-components";
import { CloseIcon } from "@vericus/cadmus-icons";
import { Fragment as Fragment2, jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var Chip = (props) => /* @__PURE__ */ jsxs6(Fragment2, { children: [
  /* @__PURE__ */ jsxs6(ChipContainer, { selected: props.selected, className: props.className, children: [
    props.children,
    /* @__PURE__ */ jsx6(CloseAction, { onMouseDown: props.onClose, selected: props.selected, children: /* @__PURE__ */ jsx6(CloseIcon, { label: "" }) })
  ] }),
  /* @__PURE__ */ jsx6("span", { children: "\uFEFF" })
] });
var ChipContainer = styled7.span`
  display: inline-flex;
  align-items: center;
  padding-left: 9px;
  padding-right: 2px;
  margin: 0 2px;
  box-sizing: border-box;
  height: 22px;
  border-radius: 12px;
  line-height: 24px;

  font-size: ${typography["bodySm"].fontSize};
  letter-spacing: ${typography["bodySm"].letterSpacing};
  white-space: nowrap;

  /* when selected, change background to CHROME selection blue */
  background: ${(p) => p.selected ? p.theme.colors.navy400 : p.theme.colors.bgrey400};
  color: ${(p) => p.selected ? p.theme.colors.white : p.theme.colors.black};

  user-select: none; /* use selected prop instead */
`;
var CloseAction = styled7.button.attrs({
  "aria-label": "Close"
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-left: 9px;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  border: none;
  outline: none;

  background: transparent;

  cursor: pointer;

  svg {
    path {
      fill: ${(p) => p.selected ? p.theme.colors.white : p.theme.colors.navy400};
    }
  }
`;

// src/colored/colored.tsx
import { styled as styled8 } from "styled-components";
var Colored = styled8.span`
  color: ${(p) => p.color in colors ? colors[p.color] : p.theme.text[p.color]};
`;

// src/color-picker/color-picker.tsx
import { styled as styled9 } from "styled-components";
import * as RadixToggleGroup from "@radix-ui/react-toggle-group";

// src/color-picker/constants.ts
var DEFAULT_PALETTE = [
  "#ff003c",
  "#ffc200",
  "#ffee00",
  "#ffa969",
  "#ff9df9",
  "#b2e35a",
  "#2ee689",
  "#29cccc",
  "#57d3ff",
  "#b68cff",
  "#6565ff",
  "#4ca2ff",
  "#fafafa",
  "#dfeaf0",
  "#b0c5c7"
];

// src/color-picker/color-picker.tsx
import { Fragment as Fragment3, jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
function ColorPicker(props) {
  const {
    pallete = DEFAULT_PALETTE,
    value,
    onChange,
    title,
    defaultColor,
    defaultLabel,
    labelId
  } = props;
  return /* @__PURE__ */ jsxs7(PickerRoot, { children: [
    title && /* @__PURE__ */ jsx7(Text, { kind: "headingSix", as: "span", id: labelId, children: title }),
    /* @__PURE__ */ jsx7(ColorButtons, { type: "single", children: pallete.map((color2) => /* @__PURE__ */ jsx7(RadixToggleGroup.Item, { value: color2, asChild: true, children: /* @__PURE__ */ jsx7(
      ColorButton,
      {
        color: color2,
        selected: value === color2,
        autoFocus: value === color2,
        onClick: () => onChange?.(color2),
        "aria-label": color2
      }
    ) }, color2)) }),
    defaultColor && /* @__PURE__ */ jsxs7(Fragment3, { children: [
      /* @__PURE__ */ jsx7(PickerDivider, {}),
      /* @__PURE__ */ jsxs7(DefaultItem, { onClick: () => onChange?.(defaultColor), children: [
        /* @__PURE__ */ jsx7(
          ColorButton,
          {
            color: defaultColor,
            selected: value === defaultColor,
            autoFocus: value === defaultColor,
            "aria-label": defaultColor
          }
        ),
        defaultLabel && /* @__PURE__ */ jsxs7(Fragment3, { children: [
          /* @__PURE__ */ jsx7(DefaultText, { children: defaultLabel }),
          /* @__PURE__ */ jsx7(DefaultNote, { children: "(default)" })
        ] })
      ] })
    ] })
  ] });
}
var PickerRoot = styled9.div`
  width: 200px;
  padding: 8px 12px;
  margin: 0;
  box-sizing: border-box;
`;
var PickerDivider = styled9.div`
  display: block;
  height: 1px;
  margin: 16px 0px;
  background: ${(p) => p.theme.divider};
  border: none;
`;
var ColorButtons = styled9(RadixToggleGroup.Root)`
  display: flex;
  flex-wrap: wrap;
  margin-top: 16px;
  gap: 6px;
`;
var ColorButton = styled9.button`
  width: 24px;
  height: 24px;
  margin: 0;
  padding: 0;
  border-radius: 50%;
  box-sizing: content-box;

  cursor: pointer;

  border: 3.2px solid
    ${(p) => p.color === "transparent" ? colors.grey200 : "white"};
  background: ${(p) => p.color};
  outline: ${(p) => p.selected ? `1px solid ${p.theme.text.accent}` : "none"};

  &:hover,
  &:active {
    border-color: #e6eaf2;
  }

  &:focus {
    ${(p) => p.theme.focus};
  }
`;
var DefaultItem = styled9.div`
  display: flex;
  align-items: center;
`;
var DefaultText = styled9.span`
  margin: 6px;
`;
var DefaultNote = styled9.span`
  color: ${(p) => p.theme.text.shade2};
  font-weight: 400;
  font-style: italic;
`;

// src/color-picker/color-picker-menu.tsx
import { forwardRef as forwardRef7 } from "react";
import * as RadixToolbar from "@radix-ui/react-toolbar";

// src/control-button/control-button.tsx
import { forwardRef as forwardRef3 } from "react";
import { styled as styled11 } from "styled-components";
import { Icon as Icon2 } from "@vericus/cadmus-icons";

// src/tooltip/index.ts
import {
  Provider as TooltipProvider
} from "@radix-ui/react-tooltip";

// src/tooltip/tooltip.tsx
import { styled as styled10 } from "styled-components";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Fragment as Fragment4, jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
function Tooltip({
  children,
  content: content5,
  open,
  defaultOpen,
  onOpenChange,
  width = 200,
  size = "md",
  sideOffset = 5,
  collisionPadding = 8,
  passThrough = false,
  delayDuration,
  disableContentStyles,
  ...props
}) {
  if (passThrough) {
    return /* @__PURE__ */ jsx8(Fragment4, { children });
  }
  return /* @__PURE__ */ jsxs8(
    TooltipPrimitive.Root,
    {
      open,
      defaultOpen,
      onOpenChange,
      delayDuration,
      children: [
        /* @__PURE__ */ jsx8(TooltipPrimitive.Trigger, { asChild: true, children }),
        /* @__PURE__ */ jsx8(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx8(
          TooltipPrimitive.Content,
          {
            sideOffset,
            collisionPadding,
            style: { zIndex: 1e4 },
            asChild: true,
            ...props,
            children: disableContentStyles ? content5 : /* @__PURE__ */ jsx8(StyledContent, { width, size, children: content5 })
          }
        ) })
      ]
    }
  );
}
var StyledContent = styled10.div`
  min-width: fit-content;
  border-radius: 4px;

  width: ${(p) => p.width === "auto" ? "initial" : `${p.width}px`};
  padding: ${(p) => p.size === "sm" ? "4px 8px" : "12px"};

  border: 1px solid ${(p) => p.theme.colors.white200};
  background-color: ${(p) => p.theme.colors.white100};
  box-shadow: ${(p) => p.theme.levels.three};

  max-height: var(--radix-tooltip-content-available-height);

  /* Reset the base font styles to system */
  font-size: ${typography.systemMd.fontSize};
  font-weight: ${typography.systemMd.fontWeight};
  line-height: ${typography.systemMd.lineHeight};
  letter-spacing: ${typography.systemMd.letterSpacing};
  text-indent: 0px;
`;

// src/control-button/control-button.tsx
import { jsx as jsx9, jsxs as jsxs9 } from "react/jsx-runtime";
var ControlButton = forwardRef3(
  (props, ref) => {
    const {
      icon: icon5,
      iconName,
      children,
      withDownCaret,
      disabled: disabled2,
      tooltipPosition = "bottom",
      hideTooltip = false,
      onClick,
      disableChildrenMinWidth,
      ...buttonProps
    } = props;
    const button5 = /* @__PURE__ */ jsxs9(
      ButtonContainer,
      {
        disabled: disabled2,
        role: "button",
        "aria-disabled": disabled2,
        ref,
        onClick: disabled2 ? void 0 : onClick,
        ...buttonProps,
        children: [
          icon5 && !iconName && /* @__PURE__ */ jsx9(IconDiv, { children: icon5 }),
          iconName && /* @__PURE__ */ jsx9(ControlIcon, { iconName }),
          children ? /* @__PURE__ */ jsx9(ControlButtonText, { disableChildrenMinWidth, children }) : "",
          withDownCaret && /* @__PURE__ */ jsx9(
            Icon2,
            {
              iconName: "DownCaret",
              style: { marginRight: children ? 4 : void 0 }
            }
          )
        ]
      }
    );
    if (props["aria-label"] && !hideTooltip) {
      return /* @__PURE__ */ jsx9(
        Tooltip,
        {
          size: "sm",
          width: "auto",
          side: tooltipPosition,
          content: props["aria-label"],
          children: button5
        }
      );
    }
    return button5;
  }
);
var ControlButtonText = styled11.span`
  min-width: ${(p) => p.disableChildrenMinWidth ? "unset" : "126px"};
  display: inline;
  text-align: left;
  padding: 0px 4px;
  font-size: 14px;
`;
var IconDiv = styled11.div`
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
var ControlIcon = styled11(Icon2)`
  margin: 0px 4px 0px 4px;
  display: flex;
`;
var ButtonContainer = styled11.button`
  --size: ${(p) => p.size ?? 36}px;
  min-width: var(--size);
  height: var(--size);
  padding: ${(p) => p.noPadding ? "0px" : "4px"};

  border-radius: 6px;
  border: none;
  outline: 0;
  position: relative;
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  background-clip: content-box;
  background-color: ${(p) => p.selected && !p.disabled ? p.theme.background.action1 : "transparent"};
  fill: ${(p) => p.selected && !p.disabled ? p.theme.text.accent : p.theme.colors.navy400};
  color: ${(p) => p.selected && !p.disabled ? p.theme.text.accent : p.theme.colors.navy400};
  transition:
    background 0.1s ease-out,
    fill 0.1s ease-out;

  &:not([aria-disabled="true"]) {
    &:focus {
      background-color: ${(p) => p.theme.colors.grey50};
    }

    &:focus-visible {
      ${(p) => p.theme.focus};
      outline: 0;
    }

    &:hover {
      background-color: ${(p) => p.theme.colors.grey50};
    }

    &:active,
    &[aria-expanded="true"] {
      background-color: ${(p) => p.theme.colors.grey100};
    }
  }

  &[aria-disabled="true"] {
    opacity: 0.36;
    cursor: not-allowed;
  }
`;

// src/popover/index.ts
import { Anchor, Root as Root5, Trigger as Trigger3 } from "@radix-ui/react-popover";

// src/popover/card.tsx
import { styled as styled12 } from "styled-components";
var Card2 = styled12.div`
  background: white;
  padding: 12px;
  margin: 0;
  position: relative;
  z-index: 50;

  /* Reset the base font styles to system */
  font-size: 14px;
  font-weight: normal;
  line-height: 21px;
  letter-spacing: 0.3px;
  text-indent: 0px;

  overflow-y: auto;
  border-radius: 4px;
  background-color: ${(p) => p.theme.colors.white100};
  box-sizing: border-box;
  box-shadow: ${(p) => p.theme.levels.two};

  min-width: max(var(--radix-popover-trigger-width, 200px), 200px);
  width: ${(p) => p.width ? `${p.width}px` : "initial"};
  max-width: var(--radix-popover-content-available-width);
  max-height: var(--radix-popover-content-available-height);
`;

// src/popover/content.tsx
import { forwardRef as forwardRef4 } from "react";
import * as RadixPopover from "@radix-ui/react-popover";
import { jsx as jsx10 } from "react/jsx-runtime";
var Content4 = forwardRef4(
  (props, ref) => {
    const {
      children,
      align = "start",
      sideOffset = 6,
      collisionPadding = 4,
      style,
      ...contentProps
    } = props;
    return /* @__PURE__ */ jsx10(RadixPopover.Portal, { children: /* @__PURE__ */ jsx10(
      RadixPopover.Content,
      {
        align,
        sideOffset,
        collisionPadding,
        style: {
          ...style,
          /* if no zIndex is explicitly set, provide a default */
          zIndex: style?.zIndex ?? 1e3
        },
        ...contentProps,
        ref,
        children
      }
    ) });
  }
);

// src/popover/label.tsx
import { forwardRef as forwardRef5 } from "react";
import { styled as styled13 } from "styled-components";
import { Fragment as Fragment5, jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
var Label2 = forwardRef5((props, ref) => {
  const { children, subtitle, ...divProps } = props;
  return /* @__PURE__ */ jsx11(Root4, { ref, ...divProps, children: typeof children === "string" ? /* @__PURE__ */ jsxs10(Fragment5, { children: [
    /* @__PURE__ */ jsx11(Text, { kind: "headingSix", inline: true, children }),
    subtitle && /* @__PURE__ */ jsx11(Text, { kind: "bodySm", inline: true, children: subtitle })
  ] }) : children });
});
var Root4 = styled13.div`
  padding: 8px 16px;
  box-sizing: border-box;
`;

// src/popover/separator.tsx
import { forwardRef as forwardRef6 } from "react";
import { styled as styled14 } from "styled-components";
import { jsx as jsx12 } from "react/jsx-runtime";
var Separator = forwardRef6((_props, ref) => /* @__PURE__ */ jsx12(StyledSeparator, { ref, children: /* @__PURE__ */ jsx12(Line, {}) }));
var StyledSeparator = styled14.div`
  padding: 8px 0px;
`;
var Line = styled14.div`
  display: block;
  height: 1px;
  background: ${(p) => p.theme.colors.grey100};
`;

// src/popover/index.ts
var Popover = {
  Root: Root5,
  Card: Card2,
  Trigger: Trigger3,
  Content: Content4,
  Separator,
  Label: Label2,
  Anchor
};

// src/color-picker/color-picker-menu.tsx
import { jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
var ColorPickerMenu = forwardRef7((props, ref) => {
  const {
    iconName = "Color",
    "aria-label": ariaLabel,
    selected,
    disabled: disabled2,
    side = "bottom",
    align = "end",
    autoFocus = true,
    tooltipPosition,
    asToolbarButton,
    pickerProps,
    // so that Radix asChild works on the trigger :)
    ...triggerButtonProps
  } = props;
  const trigger2 = /* @__PURE__ */ jsx13(Popover.Trigger, { ...triggerButtonProps, asChild: true, ref, children: /* @__PURE__ */ jsx13(
    ControlButton,
    {
      "aria-label": ariaLabel,
      iconName,
      selected,
      disabled: disabled2,
      "aria-disabled": disabled2,
      tooltipPosition
    }
  ) });
  return /* @__PURE__ */ jsxs11(Popover.Root, { children: [
    asToolbarButton ? /* @__PURE__ */ jsx13(RadixToolbar.Button, { asChild: true, children: trigger2 }) : trigger2,
    /* @__PURE__ */ jsx13(Popover.Content, { autoFocus, side, align, children: /* @__PURE__ */ jsx13(Popover.Card, { children: /* @__PURE__ */ jsx13(ColorPicker, { ...pickerProps }) }) })
  ] });
});

// src/controlbar/constants.ts
var TEXT_COLORS = [
  "#e81a17",
  "#ff6600",
  "#ffce00",
  "#774835",
  "#d900b5",
  "#00a854",
  "#00a3a3",
  "#007138",
  "#0066ff",
  "#aa00ff",
  "#5100f5",
  "#004183",
  "#f5f5f5",
  "#bdccd4",
  "#7f8c8d"
];
var DEFAULT_TEXT_COLOR = "#282A30";
var HIGHLIGHT_COLORS = [
  "#ff003c",
  "#ffc200",
  "#ffee00",
  "#ffa969",
  "#ff9df9",
  "#b2e35a",
  "#2ee689",
  "#29cccc",
  "#57d3ff",
  "#b68cff",
  "#6565ff",
  "#4ca2ff",
  "#fafafa",
  "#dfeaf0",
  "#b0c5c7"
];
var DEFAULT_HIGHLIGHT_COLOR = "transparent";
var CRITERION_COLORS = [
  "#FF4136",
  "#FF851B",
  "#FFDC00",
  "#FF6B6B",
  "#F012BE",
  "#2ECC40",
  "#0074D9",
  "#2A6996",
  "#45B7D1",
  "#FFA07A",
  "#98D8C8",
  "#FBFF8B",
  "#BB8FCE",
  "#85A6E2",
  "#DAA134",
  "#808000",
  "#FF85A2",
  "#7FDBFF",
  "#F9C172",
  "#000000",
  "#85144B",
  "#3D9970",
  "#FFF0D2",
  "#20B2AA",
  "#7D00BE",
  "#CD853F",
  "#00CED1",
  "#1012B7",
  "#8B4513",
  "#4E4E4E"
];

// src/controlbar/control-action.tsx
import { css as css3, styled as styled15 } from "styled-components";
import { Icon as Icon3 } from "@vericus/cadmus-icons";
import { jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
var ControlAction = (props) => {
  const { kind, iconName, children } = props;
  return /* @__PURE__ */ jsxs12(ButtonContainer2, { kind, ...props, children: [
    /* @__PURE__ */ jsx14(ButtonText, { children }),
    iconName && /* @__PURE__ */ jsx14(ButtonIcon, { iconName })
  ] });
};
var ButtonContainer2 = styled15.button`
  padding: 6px 18px;
  margin: 0;
  height: 100%;

  display: inline-flex;
  align-items: center;

  font-family: ${typography.ctaMd.fontFamily};
  font-size: ${typography.ctaMd.fontSize};
  text-transform: ${typography.ctaMd.textTransform};
  font-weight: ${typography.ctaMd.fontWeight};
  line-height: ${typography.ctaMd.lineHeight};
  letter-spacing: ${typography.ctaMd.letterSpacing};

  position: relative;

  border: none;
  box-sizing: border-box;
  cursor: pointer;

  ${(p) => background(p.kind)};

  outline: 0;

  &:not(:disabled) {
    &:after {
      content: "";
      box-sizing: border-box;
      position: absolute;
      top: 0;
      left: 0;

      /* so that the focus ring is displayed correctly */
      width: calc(100% - 3px);
      margin-top: 3px;
      height: calc(100% - 6px);
    }

    &:focus:after {
      background: ${(p) => p.theme.background.action2};
    }

    &:focus-visible:after {
      ${(p) => p.theme.focus};
      outline: 0;
    }

    &:hover:after {
      background: ${(p) => p.theme.background.action2};
    }

    &:active:after {
      opacity: 0.54;
    }
  }

  &:disabled {
    opacity: 0.54;
  }
`;
var ButtonText = styled15.span`
  position: relative;
  white-space: nowrap;
  z-index: 2;
`;
var ButtonIcon = styled15(Icon3)`
  margin-left: 9px;
  font-size: 16px; /* Increases icon size */
`;
var background = (kind) => {
  if (kind === "primary") {
    return css3`
      fill: white;
      color: white;
      background: ${(p) => p.theme.primaryColor};
    `;
  }
  return css3`
    color: ${(p) => p.theme.colors.neutralBlack500};
    fill: ${(p) => p.theme.text.main};
    background-color: transparent;
  `;
};

// src/controlbar/controlbar.tsx
import { styled as styled28 } from "styled-components";
import { InsertCommentIcon, InsertFootnoteIcon } from "@vericus/cadmus-icons";
import * as RadixToggleGroup7 from "@radix-ui/react-toggle-group";
import * as RadixToolbar10 from "@radix-ui/react-toolbar";

// src/controlbar/collaborators-popover.tsx
import { forwardRef as forwardRef18 } from "react";
import { styled as styled23 } from "styled-components";
import { ChevronDownIcon as ChevronDownIcon2 } from "@vericus/cadmus-icons";
import * as RadixToolbar2 from "@radix-ui/react-toolbar";

// src/avatar-icon/avatar-icon.tsx
import { useMemo } from "react";
import clsx4 from "clsx";

// src/avatar-icon/avatar-icon.css.ts
var avatar = { lg: "_1rmqe073 _1rmqe072", md: "_1rmqe074 _1rmqe072", sm: "_1rmqe075 _1rmqe072" };
var bg = "var(--_1rmqe070)";
var fg = "var(--_1rmqe071)";

// src/avatar-icon/avatar-icon.tsx
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
function AvatarIcon(props) {
  const { name, size = "md", style, className, shade, fg: fg2, bg: bg2 } = props;
  const [bgVar, fgVar] = useMemo(() => {
    const cfg = getAvatarColourset(shade ?? null);
    return [bg2 ?? cfg.bg, fg2 ?? cfg.fg];
  }, [shade, fg2, bg2]);
  if (!name) {
    return /* @__PURE__ */ jsx15("span", { className: clsx4(avatar[size], className), style, children: /* @__PURE__ */ jsx15(Icon, { iconName: "Question" }) });
  }
  const nameParts = name.split(" ");
  const firstNameInitial = nameParts[0] ? nameParts[0][0] : "";
  const lastNameInitial = nameParts[1] ? nameParts[1][0] : "";
  return /* @__PURE__ */ jsx15(Tooltip, { content: name, width: "auto", size: "sm", children: /* @__PURE__ */ jsx15(
    "div",
    {
      className: clsx4(avatar[size], className),
      style: assignInlineVars({
        [bg]: bgVar,
        [fg]: fgVar,
        ...style || {}
      }),
      children: /* @__PURE__ */ jsxs13(
        Text,
        {
          kind: size === "lg" ? "bodyLine" : "systemSm",
          color: "inherit",
          as: "span",
          children: [
            firstNameInitial,
            lastNameInitial
          ]
        }
      )
    }
  ) });
}

// src/dropdown-menu/index.ts
import {
  RadioGroup as RadioGroup2,
  Root as Root7,
  Sub,
  SubTrigger,
  Trigger as Trigger4
} from "@radix-ui/react-dropdown-menu";

// src/dropdown-menu/checkbox-item.tsx
import { forwardRef as forwardRef9 } from "react";
import { styled as styled17 } from "styled-components";
import * as RadixDropdownMenu2 from "@radix-ui/react-dropdown-menu";

// src/switch/switch.tsx
import { useId } from "react";
import {
  Root as SwitchRoot,
  Thumb as SwitchThumb
} from "@radix-ui/react-switch";
import clsx5 from "clsx";

// src/switch/switch.css.ts
import { createRuntimeFn as _7a4684 } from "@vanilla-extract/recipes/createRuntimeFn";
var container2 = _7a4684({ defaultClassName: "l5s5u60", variantClassNames: { labelPosition: { right: "l5s5u61", left: "l5s5u62" } }, defaultVariants: {}, compoundVariants: [] });
var disabled = _7a4684({ defaultClassName: "l5s5u6d", variantClassNames: { checked: { true: "l5s5u6e", false: "l5s5u6f" } }, defaultVariants: {}, compoundVariants: [] });
var label = "l5s5u6g";
var root2 = _7a4684({ defaultClassName: "l5s5u63", variantClassNames: { size: { sm: "l5s5u64", md: "l5s5u65", lg: "l5s5u66" } }, defaultVariants: {}, compoundVariants: [] });
var switchThumb = _7a4684({ defaultClassName: "l5s5u67", variantClassNames: { disabled: { true: "l5s5u68", false: "l5s5u69" }, size: { sm: "l5s5u6a", md: "l5s5u6b", lg: "l5s5u6c" } }, defaultVariants: {}, compoundVariants: [] });

// src/switch/switch.tsx
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
function Switch({
  size = "md",
  label: label4,
  children,
  labelKind = "bodyLine",
  labelPosition = "right",
  className,
  style,
  disabled: disabled2 = false,
  checked,
  defaultChecked,
  ...switchRootProps
}) {
  const id = useId();
  return /* @__PURE__ */ jsxs14(
    "div",
    {
      className: clsx5(container2({ labelPosition }), className),
      style,
      children: [
        /* @__PURE__ */ jsx16(
          SwitchRoot,
          {
            id,
            className: clsx5(
              disabled2 && disabled({
                checked: !!(checked || defaultChecked)
              }),
              root2({ size })
            ),
            "aria-disabled": disabled2,
            disabled: disabled2,
            checked,
            defaultChecked,
            ...switchRootProps,
            children: /* @__PURE__ */ jsx16(SwitchThumb, { className: switchThumb({ size, disabled: disabled2 }) })
          }
        ),
        (label4 || children) && /* @__PURE__ */ jsx16(Text, { kind: labelKind, as: "label", htmlFor: id, className: label, children: label4 || children })
      ]
    }
  );
}

// src/dropdown-menu/item.tsx
import { forwardRef as forwardRef8 } from "react";
import { styled as styled16 } from "styled-components";
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var Item4 = forwardRef8((props, ref) => {
  const {
    children,
    content: content5,
    selected,
    iconName,
    icon: icon5,
    leftSection: leftSection2,
    ...itemProps
  } = props;
  return /* @__PURE__ */ jsx17(RadixDropdownMenu.Item, { asChild: true, ...itemProps, ref, children: /* @__PURE__ */ jsxs15(MenuItemDiv, { selected, children: [
    leftSection2 ? leftSection2 : icon5 ? icon5 : iconName && /* @__PURE__ */ jsx17(Icon, { iconName }),
    content5 ? /* @__PURE__ */ jsx17(Text, { kind: "bodyLine", color: "inherit", as: "span", children: content5 }) : children
  ] }) });
});
var MenuItemDiv = styled16.div`
  display: flex;
  gap: 12px;
  align-items: center;

  min-height: 40px;
  padding: 0px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;

  color: ${(p) => p.selected ? p.theme.colors.primary : p.theme.colors.black100};
  fill: ${(p) => p.selected ? p.theme.colors.primary : p.theme.colors.grey500};

  transition:
    background 0.3s ${easeOutCirc},
    fill 0.3s ${easeOutCirc};

  outline: none;
  cursor: pointer;

  &:not([data-disabled]) {
    &:hover,
    &:focus {
      background: ${(p) => p.theme.colors.bgrey300};
    }

    &:active,
    &[aria-expanded="true"] {
      background: ${(p) => p.theme.colors.bgrey500};
    }
  }

  &[aria-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &[data-focus] {
    background: ${(p) => p.theme.colors.bgrey300};
  }
`;

// src/dropdown-menu/checkbox-item.tsx
import { Fragment as Fragment6, jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
var CheckboxItem2 = forwardRef9(
  (props, ref) => {
    const {
      children,
      content: content5,
      iconName,
      icon: icon5,
      toggleable,
      tickable,
      ...itemProps
    } = props;
    return /* @__PURE__ */ jsx18(RadixDropdownMenu2.CheckboxItem, { asChild: true, ...itemProps, ref, children: /* @__PURE__ */ jsxs16(MenuItemDiv, { children: [
      !(toggleable || tickable) && /* @__PURE__ */ jsx18(
        Checkbox,
        {
          style: { marginRight: "-4px" },
          checked: itemProps.checked === true || itemProps.checked === "indeterminate",
          disabled: itemProps.disabled,
          readOnly: true,
          mixed: itemProps.checked === "indeterminate"
        }
      ),
      tickable && !toggleable && /* @__PURE__ */ jsx18(Fragment6, { children: /* @__PURE__ */ jsx18(ActiveWrapper, { children: /* @__PURE__ */ jsx18(RadixDropdownMenu2.ItemIndicator, { children: /* @__PURE__ */ jsx18(Icon, { iconName: "Tick", iconColor: "lilac500" }) }) }) }),
      iconName && /* @__PURE__ */ jsx18(
        Icon,
        {
          iconName,
          iconColor: tickable && itemProps.checked ? "lilac500" : void 0
        }
      ),
      icon5 && icon5,
      content5 ? /* @__PURE__ */ jsx18(Text, { kind: "bodyMd", children: content5 }) : children,
      toggleable && !tickable && /* @__PURE__ */ jsx18(
        Switch,
        {
          style: { marginLeft: "auto" },
          checked: itemProps.checked === true,
          disabled: itemProps.disabled
        }
      )
    ] }) });
  }
);
function CheckboxItemDiv(props) {
  return /* @__PURE__ */ jsxs16(StyledMenuItemDiv, { onClick: props.onClick, children: [
    /* @__PURE__ */ jsx18(Checkbox, { checked: props.checked, readOnly: true }),
    /* @__PURE__ */ jsx18(Text, { kind: "bodyMd", children: props.content })
  ] });
}
var ActiveWrapper = styled17.span`
  width: 21px;
`;
var StyledMenuItemDiv = styled17(MenuItemDiv)`
  &:focus-within {
    background: ${colors.grey50};
  }
`;

// src/dropdown-menu/content.tsx
import { forwardRef as forwardRef10 } from "react";
import { styled as styled18 } from "styled-components";
import * as RadixDropdownMenu3 from "@radix-ui/react-dropdown-menu";
import { jsx as jsx19 } from "react/jsx-runtime";
var Content6 = forwardRef10(
  (props, ref) => {
    const {
      children,
      align = "start",
      sideOffset = 6,
      collisionPadding = 4,
      ...contentProps
    } = props;
    return /* @__PURE__ */ jsx19(RadixDropdownMenu3.Portal, { children: /* @__PURE__ */ jsx19(
      RadixDropdownMenu3.Content,
      {
        align,
        sideOffset,
        collisionPadding,
        ...contentProps,
        ref,
        asChild: true,
        children: /* @__PURE__ */ jsx19(ContentDiv, { children })
      }
    ) });
  }
);
var ContentDiv = styled18.div`
  padding: 8px;
  margin: 0;
  min-width: max(var(--radix-dropdown-trigger-width, 200px), 200px);
  border-radius: 8px;
  background-color: ${(p) => p.theme.colors.white100};
  box-sizing: border-box;
  box-shadow: ${(p) => p.theme.levels.two};
  overflow-y: auto;
  max-height: var(--radix-dropdown-menu-content-available-height);
  z-index: 10000;

  &:empty {
    visibility: hidden;
  }
`;

// src/dropdown-menu/label.tsx
import { forwardRef as forwardRef11 } from "react";
import { styled as styled19 } from "styled-components";
import * as RadixDropdownMenu4 from "@radix-ui/react-dropdown-menu";
import { Fragment as Fragment7, jsx as jsx20, jsxs as jsxs17 } from "react/jsx-runtime";
var Label4 = forwardRef11((props, ref) => {
  const { children, subtitle, ...divProps } = props;
  return /* @__PURE__ */ jsx20(StyledLabel, { ref, ...divProps, children: typeof children === "string" ? /* @__PURE__ */ jsxs17(Fragment7, { children: [
    /* @__PURE__ */ jsx20(Text, { kind: "headingSix", as: "div", inline: true, children }),
    subtitle && /* @__PURE__ */ jsx20(Text, { kind: "bodyMd", as: "div", inline: true, children: subtitle })
  ] }) : children });
});
var StyledLabel = styled19(RadixDropdownMenu4.Label)`
  padding: 8px 16px;
  box-sizing: border-box;
`;

// src/dropdown-menu/link-item.tsx
import { forwardRef as forwardRef12 } from "react";
import { jsx as jsx21 } from "react/jsx-runtime";
var LinkItem = forwardRef12(
  (props, ref) => {
    const { children, color: color2, content: content5, ...itemProps } = props;
    return /* @__PURE__ */ jsx21(Item4, { ref, ...itemProps, children: /* @__PURE__ */ jsx21(Text, { color: color2 ?? "primary", kind: "bodyMd", children: content5 ?? children }) });
  }
);

// src/dropdown-menu/radio-item.tsx
import { forwardRef as forwardRef15 } from "react";
import * as RadixDropdownMenu5 from "@radix-ui/react-dropdown-menu";

// src/radio/radio-indicator.tsx
import { styled as styled20 } from "styled-components";
var mediumCheckedInnerCircle = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Ccircle cx='8' cy='8' r='4' fill='%23FFFFFF'/%3E%3C/svg%3E");`;
var largeCheckedInnerCircle = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Ccircle cx='11' cy='11' r='5' fill='%23FFFFFF'/%3E%3C/svg%3E");`;
var RadioIndicator = styled20.span`
  flex: none;
  display: inline-block;

  width: ${(p) => p.$size === "lg" ? 24 : 18}px;
  height: ${(p) => p.$size === "lg" ? 24 : 18}px;

  position: relative;

  box-sizing: border-box;
  border: 1px solid ${(p) => p.theme.text.shade2};
  background: white;
  border-radius: 50%;

  cursor: pointer;

  /* The :before pseudo-element creates a focus and ping outline */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 50%;
    width: 100%;
    height: 100%;
  }

  /* Apply checked styles to Box */
  &[data-state="checked"] {
    background: ${(p) => p.$color ? p.theme.colors[p.$color] : p.theme.colors.primary};
    background-image: ${(p) => p.$size === "lg" ? largeCheckedInnerCircle : mediumCheckedInnerCircle};
  }
  &[data-disabled] {
    cursor: not-allowed;
  }
`;

// src/radio/radio.tsx
import { forwardRef as forwardRef13 } from "react";
import { styled as styled21 } from "styled-components";
import { jsx as jsx22, jsxs as jsxs18 } from "react/jsx-runtime";
var Radio = forwardRef13(
  ({
    content: content5,
    className,
    children,
    sizeVariant = "md",
    colorVariant = "black",
    boxMargin = "6px 9px 6px 0",
    ...inputProps
  }, ref) => {
    return /* @__PURE__ */ jsxs18(Label5, { className, children: [
      /* @__PURE__ */ jsx22(
        HiddenInput2,
        {
          ref,
          type: "radio",
          sizeVariant,
          colorVariant,
          ...inputProps
        }
      ),
      /* @__PURE__ */ jsx22(Box2, { sizeVariant, boxMargin }),
      content5 ? /* @__PURE__ */ jsx22(Text, { kind: "bodyLine", as: "span", children: content5 }) : children
    ] });
  }
);
var Label5 = styled21.label`
  margin-right: ${(p) => p.marginRight ? p.marginRight : 18}px;
  display: inline-flex;
  align-items: center;

  outline: 0;

  position: relative;
  cursor: pointer;

  /* Remove cursor pointer when input is disabled */
  input:disabled ~ * {
    cursor: default;
  }
`;
var Box2 = styled21.span`
  flex: none;
  display: inline-block;
  width: ${(p) => p.sizeVariant === "lg" ? 24 : p.sizeVariant === "md" ? 18 : 12}px;
  height: ${(p) => p.sizeVariant === "lg" ? 24 : p.sizeVariant === "md" ? 18 : 12}px;
  margin: ${(p) => p.boxMargin ? p.boxMargin : `6px 9px 6px 0`};

  position: relative;

  box-sizing: border-box;
  border: 1px solid ${(p) => p.theme.text.shade2};
  background: white;
  border-radius: 50%;

  cursor: pointer;

  /* The :before pseudo-element creates a focus and ping outline */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 50%;
    width: 100%;
    height: 100%;
  }
`;
var smallCheckedInnerCircle = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Ccircle cx='5' cy='5' r='3' fill='%23FFFFFF'/%3E%3C/svg%3E");`;
var mediumCheckedInnerCircle2 = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Ccircle cx='8' cy='8' r='4' fill='%23FFFFFF'/%3E%3C/svg%3E");`;
var largeCheckedInnerCircle2 = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Ccircle cx='11' cy='11' r='5' fill='%23FFFFFF'/%3E%3C/svg%3E");`;
var HiddenInput2 = styled21.input`
  position: absolute;
  z-index: -1;
  opacity: 0;
  width: ${(p) => p.sizeVariant === "lg" ? 24 : p.sizeVariant === "md" ? 18 : 12}px; /* Same as Box */
  height: ${(p) => p.sizeVariant === "lg" ? 24 : p.sizeVariant === "md" ? 18 : 12}px; /* Same as Box */

  cursor: pointer;

  /* Apply hover styles to Box when unchecked */
  &:not(:checked) ~ ${Box2}:hover {
    background: ${(p) => p.theme.background.action1};
  }

  /* Apply active styles to Box when unchecked */
  &:not(:checked) ~ ${Box2}:active {
    background: ${(p) => p.theme.background.action2};
  }

  &:not(:disabled) {
    /* Apply focued styles to Box */
    &:focus ~ ${Box2} {
      border-color: ${(p) => p.theme.text.shade2};
    }

    /* Apply focued styles to Box's focus pseudo-element */
    &:focus ~ ${Box2}:before {
      ${FocusAnimation};
    }
  }

  /* Apply checked styles to Box */
  &:checked ~ ${Box2} {
    background: ${(p) => p.colorVariant === "blue" ? p.theme.colors.blue500 : p.colorVariant === "black" ? p.theme.colors.black200 : p.theme.colors[p.colorVariant]};
    background-image: ${(p) => p.sizeVariant === "lg" ? largeCheckedInnerCircle2 : p.sizeVariant === "md" ? mediumCheckedInnerCircle2 : smallCheckedInnerCircle};
  }

  /* Apply disabled styles to Box and Text */
  &:disabled ~ ${Box2}, &:disabled ~ ${Text} {
    opacity: 0.36;
  }

  &:disabled ~ ${Box2} {
    cursor: not-allowed;
  }
`;

// src/radio/radio-group.tsx
import { forwardRef as forwardRef14, useId as useId2 } from "react";
import * as RadixRadioGroup from "@radix-ui/react-radio-group";
import clsx6 from "clsx";

// src/radio/radio.css.ts
var card = "e9k2650";
var item = "e9k2653";
var label2 = "e9k2651";
var option = "e9k2652";

// src/radio/radio-group.tsx
import { jsx as jsx23, jsxs as jsxs19 } from "react/jsx-runtime";
var CardItem = forwardRef14((props, ref) => {
  const {
    children,
    size = "md",
    name,
    description: description2,
    className,
    indicatorColor = "lilac500",
    ...itemProps
  } = props;
  const id = useId2();
  return /* @__PURE__ */ jsxs19(
    RadixRadioGroup.Item,
    {
      id,
      ref,
      className: clsx6(card, className),
      ...itemProps,
      children: [
        /* @__PURE__ */ jsx23(RadixRadioGroup.Indicator, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx23(RadioIndicator, { $size: size, $color: indicatorColor }) }),
        /* @__PURE__ */ jsxs19(
          "label",
          {
            htmlFor: id,
            className: label2,
            style: { gap: size === "md" ? 4 : 8 },
            children: [
              name && /* @__PURE__ */ jsx23(
                Text,
                {
                  kind: size === "md" ? "headingSix" : "headingFour",
                  as: "div",
                  color: "neutralblack500",
                  inline: true,
                  children: name
                }
              ),
              description2 && /* @__PURE__ */ jsx23(Text, { kind: "bodyMd", as: "div", color: "neutralblack300", inline: true, children: description2 }),
              children
            ]
          }
        )
      ]
    }
  );
});
var Option = forwardRef14((props, ref) => {
  const { children, className, ...itemProps } = props;
  const id = useId2();
  return /* @__PURE__ */ jsxs19("div", { ref, className: clsx6(option, className), children: [
    /* @__PURE__ */ jsx23(RadixRadioGroup.Item, { id, className: item, ...itemProps, children: /* @__PURE__ */ jsx23(RadixRadioGroup.Indicator, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx23(RadioIndicator, { $size: "md", $color: "lilac500" }) }) }),
    /* @__PURE__ */ jsx23("label", { htmlFor: id, className: label2, children })
  ] });
});
var RadioGroup = {
  Root: RadixRadioGroup.Root,
  Item: RadixRadioGroup.Item,
  Card: CardItem,
  Option
};

// src/dropdown-menu/radio-item.tsx
import { jsx as jsx24, jsxs as jsxs20 } from "react/jsx-runtime";
var RadioItem2 = forwardRef15(
  (props, ref) => {
    const {
      children,
      content: content5,
      icon: icon5,
      radioColor = "lilac500",
      ...itemProps
    } = props;
    return /* @__PURE__ */ jsx24(RadixDropdownMenu5.RadioItem, { asChild: true, ...itemProps, ref, children: /* @__PURE__ */ jsxs20(MenuItemDiv, { children: [
      /* @__PURE__ */ jsx24(RadixDropdownMenu5.ItemIndicator, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx24(RadioIndicator, { $size: "md", $color: radioColor }) }),
      icon5,
      content5 ? /* @__PURE__ */ jsx24(Text, { kind: "bodyMd", as: "span", children: content5 }) : children
    ] }) });
  }
);

// src/dropdown-menu/separator.tsx
import { forwardRef as forwardRef16 } from "react";
import { styled as styled22 } from "styled-components";
import * as RadixDropdownMenu6 from "@radix-ui/react-dropdown-menu";
import { jsx as jsx25 } from "react/jsx-runtime";
var Separator3 = forwardRef16((_props, ref) => /* @__PURE__ */ jsx25(StyledSeparator2, { ref, children: /* @__PURE__ */ jsx25(Line2, {}) }));
var StyledSeparator2 = styled22(RadixDropdownMenu6.Separator)`
  padding: 4px 0px;
`;
var Line2 = styled22.div`
  display: block;
  height: 1px;
  background: ${(p) => p.theme.colors.grey100};
`;

// src/dropdown-menu/sub-content.tsx
import { forwardRef as forwardRef17 } from "react";
import * as RadixDropdownMenu7 from "@radix-ui/react-dropdown-menu";
import { jsx as jsx26 } from "react/jsx-runtime";
var SubContent2 = forwardRef17(
  (props, ref) => {
    const { children, ...contentProps } = props;
    return /* @__PURE__ */ jsx26(RadixDropdownMenu7.Portal, { children: /* @__PURE__ */ jsx26(RadixDropdownMenu7.SubContent, { ...contentProps, ref, asChild: true, children: /* @__PURE__ */ jsx26(ContentDiv, { children }) }) });
  }
);

// src/dropdown-menu/index.ts
var DropdownMenu = {
  Root: Root7,
  Trigger: Trigger4,
  Separator: Separator3,
  Content: Content6,
  ContentDiv,
  Label: Label4,
  Item: Item4,
  MenuItemDiv,
  CheckboxItem: CheckboxItem2,
  LinkItem,
  Sub,
  SubTrigger,
  SubContent: SubContent2,
  RadioGroup: RadioGroup2,
  RadioItem: RadioItem2,
  CheckboxItemDiv
};

// src/controlbar/collaborators-popover.css.ts
var avatarIndex = "var(--_1d5ri5l0)";
var avatarShift = "var(--_1d5ri5l1)";
var stackedAvatar = "_1d5ri5l2";

// src/controlbar/collaborators-popover.tsx
import { jsx as jsx27, jsxs as jsxs21 } from "react/jsx-runtime";
var CollaboratorsPopover = forwardRef18((props, ref) => {
  const { collaborators, disabled: disabled2, onCollaboratorSelected } = props;
  if (!collaborators || !collaborators?.length) return null;
  const subsetOfCollaborators = collaborators.slice(0, 3);
  return /* @__PURE__ */ jsxs21(DropdownMenu.Root, { children: [
    /* @__PURE__ */ jsx27(RadixToolbar2.Button, { asChild: true, children: /* @__PURE__ */ jsx27(DropdownMenu.Trigger, { asChild: true, disabled: disabled2, children: /* @__PURE__ */ jsxs21(ConnectedUsersSection, { children: [
      subsetOfCollaborators.map((user, index) => /* @__PURE__ */ jsx27(
        AvatarIcon,
        {
          size: "sm",
          name: user.name,
          shade: user.colourset,
          className: stackedAvatar,
          style: assignInlineVars({
            [avatarIndex]: `${-index}`,
            [avatarShift]: index > 0 ? `${-5}px` : "2px"
          })
        },
        user.clientId
      )),
      !disabled2 && /* @__PURE__ */ jsx27(
        ControlButton,
        {
          ref,
          "aria-label": "Connected Users",
          icon: /* @__PURE__ */ jsx27(
            ChevronDownIcon2,
            {
              size: 16,
              label: "",
              style: { color: "gray" }
            }
          )
        }
      )
    ] }) }) }),
    /* @__PURE__ */ jsx27(
      DropdownMenu.Content,
      {
        align: "end",
        alignOffset: 15,
        onCloseAutoFocus: (e) => e.preventDefault(),
        children: collaborators.map((collaborator) => {
          const inactive = collaborator.anchor === null;
          return /* @__PURE__ */ jsxs21(
            DropdownMenu.Item,
            {
              disabled: inactive,
              onClick: () => onCollaboratorSelected(collaborator),
              children: [
                /* @__PURE__ */ jsx27(
                  AvatarIcon,
                  {
                    size: "sm",
                    name: collaborator.name,
                    shade: collaborator.colourset
                  },
                  `content-${collaborator.clientId}`
                ),
                /* @__PURE__ */ jsx27(Text, { kind: "bodyLine", children: collaborator.name })
              ]
            },
            collaborator.clientId
          );
        })
      }
    )
  ] });
});
var ConnectedUsersSection = styled23.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0px 12px;
  cursor: pointer;
`;

// src/controlbar/control-group-popover.tsx
import { forwardRef as forwardRef19 } from "react";
import { styled as styled24 } from "styled-components";
import * as RadixToolbar3 from "@radix-ui/react-toolbar";
import { jsx as jsx28, jsxs as jsxs22 } from "react/jsx-runtime";
var ControlGroupPopover = forwardRef19((props, ref) => {
  const { children, ...buttonProps } = props;
  return /* @__PURE__ */ jsxs22(Popover.Root, { children: [
    /* @__PURE__ */ jsx28(RadixToolbar3.Button, { asChild: true, children: /* @__PURE__ */ jsx28(Popover.Trigger, { asChild: true, children: /* @__PURE__ */ jsx28(ControlButton, { ref, withDownCaret: true, ...buttonProps }) }) }),
    /* @__PURE__ */ jsx28(StyledPopoverContent, { sideOffset: 2, children })
  ] });
});
var StyledPopoverContent = styled24(Popover.Content)`
  border-radius: 3px;
  padding: 2px;
  margin: 0;

  display: flex;

  background: white;
  background-color: ${(p) => p.theme.background.default};
  z-index: 4;
  position: relative;
  box-shadow: ${levels.three};

  max-width: calc(var(--radix-popover-content-available-width) - 1rem);
  flex-wrap: wrap;
`;

// src/controlbar/control-paragraph.tsx
import { forwardRef as forwardRef20, useMemo as useMemo2 } from "react";
import * as RadixToolbar4 from "@radix-ui/react-toolbar";
import { jsx as jsx29, jsxs as jsxs23 } from "react/jsx-runtime";
var Items = [
  ["paragraph", "Paragraph"],
  ["blockquote", "Quote"],
  ["heading-one", "Title"],
  ["heading-two", "Subtitle"],
  ["heading-three", "Heading"],
  ["heading-four", "Subheading"]
];
var ControlParagraph = forwardRef20(
  (props, ref) => {
    const { paragraphStyle, disabled: disabled2, onChange, enabledParagraphStyles } = props;
    const selectedText = useMemo2(() => {
      const selectedItem = Items.find(([key]) => key === paragraphStyle);
      return selectedItem ? selectedItem[1] : "Paragraph";
    }, [paragraphStyle]);
    const handleSelect = (style) => {
      onChange(style);
    };
    return /* @__PURE__ */ jsxs23(DropdownMenu.Root, { children: [
      /* @__PURE__ */ jsx29(RadixToolbar4.Button, { asChild: true, children: /* @__PURE__ */ jsx29(DropdownMenu.Trigger, { asChild: true, children: /* @__PURE__ */ jsx29(
        ControlButton,
        {
          ref,
          disabled: disabled2,
          "aria-disabled": disabled2,
          "aria-label": "Paragraph Style",
          withDownCaret: true,
          children: selectedText
        }
      ) }) }),
      /* @__PURE__ */ jsx29(DropdownMenu.Content, { onCloseAutoFocus: (e) => e.preventDefault(), children: Items.map(([style, label4]) => /* @__PURE__ */ jsx29(
        DropdownMenu.Item,
        {
          selected: label4 === selectedText,
          "aria-selected": label4 === selectedText,
          onSelect: () => handleSelect(style),
          "aria-label": label4,
          disabled: disabled2 || !enabledParagraphStyles?.includes(style),
          children: label4
        },
        style
      )) })
    ] });
  }
);

// src/controlbar/controls/styles-controls.tsx
import * as RadixToggleGroup2 from "@radix-ui/react-toggle-group";
import * as RadixToolbar6 from "@radix-ui/react-toolbar";

// src/controlbar/shared.tsx
import { styled as styled25 } from "styled-components";
import * as RadixToolbar5 from "@radix-ui/react-toolbar";
import { jsx as jsx30 } from "react/jsx-runtime";
var ToggleGroup2 = styled25(RadixToolbar5.ToggleGroup)`
  display: flex;
`;
function renderControlButton(plugin, title, iconName) {
  if (plugin) {
    const { onClick, active, disabled: disabled2, disabledMessage } = plugin;
    return /* @__PURE__ */ jsx30(
      ControlButton,
      {
        "aria-label": disabled2 && disabledMessage ? disabledMessage : title,
        selected: active,
        disabled: disabled2,
        onClick,
        iconName
      }
    );
  }
  return /* @__PURE__ */ jsx30(
    ControlButton,
    {
      "aria-label": title,
      iconName,
      disabled: true,
      tooltipPosition: "top"
    }
  );
}

// src/controlbar/controls/responsiveness.tsx
import { styled as styled26 } from "styled-components";
var BREAKPOINTS = {
  textStyling: 910,
  alignment: 1085,
  list: 995,
  colors: 935,
  media: 1085
};
function createBreakpointUtilities(breakpointSizePx) {
  return {
    /** Hide content when the container width is bigger than the defined breakpoint. */
    Dropdown: styled26.div`
      display: flex;
      @container editor (min-width: ${breakpointSizePx}px) {
        display: none;
      }
    `,
    /** Show content when the container width is bigger than the defined breakpoint. */
    Expanded: styled26.div`
      display: none;
      @container editor (min-width: ${breakpointSizePx}px) {
        display: flex;
      }
    `
  };
}
var TextStylingResponsiveness = createBreakpointUtilities(
  BREAKPOINTS.textStyling
);
var ListStyleResponsiveness = createBreakpointUtilities(
  BREAKPOINTS.list
);
var ColorsResponsiveness = createBreakpointUtilities(
  BREAKPOINTS.colors
);
var AlignmentResponsiveness = createBreakpointUtilities(
  BREAKPOINTS.alignment
);
var MediaResponsiveness = createBreakpointUtilities(
  BREAKPOINTS.media
);

// src/controlbar/controls/styles-controls.tsx
import { Fragment as Fragment8, jsx as jsx31, jsxs as jsxs24 } from "react/jsx-runtime";
var StylesControls = (props) => {
  const { bold, italic, underline, strikethrough, superscript, subscript } = props;
  const stylesDisabled = [
    bold,
    italic,
    underline,
    strikethrough,
    superscript,
    subscript
  ].every((plugin) => plugin?.disabled);
  const boldButton = renderControlButton(bold, "Bold", "Bold");
  const italicButton = renderControlButton(italic, "Italic", "Italic");
  const underlineButton = renderControlButton(
    underline,
    "Underline",
    "Underline"
  );
  const strikethroughButton = renderControlButton(
    strikethrough,
    "Strikethrough",
    "Strikethrough"
  );
  const superscriptButton = renderControlButton(
    superscript,
    "Superscript",
    "Superscript"
  );
  const subscriptButton = renderControlButton(
    subscript,
    "Subscript",
    "Subscript"
  );
  return /* @__PURE__ */ jsxs24(Fragment8, { children: [
    /* @__PURE__ */ jsx31(TextStylingResponsiveness.Dropdown, { children: /* @__PURE__ */ jsx31(
      ControlGroupPopover,
      {
        iconName: "Bold",
        "aria-label": "Style",
        disabled: stylesDisabled,
        "aria-disabled": stylesDisabled,
        children: /* @__PURE__ */ jsxs24(RadixToggleGroup2.Root, { type: "single", tabIndex: 0, children: [
          /* @__PURE__ */ jsx31(RadixToggleGroup2.ToggleGroupItem, { asChild: true, value: "bold", children: boldButton }),
          /* @__PURE__ */ jsx31(RadixToggleGroup2.ToggleGroupItem, { asChild: true, value: "italic", children: italicButton }),
          /* @__PURE__ */ jsx31(RadixToggleGroup2.ToggleGroupItem, { asChild: true, value: "underline", children: underlineButton }),
          /* @__PURE__ */ jsx31(RadixToggleGroup2.ToggleGroupItem, { asChild: true, value: "strikethrough", children: strikethroughButton }),
          /* @__PURE__ */ jsx31(RadixToggleGroup2.ToggleGroupItem, { asChild: true, value: "superscript", children: superscriptButton }),
          /* @__PURE__ */ jsx31(RadixToggleGroup2.ToggleGroupItem, { asChild: true, value: "subscript", children: subscriptButton })
        ] })
      }
    ) }),
    /* @__PURE__ */ jsx31(TextStylingResponsiveness.Expanded, { children: /* @__PURE__ */ jsxs24(ToggleGroup2, { type: "multiple", children: [
      /* @__PURE__ */ jsx31(RadixToolbar6.ToggleItem, { asChild: true, value: "bold", children: boldButton }),
      /* @__PURE__ */ jsx31(RadixToolbar6.ToggleItem, { asChild: true, value: "italic", children: italicButton }),
      /* @__PURE__ */ jsx31(RadixToolbar6.ToggleItem, { asChild: true, value: "underline", children: underlineButton }),
      /* @__PURE__ */ jsx31(RadixToolbar6.ToggleItem, { asChild: true, value: "strikethrough", children: strikethroughButton }),
      /* @__PURE__ */ jsx31(RadixToolbar6.ToggleItem, { asChild: true, value: "superscript", children: superscriptButton }),
      /* @__PURE__ */ jsx31(RadixToolbar6.ToggleItem, { asChild: true, value: "subscript", children: subscriptButton })
    ] }) })
  ] });
};

// src/controlbar/controls/color-controls.tsx
import { styled as styled27 } from "styled-components";
import * as RadixToggleGroup3 from "@radix-ui/react-toggle-group";
import { Fragment as Fragment9, jsx as jsx32, jsxs as jsxs25 } from "react/jsx-runtime";
var ColorControls = (props) => {
  const { textColor: textColor2, highlightColor } = props;
  const colorDisabled = [textColor2, highlightColor].every(
    (member) => member === void 0 || member.disabled
  );
  return /* @__PURE__ */ jsxs25(Fragment9, { children: [
    /* @__PURE__ */ jsx32(ColorsResponsiveness.Dropdown, { children: /* @__PURE__ */ jsx32(
      ControlGroupPopover,
      {
        iconName: "Color",
        "aria-label": "Color",
        disabled: colorDisabled,
        "aria-disabled": colorDisabled ? "true" : "false",
        children: /* @__PURE__ */ jsxs25(RadixToggleGroup3.Root, { tabIndex: 0, type: "single", children: [
          /* @__PURE__ */ jsx32(RadixToggleGroup3.ToggleGroupItem, { value: "text-color", asChild: true, children: /* @__PURE__ */ jsx32(
            ColorPickerMenu,
            {
              value: textColor2 ? textColor2.activeColor : DEFAULT_TEXT_COLOR,
              pickerProps: {
                value: textColor2 ? textColor2.activeColor : DEFAULT_TEXT_COLOR,
                onChange: textColor2 ? textColor2.onChange : void 0,
                defaultColor: DEFAULT_TEXT_COLOR,
                defaultLabel: "Black",
                pallete: TEXT_COLORS
              },
              disabled: textColor2 ? textColor2.disabled : true,
              title: "Text colour",
              "aria-label": "Text colour",
              iconName: "Color"
            }
          ) }),
          /* @__PURE__ */ jsx32(RadixToggleGroup3.ToggleGroupItem, { value: "highlight-color", asChild: true, children: /* @__PURE__ */ jsx32(
            ColorPickerMenu,
            {
              value: highlightColor ? highlightColor.activeColor : DEFAULT_HIGHLIGHT_COLOR,
              pickerProps: {
                value: highlightColor ? highlightColor.activeColor : DEFAULT_HIGHLIGHT_COLOR,
                onChange: highlightColor ? highlightColor.onChange : void 0,
                defaultColor: DEFAULT_HIGHLIGHT_COLOR,
                defaultLabel: "None",
                pallete: HIGHLIGHT_COLORS
              },
              disabled: highlightColor ? highlightColor.disabled : true,
              title: "Highlight colour",
              "aria-label": "Highlight colour",
              iconName: "Highlight"
            }
          ) })
        ] })
      }
    ) }),
    /* @__PURE__ */ jsx32(ColorsResponsiveness.Expanded, { children: /* @__PURE__ */ jsxs25(FlexDiv, { children: [
      /* @__PURE__ */ jsx32(
        ColorPickerMenu,
        {
          value: textColor2 ? textColor2.activeColor : DEFAULT_TEXT_COLOR,
          pickerProps: {
            value: textColor2 ? textColor2.activeColor : DEFAULT_TEXT_COLOR,
            onChange: textColor2 ? textColor2.onChange : void 0,
            defaultColor: DEFAULT_TEXT_COLOR,
            defaultLabel: "Black",
            pallete: TEXT_COLORS
          },
          disabled: textColor2 ? textColor2.disabled : true,
          title: "Text colour",
          "aria-label": "Text colour",
          iconName: "Color",
          asToolbarButton: true
        }
      ),
      /* @__PURE__ */ jsx32(
        ColorPickerMenu,
        {
          value: highlightColor ? highlightColor.activeColor : DEFAULT_HIGHLIGHT_COLOR,
          pickerProps: {
            value: highlightColor ? highlightColor.activeColor : DEFAULT_HIGHLIGHT_COLOR,
            onChange: highlightColor ? highlightColor.onChange : void 0,
            defaultColor: DEFAULT_HIGHLIGHT_COLOR,
            defaultLabel: "None",
            pallete: HIGHLIGHT_COLORS
          },
          disabled: highlightColor ? highlightColor.disabled : true,
          title: "Highlight colour",
          "aria-label": "Highlight colour",
          iconName: "Highlight",
          asToolbarButton: true
        }
      )
    ] }) })
  ] });
};
var FlexDiv = styled27.div`
  display: flex;
`;

// src/controlbar/controls/list-controls.tsx
import * as RadixToggleGroup4 from "@radix-ui/react-toggle-group";
import * as RadixToolbar7 from "@radix-ui/react-toolbar";
import { Fragment as Fragment10, jsx as jsx33, jsxs as jsxs26 } from "react/jsx-runtime";
function ListControls(props) {
  const { orderedList, unorderedList, checkList } = props;
  const listDisabled = [orderedList, unorderedList, checkList].every(
    (member) => member === void 0 || member.disabled
  );
  return /* @__PURE__ */ jsxs26(Fragment10, { children: [
    /* @__PURE__ */ jsx33(ListStyleResponsiveness.Dropdown, { children: /* @__PURE__ */ jsx33(
      ControlGroupPopover,
      {
        iconName: "NumberList",
        "aria-label": "List",
        disabled: listDisabled,
        "aria-disabled": listDisabled ? "true" : "false",
        children: /* @__PURE__ */ jsxs26(RadixToggleGroup4.Root, { type: "single", tabIndex: 0, children: [
          /* @__PURE__ */ jsx33(RadixToggleGroup4.ToggleGroupItem, { asChild: true, value: "ordered-list", children: renderControlButton(orderedList, "Ordered list", "NumberList") }),
          /* @__PURE__ */ jsx33(RadixToggleGroup4.ToggleGroupItem, { asChild: true, value: "unordered-list", children: renderControlButton(
            unorderedList,
            "Unordered list",
            "BulletList"
          ) }),
          /* @__PURE__ */ jsx33(RadixToggleGroup4.ToggleGroupItem, { asChild: true, value: "check-list", children: renderControlButton(checkList, "Check list", "CheckList") })
        ] })
      }
    ) }),
    /* @__PURE__ */ jsx33(ListStyleResponsiveness.Expanded, { children: /* @__PURE__ */ jsxs26(ToggleGroup2, { type: "single", children: [
      /* @__PURE__ */ jsx33(RadixToolbar7.ToggleItem, { asChild: true, value: "ordered-list", children: renderControlButton(orderedList, "Ordered list", "NumberList") }),
      /* @__PURE__ */ jsx33(RadixToolbar7.ToggleItem, { asChild: true, value: "unordered-list", children: renderControlButton(unorderedList, "Unordered list", "BulletList") }),
      /* @__PURE__ */ jsx33(RadixToolbar7.ToggleItem, { asChild: true, value: "check-list", children: renderControlButton(checkList, "Check list", "CheckList") })
    ] }) })
  ] });
}

// src/controlbar/controls/alignment-controls.tsx
import * as RadixToggleGroup5 from "@radix-ui/react-toggle-group";
import * as RadixToolbar8 from "@radix-ui/react-toolbar";
import { Fragment as Fragment11, jsx as jsx34, jsxs as jsxs27 } from "react/jsx-runtime";
function AlignmentControls(props) {
  const { leftAlign, centerAlign, rightAlign, justifyAlign } = props;
  const alignmentDisabled = [
    leftAlign,
    centerAlign,
    rightAlign,
    justifyAlign
  ].every((member) => member === void 0 || member.disabled);
  return /* @__PURE__ */ jsxs27(Fragment11, { children: [
    /* @__PURE__ */ jsx34(AlignmentResponsiveness.Dropdown, { children: /* @__PURE__ */ jsx34(
      ControlGroupPopover,
      {
        iconName: "LeftAlign",
        "aria-label": "Alignment",
        disabled: alignmentDisabled,
        "aria-disabled": alignmentDisabled ? "true" : "false",
        children: /* @__PURE__ */ jsxs27(RadixToggleGroup5.Root, { type: "single", tabIndex: 0, children: [
          /* @__PURE__ */ jsx34(RadixToggleGroup5.ToggleGroupItem, { asChild: true, value: "left-align", children: renderControlButton(leftAlign, "Left align", "LeftAlign") }),
          /* @__PURE__ */ jsx34(RadixToggleGroup5.ToggleGroupItem, { asChild: true, value: "center-align", children: renderControlButton(centerAlign, "Center align", "CenterAlign") }),
          /* @__PURE__ */ jsx34(RadixToggleGroup5.ToggleGroupItem, { asChild: true, value: "right-align", children: renderControlButton(rightAlign, "Right align", "RightAlign") }),
          /* @__PURE__ */ jsx34(RadixToggleGroup5.ToggleGroupItem, { asChild: true, value: "justify-align", children: renderControlButton(justifyAlign, "Justify align", "Justify") })
        ] })
      }
    ) }),
    /* @__PURE__ */ jsx34(AlignmentResponsiveness.Expanded, { children: /* @__PURE__ */ jsxs27(ToggleGroup2, { type: "single", children: [
      /* @__PURE__ */ jsx34(RadixToolbar8.ToggleItem, { asChild: true, value: "left-align", children: renderControlButton(leftAlign, "Left align", "LeftAlign") }),
      /* @__PURE__ */ jsx34(RadixToolbar8.ToggleItem, { asChild: true, value: "center-align", children: renderControlButton(centerAlign, "Center align", "CenterAlign") }),
      /* @__PURE__ */ jsx34(RadixToolbar8.ToggleItem, { asChild: true, value: "right-align", children: renderControlButton(rightAlign, "Right align", "RightAlign") }),
      /* @__PURE__ */ jsx34(RadixToolbar8.ToggleItem, { asChild: true, value: "justify", children: renderControlButton(justifyAlign, "Justify align", "Justify") })
    ] }) })
  ] });
}

// src/controlbar/controls/media-controls.tsx
import * as RadixToggleGroup6 from "@radix-ui/react-toggle-group";
import * as RadixToolbar9 from "@radix-ui/react-toolbar";
import { Fragment as Fragment12, jsx as jsx35, jsxs as jsxs28 } from "react/jsx-runtime";
function isDisabled(prop) {
  return prop === void 0 || !!prop.disabled;
}
function MediaControls(props) {
  const { image, audio, video } = props;
  const showAudio = !isDisabled(audio);
  const showVideo = !isDisabled(video);
  const imageOnly = !showAudio && !showVideo;
  if (imageOnly) {
    return /* @__PURE__ */ jsx35(
      RadixToolbar9.Button,
      {
        asChild: true,
        "aria-label": "Image",
        disabled: isDisabled(image),
        "aria-disabled": isDisabled(image) ? "true" : "false",
        children: renderControlButton(image, "Insert image", "Image")
      }
    );
  }
  return /* @__PURE__ */ jsxs28(Fragment12, { children: [
    /* @__PURE__ */ jsx35(MediaResponsiveness.Dropdown, { children: /* @__PURE__ */ jsx35(
      ControlGroupPopover,
      {
        iconName: "Image",
        "aria-label": "Upload media",
        disabled: isDisabled(image),
        "aria-disabled": isDisabled(image) ? "true" : "false",
        children: /* @__PURE__ */ jsxs28(RadixToggleGroup6.Root, { type: "single", tabIndex: 0, children: [
          /* @__PURE__ */ jsx35(RadixToggleGroup6.ToggleGroupItem, { asChild: true, value: "image", children: renderControlButton(image, "Insert image", "Image") }),
          showAudio && /* @__PURE__ */ jsx35(RadixToggleGroup6.ToggleGroupItem, { asChild: true, value: "audio", children: renderControlButton(audio, "Upload audio", "AudioUpload") }),
          showVideo && /* @__PURE__ */ jsx35(RadixToggleGroup6.ToggleGroupItem, { asChild: true, value: "video", children: renderControlButton(video, "Upload video", "VideoUpload") })
        ] })
      }
    ) }),
    /* @__PURE__ */ jsx35(MediaResponsiveness.Expanded, { children: /* @__PURE__ */ jsxs28(ToggleGroup2, { type: "single", children: [
      /* @__PURE__ */ jsx35(RadixToolbar9.ToggleItem, { asChild: true, value: "image", children: renderControlButton(image, "Insert image", "Image") }),
      showAudio && /* @__PURE__ */ jsx35(RadixToolbar9.ToggleItem, { asChild: true, value: "audio", children: renderControlButton(audio, "Upload audio", "AudioUpload") }),
      showVideo && /* @__PURE__ */ jsx35(RadixToolbar9.ToggleItem, { asChild: true, value: "video", children: renderControlButton(video, "Upload video", "VideoUpload") })
    ] }) })
  ] });
}

// src/controlbar/controlbar.tsx
import { Fragment as Fragment13, jsx as jsx36, jsxs as jsxs29 } from "react/jsx-runtime";
function Controlbar(props) {
  const { className } = props;
  return /* @__PURE__ */ jsx36(Toolbar, { children: /* @__PURE__ */ jsx36(ToolbarRoot, { id: "cadmus-editor-toolbar", className, children: /* @__PURE__ */ jsx36(ToolbarBody, { ...props }) }) });
}
var ToolbarBody = (props) => {
  const {
    undo,
    redo,
    paragraph,
    bold,
    italic,
    underline,
    strikethrough,
    leftAlign,
    centerAlign,
    rightAlign,
    justifyAlign,
    orderedList,
    unorderedList,
    checkList,
    indent,
    outdent,
    firstLineIndent,
    hangingIndent,
    normalIndent,
    image,
    audio,
    video,
    table,
    textColor: textColor2,
    highlightColor,
    superscript,
    subscript,
    link,
    codeBlock,
    code,
    blockMath,
    inlineMath,
    footnote,
    thread,
    activeCollaborators
  } = props;
  const indentationDisabled = [
    indent,
    outdent,
    firstLineIndent,
    normalIndent,
    hangingIndent
  ].every((member) => member === void 0 || member.disabled);
  const codeDisabled = [code, codeBlock].every(
    (member) => member === void 0 || member.disabled
  );
  const equationDisabled = [inlineMath, blockMath].every(
    (member) => member === void 0 || member.disabled
  );
  return /* @__PURE__ */ jsxs29(Fragment13, { children: [
    /* @__PURE__ */ jsx36(RadixToolbar10.Button, { asChild: true, "aria-label": "Undo", children: renderControlButton(undo, "Undo", "Undo") }),
    /* @__PURE__ */ jsx36(RadixToolbar10.Button, { asChild: true, "aria-label": "Redo", children: renderControlButton(redo, "Redo", "Redo") }),
    paragraph && /* @__PURE__ */ jsxs29(Fragment13, { children: [
      /* @__PURE__ */ jsx36(ToolbarSeparator, {}),
      /* @__PURE__ */ jsx36(ControlParagraph, { ...paragraph })
    ] }),
    /* @__PURE__ */ jsx36(ToolbarSeparator, {}),
    /* @__PURE__ */ jsx36(
      StylesControls,
      {
        bold,
        italic,
        underline,
        strikethrough,
        superscript,
        subscript
      }
    ),
    /* @__PURE__ */ jsx36(ToolbarSeparator, {}),
    /* @__PURE__ */ jsx36(ColorControls, { highlightColor, textColor: textColor2 }),
    /* @__PURE__ */ jsx36(ToolbarSeparator, {}),
    /* @__PURE__ */ jsx36(
      AlignmentControls,
      {
        leftAlign,
        centerAlign,
        justifyAlign,
        rightAlign
      }
    ),
    /* @__PURE__ */ jsx36(ToolbarSeparator, {}),
    /* @__PURE__ */ jsx36(
      ListControls,
      {
        orderedList,
        unorderedList,
        checkList
      }
    ),
    /* @__PURE__ */ jsx36(ToolbarSeparator, {}),
    /* @__PURE__ */ jsx36(
      ControlGroupPopover,
      {
        iconName: "HangingIndent",
        "aria-label": "Indentation",
        disabled: indentationDisabled,
        "aria-disabled": indentationDisabled ? "true" : "false",
        children: /* @__PURE__ */ jsxs29(RadixToggleGroup7.Root, { type: "single", tabIndex: 0, children: [
          /* @__PURE__ */ jsx36(RadixToggleGroup7.ToggleGroupItem, { value: "first-line", asChild: true, children: renderControlButton(
            firstLineIndent,
            "First line indent",
            "FirstLine"
          ) }),
          /* @__PURE__ */ jsx36(RadixToggleGroup7.ToggleGroupItem, { value: "hanging-indent", asChild: true, children: renderControlButton(
            hangingIndent,
            "Hanging indent",
            "HangingIndent"
          ) }),
          /* @__PURE__ */ jsx36(RadixToggleGroup7.ToggleGroupItem, { value: "reset-indent", asChild: true, children: renderControlButton(normalIndent, "Reset special indent", "Unset") }),
          /* @__PURE__ */ jsx36(Separator5, {}),
          /* @__PURE__ */ jsx36(RadixToggleGroup7.ToggleGroupItem, { value: "increase-indent", asChild: true, children: renderControlButton(indent, "Increase indent", "Indent") }),
          /* @__PURE__ */ jsx36(RadixToggleGroup7.ToggleGroupItem, { value: "decrease-indent", asChild: true, children: renderControlButton(outdent, "Decrease indent", "Outdent") })
        ] })
      }
    ),
    /* @__PURE__ */ jsx36(ToolbarSeparator, {}),
    /* @__PURE__ */ jsx36(
      RadixToolbar10.Button,
      {
        asChild: true,
        "aria-label": "Table",
        disabled: table?.disabled,
        children: renderControlButton(table, "Insert table", "Table")
      }
    ),
    /* @__PURE__ */ jsx36(
      RadixToolbar10.Button,
      {
        asChild: true,
        "aria-label": "Link",
        disabled: link?.disabled,
        "aria-disabled": link?.disabled ? "true" : "false",
        children: renderControlButton(link, "Insert link", "Link")
      }
    ),
    /* @__PURE__ */ jsx36(MediaControls, { image, audio, video }),
    /* @__PURE__ */ jsx36(
      ControlGroupPopover,
      {
        iconName: "Code",
        "aria-label": "Insert code",
        disabled: codeDisabled,
        "aria-disabled": codeDisabled ? "true" : "false",
        children: /* @__PURE__ */ jsxs29(RadixToggleGroup7.Root, { type: "single", tabIndex: 0, children: [
          /* @__PURE__ */ jsx36(RadixToggleGroup7.ToggleGroupItem, { value: "code-block", asChild: true, children: renderControlButton(
            codeBlock,
            "Insert Code Block (```)",
            "CodeBlock"
          ) }),
          /* @__PURE__ */ jsx36(RadixToggleGroup7.ToggleGroupItem, { value: "inline-code", asChild: true, children: renderControlButton(code, "Insert Inline Code (`)", "Code") })
        ] })
      }
    ),
    /* @__PURE__ */ jsx36(
      ControlGroupPopover,
      {
        iconName: "Equation",
        "aria-label": "Insert Equation",
        disabled: equationDisabled,
        "aria-disabled": equationDisabled ? "true" : "false",
        children: /* @__PURE__ */ jsxs29(RadixToggleGroup7.Root, { type: "single", tabIndex: 0, children: [
          /* @__PURE__ */ jsx36(RadixToggleGroup7.ToggleGroupItem, { value: "equation-block", asChild: true, children: renderControlButton(blockMath, "Block Equation", "Equation") }),
          /* @__PURE__ */ jsx36(RadixToggleGroup7.ToggleGroupItem, { value: "inline-equation", asChild: true, children: renderControlButton(
            inlineMath,
            "Inline Equation",
            "EquationInline"
          ) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx36(ToolbarSeparator, {}),
    footnote && /* @__PURE__ */ jsx36(Fragment13, { children: /* @__PURE__ */ jsx36(
      RadixToolbar10.Button,
      {
        asChild: true,
        "aria-label": "Insert footnote",
        disabled: footnote?.disabled,
        "aria-disabled": footnote?.disabled ? "true" : "false",
        children: /* @__PURE__ */ jsx36(
          ControlButton,
          {
            selected: footnote.active,
            disabled: footnote.disabled,
            onClick: footnote.onClick,
            disableChildrenMinWidth: true,
            hideTooltip: thread !== void 0,
            icon: /* @__PURE__ */ jsx36(InsertFootnoteIcon, { label: "Insert Footnote", size: 18 }),
            children: thread === void 0 ? /* @__PURE__ */ jsx36(Text, { kind: "systemMd", as: "span", style: { marginRight: "5px" }, children: "Insert footnote" }) : null
          }
        )
      }
    ) }),
    thread && /* @__PURE__ */ jsx36(
      RadixToolbar10.Button,
      {
        asChild: true,
        "aria-label": "Comment Thread",
        disabled: thread?.disabled,
        "aria-disabled": thread?.disabled ? "true" : "false",
        children: /* @__PURE__ */ jsx36(
          ControlButton,
          {
            selected: thread.active,
            disabled: thread.disabled,
            onClick: thread.onClick,
            disableChildrenMinWidth: true,
            icon: /* @__PURE__ */ jsx36(InsertCommentIcon, { label: "Comment Thread", size: 18 })
          }
        )
      }
    ),
    activeCollaborators && /* @__PURE__ */ jsx36(
      CollaboratorsPopover,
      {
        disabled: activeCollaborators.disabled,
        collaborators: activeCollaborators.collaborators,
        onCollaboratorSelected: activeCollaborators.onCollaboratorSelected
      }
    )
  ] });
};
var ToolbarRoot = styled28(RadixToolbar10.Root)`
  display: flex;
  flex: auto;
  width: 100%;
  flex-wrap: wrap;
  margin: auto;
`;
var ToolbarSeparator = styled28(RadixToolbar10.Separator)`
  display: inline-flex;
  width: 1px;
  height: 20px;
  background: rgba(89, 115, 166, 0.15);
  margin: 7px 2px;
`;
var Separator5 = styled28.div`
  display: inline-flex;
  width: 1px;
  height: 20px;
  background: rgba(89, 115, 166, 0.15);
  margin: 7px 2px;
`;
var Toolbar = styled28.div`
  display: flex;
  flex: auto;
  width: 100%;
  align-items: center;
  justify-content: center;
  height: 100%;
  container: editor/inline-size;
`;

// src/css-baseline/css-baseline.tsx
import { createGlobalStyle } from "styled-components";

// src/css-baseline/normalise.ts
import { css as css4 } from "styled-components";
var normalise = css4`
  html {
    line-height: 1.15;
    -webkit-text-size-adjust: 100%;
  }
  body {
    margin: 0;
  }
  h1 {
    font-size: 2em;
    margin: 0.67em 0;
  }
  hr {
    box-sizing: content-box;
    height: 0;
    overflow: visible;
    margin: 0;
  }
  pre {
    font-family: monospace, monospace;
    font-size: 1em;
  }
  a {
    background-color: transparent;
  }
  abbr[title] {
    border-bottom: none;
    text-decoration: underline;
    text-decoration: underline dotted;
  }
  b,
  strong {
    font-weight: bolder;
  }
  code,
  kbd,
  samp {
    font-family: monospace, monospace;
    font-size: 1em;
  }
  small {
    font-size: 80%;
  }
  sub,
  sup {
    font-size: 75%;
    line-height: 0;
    position: relative;
    vertical-align: baseline;
  }
  sub {
    bottom: -0.25em;
  }
  sup {
    top: -0.5em;
  }
  img {
    border-style: none;
  }
  button,
  input,
  optgroup,
  select,
  textarea {
    font-family: inherit;
    font-size: 100%;
    line-height: 1.15;
    margin: 0;
  }
  button,
  input {
    overflow: visible;
  }
  button,
  select {
    text-transform: none;
  }
  button,
  [type="button"],
  [type="reset"],
  [type="submit"] {
    -webkit-appearance: button;
  }
  button::-moz-focus-inner,
  [type="button"]::-moz-focus-inner,
  [type="reset"]::-moz-focus-inner,
  [type="submit"]::-moz-focus-inner {
    border-style: none;
    padding: 0;
  }
  button:-moz-focusring,
  [type="button"]:-moz-focusring,
  [type="reset"]:-moz-focusring,
  [type="submit"]:-moz-focusring {
    outline: 1px dotted ButtonText;
  }
  fieldset {
    padding: 0.35em 0.75em 0.625em;
  }
  legend {
    box-sizing: border-box;
    color: inherit;
    display: table;
    max-width: 100%;
    padding: 0;
    white-space: normal;
  }
  progress {
    vertical-align: baseline;
  }
  textarea {
    overflow: auto;
  }
  [type="checkbox"],
  [type="radio"] {
    box-sizing: border-box;
    padding: 0;
  }
  [type="number"]::-webkit-inner-spin-button,
  [type="number"]::-webkit-outer-spin-button {
    height: auto;
  }
  [type="search"] {
    -webkit-appearance: textfield;
    outline-offset: -2px;
  }
  [type="search"]::-webkit-search-decoration {
    -webkit-appearance: none;
  }
  ::-webkit-file-upload-button {
    -webkit-appearance: button;
    font: inherit;
  }
  details {
    display: block;
  }
  summary {
    display: list-item;
  }
  template {
    display: none;
  }
  [hidden] {
    display: none;
  }
`;
var normalise_default = normalise;

// src/css-baseline/css-baseline.tsx
var fontFamily = `acumin-pro, -apple-system, BlinkMacSystemFont,
Helvetica, Arial, "Noto Sans", "Noto Sans CJK", "Noto Sans JP", "Noto Sans KR",
"Noto Sans HK", "Noto Sans SC", "Noto Sans TC", "Twemoji Mozilla",
"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji",
"EmojiOne Color", "Android Emoji", sans-serif`;
var CssBaseline = createGlobalStyle`
 ${normalise_default};

 html {
    font-family: ${fontFamily};
    color: ${(props) => props.theme.text.main};
    fill: ${(props) => props.theme.text.main};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    scroll-behavior: smooth;
  }

  body {
    padding: 0;
    margin: 0;
  }

  a {
    color: ${(p) => p.theme.link};
  }

  // Style Mantine input placeholder to match Cadmus theme
  .mantine-InputPlaceholder-placeholder {
    color: ${(p) => p.theme.colors.navy400};
  }

  input[type=number]::-webkit-outer-spin-button,
  input[type=number]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
      -moz-appearance:textfield;
  }
`;

// src/desk/desk.tsx
import { styled as styled29 } from "styled-components";
import { jsx as jsx37, jsxs as jsxs30 } from "react/jsx-runtime";
var Layout = styled29.div`
  position: relative;
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
var Shelf = styled29.div`
  position: relative;
  width: 100%;
  min-height: 40px;
  flex: none;
  border-bottom: 1px solid ${(p) => p.theme.divider};
  box-sizing: border-box;
  z-index: 2;
`;
var Table = styled29.div`
  width: 100%;
  // height for firefox which should not cause issue on safari
  min-height: 0;
  height: 100%;
  flex: auto;

  overflow-y: scroll;
  display: block;
  scroll-behavior: smooth;

  position: relative;
  z-index: 1;
`;
var SplitTable = styled29.div`
  width: 100%;
  // height for firefox which should not cause issue on safari
  min-height: 0;
  height: 100%;
  flex: auto;

  overflow-y: clip;
  display: flex;
  scroll-behavior: smooth;

  position: relative;
  z-index: 1;
`;
var panelWidth = (width) => {
  switch (width) {
    case "closed":
      return 2;
    case "golden":
      return 38;
    case "half":
      return 50;
    case "inverseGolden":
      return 62;
    case "full":
      return 100;
    default:
      return 38;
  }
};
var Left = (props) => {
  const { width, background: background3, track: track2, children, ...divProps } = props;
  return /* @__PURE__ */ jsxs30(LeftContainer, { width, background: background3, ...divProps, children: [
    /* @__PURE__ */ jsx37(PanelContent, { tabIndex: 0, children }),
    track2 && /* @__PURE__ */ jsx37(Track, { children: track2 })
  ] });
};
var Right = (props) => {
  const { background: background3, track: track2, children, ...divProps } = props;
  return /* @__PURE__ */ jsxs30(RightContainer, { background: background3, ...divProps, children: [
    /* @__PURE__ */ jsx37(PanelContent, { tabIndex: 0, children }),
    track2 && /* @__PURE__ */ jsx37(Track, { children: track2 })
  ] });
};
var LeftContainer = styled29.div`
  width: ${(p) => `${panelWidth(p.width)}%`};
  height: 100%;
  flex: none;

  border-right: ${(p) => p.width === "full" ? void 0 : `1px solid ${p.theme.divider}`};
  box-sizing: border-box;
  position: relative;
  transition: width 0.6s ease;
  overflow: hidden;

  background: ${(p) => p.background ? colors[p.background] : "initial"};
`;
var RightContainer = styled29.div`
  height: 100%;
  flex: auto;

  overflow: hidden;
  scroll-behavior: smooth;
  position: relative;

  background: ${(p) => p.background ? colors[p.background] : "initial"};
`;
var PanelContent = styled29.div`
  height: 100%;
  overflow-y: auto;
  padding: 0;
  box-sizing: border-box;

  &:focus {
    outline-color: ${(p) => p.theme.colors.primary};
  }
`;
var Track = styled29.div`
  /* Force zero width, use translation to position the track. This preseves
  pointer events over the scrollbar and track area */
  width: 0px;
  transform: translateX(-54px);
  position: absolute;
  right: 0;
  top: 0;

  height: 100%;
  padding: 18px 4px;
  box-sizing: border-box;

  display: grid;
  align-content: end; /* Grid tracks align to end */
  grid-template-columns: 36px;
  grid-auto-rows: auto;
  grid-row-gap: 9px;

  z-index: 2;
`;
var Drawer = styled29.div`
  width: 100%;
  height: 63px;
  flex: none;

  border-top: 1px solid ${(p) => p.theme.divider};
  box-sizing: border-box;

  position: relative;
  z-index: 3;
`;
var Modal = styled29.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;

  background: ${(p) => p.theme.background.default};
  z-index: 4;
`;
var Desk = {
  Layout,
  Shelf,
  Table,
  SplitTable,
  Left,
  Right,
  Track,
  Drawer,
  Modal
};

// src/date-input/date-picker.tsx
import { useCallback } from "react";
import {
  DatePicker as MantineDatePicker
} from "@mantine/dates";
import dayjs from "dayjs";

// src/date-input/date-input.css.ts
import { createRuntimeFn as _7a4685 } from "@vanilla-extract/recipes/createRuntimeFn";
var calendarHeaderLevel = "jqj2xr9";
var calendarWeekday = "jqj2xra";
var container3 = "jqj2xr1";
var dates = "jqj2xr8";
var day = "jqj2xrf";
var dayCell = "jqj2xrg";
var error = "jqj2xrb";
var errorIcon = "jqj2xrc";
var highlightDay = "jqj2xrd";
var highlightDayContainer = "jqj2xre";
var input = _7a4685({ defaultClassName: "jqj2xr3", variantClassNames: { size: { sm: "jqj2xr4", md: "jqj2xr5" }, error: { true: "jqj2xr6", false: "jqj2xr7" } }, defaultVariants: { size: "md" }, compoundVariants: [] });
var leftBehind = "jqj2xrk";
var placeholder = "jqj2xr2";
var rangeHighlight = "jqj2xrh";
var rangeHighlightEnd = "jqj2xrj";
var rangeHighlightStart = "jqj2xri";
var rightBehind = "jqj2xrl";
var root3 = "jqj2xr0";

// src/date-input/date-picker.tsx
import { Fragment as Fragment14, jsx as jsx38, jsxs as jsxs31 } from "react/jsx-runtime";
function DatePicker(props) {
  const {
    className,
    type = "default",
    value,
    onChange,
    highlightDays = {},
    maxLevel = "month",
    numberOfColumns = 2,
    weekendDays = [],
    withCellSpacing = false,
    yearLabelFormat = "yyyy",
    monthLabelFormat = "MMMM YYYY",
    dateRangeHighlight,
    ...rest
  } = props;
  const renderDateRangeHighlight = (day2, dateRangeHighlight2) => {
    const dayStr = dayjs(day2).format("YYYY-MM-DD");
    const fromStr = dayjs(dateRangeHighlight2.fromDate).format("YYYY-MM-DD");
    const toStr = dayjs(dateRangeHighlight2.toDate).format("YYYY-MM-DD");
    const isDateRangeStart = dayStr === fromStr;
    const isDateRangeEnd = dayStr === toStr;
    const isMultiDay = fromStr !== toStr;
    const isWithinDateRange = dayStr > fromStr && dayStr < toStr;
    return /* @__PURE__ */ jsxs31(Fragment14, { children: [
      isDateRangeStart && /* @__PURE__ */ jsxs31(Fragment14, { children: [
        /* @__PURE__ */ jsx38("div", { className: rangeHighlightStart }),
        isMultiDay && /* @__PURE__ */ jsx38("div", { className: leftBehind })
      ] }),
      isDateRangeEnd && /* @__PURE__ */ jsxs31(Fragment14, { children: [
        /* @__PURE__ */ jsx38("div", { className: rangeHighlightEnd }),
        isMultiDay && /* @__PURE__ */ jsx38("div", { className: rightBehind })
      ] }),
      !isDateRangeEnd && isWithinDateRange && /* @__PURE__ */ jsx38("div", { className: rangeHighlight })
    ] });
  };
  const renderDayRangeContents = useCallback(
    (dayString) => {
      const day2 = dayjs(dayString).toDate();
      const highlights = (highlightDays[dayjs(day2).format("YYYY-MM-DD")] || []).map((c, i) => /* @__PURE__ */ jsx38(
        "div",
        {
          className: highlightDay,
          style: { background: vars.colors[c] }
        },
        i
      ));
      return /* @__PURE__ */ jsxs31(Fragment14, { children: [
        highlights.length > 0 && /* @__PURE__ */ jsx38("div", { className: highlightDayContainer, children: highlights }),
        /* @__PURE__ */ jsx38("div", { className: day, children: day2.getDate() }),
        dateRangeHighlight && renderDateRangeHighlight(day2, dateRangeHighlight)
      ] });
    },
    [highlightDays, dateRangeHighlight]
  );
  const renderDayDefaultContents = useCallback(
    (dayString) => {
      const day2 = dayjs(dayString).toDate();
      const highlights = highlightDays[dayjs(day2).format("YYYY-MM-DD")];
      if (!highlights) return day2.getDate();
      const Indicators = highlights.map((c, i) => /* @__PURE__ */ jsx38(
        "div",
        {
          className: highlightDay,
          style: { background: vars.colors[c] }
        },
        i
      ));
      return /* @__PURE__ */ jsxs31(Fragment14, { children: [
        /* @__PURE__ */ jsx38("div", { className: highlightDayContainer, children: Indicators }),
        /* @__PURE__ */ jsx38("div", { className: day, children: day2.getDate() })
      ] });
    },
    [highlightDays]
  );
  return /* @__PURE__ */ jsx38("div", { className, children: /* @__PURE__ */ jsx38(
    MantineDatePicker,
    {
      ...rest,
      type,
      className,
      classNames: {
        day: dayCell,
        calendarHeaderLevel,
        weekday: calendarWeekday
      },
      renderDay: (day2) => type === "default" ? renderDayDefaultContents(day2) : renderDayRangeContents(day2),
      value,
      onChange,
      maxLevel,
      numberOfColumns,
      weekendDays,
      withCellSpacing,
      yearLabelFormat,
      monthLabelFormat
    }
  ) });
}

// src/date-input/date-time-picker.tsx
import { forwardRef as forwardRef21 } from "react";
import { ExclamationTriangleIcon as ExclamationTriangleIcon2 } from "@vericus/cadmus-icons";
import {
  DateTimePicker as MantineDatePicker2
} from "@mantine/dates";
import clsx7 from "clsx";
import { jsx as jsx39, jsxs as jsxs32 } from "react/jsx-runtime";
var DateTimePicker = forwardRef21(
  (props, ref) => {
    const {
      size = "md",
      width = 300,
      className,
      placeholder: placeholder2,
      disabled: disabled2,
      value,
      onChange,
      leftSection: leftSection2,
      fullWidth,
      errorMessage,
      clearable,
      rootClassName,
      dayClassName,
      calendarHeaderClassName,
      inputClassName,
      ...rest
    } = props;
    return /* @__PURE__ */ jsxs32(
      "div",
      {
        className: container3,
        style: { width: fullWidth ? "100%" : width },
        children: [
          /* @__PURE__ */ jsx39(
            MantineDatePicker2,
            {
              ...rest,
              ref,
              className,
              classNames: {
                root: clsx7(root3, rootClassName),
                day: clsx7(dates, dayClassName),
                calendarHeader: clsx7(dates, calendarHeaderClassName),
                input: clsx7(
                  input({ size, error: !!errorMessage }),
                  inputClassName
                )
              },
              value,
              clearable,
              size,
              placeholder: placeholder2,
              leftSection: leftSection2,
              onChange,
              disabled: disabled2
            }
          ),
          errorMessage && /* @__PURE__ */ jsxs32("div", { className: error, children: [
            /* @__PURE__ */ jsx39("div", { className: errorIcon, children: /* @__PURE__ */ jsx39(ExclamationTriangleIcon2, { label: "", size: 16 }) }),
            /* @__PURE__ */ jsx39(
              Text,
              {
                kind: "systemMd",
                as: "div",
                color: "failure",
                "aria-live": "assertive",
                children: errorMessage
              }
            )
          ] })
        ]
      }
    );
  }
);

// src/date-input/date-picker-input.tsx
import { ExclamationTriangleIcon as ExclamationTriangleIcon3 } from "@vericus/cadmus-icons";
import {
  DatePickerInput as MantineDatePickerInput
} from "@mantine/dates";
import { clsx as clsx8 } from "clsx";
import { jsx as jsx40, jsxs as jsxs33 } from "react/jsx-runtime";
function DatePickerInput(props) {
  const {
    size = "md",
    errorMessage,
    style,
    className,
    rootClassName,
    dayClassName,
    calendarHeaderClassName,
    inputClassName,
    placeholderClassName,
    ...rest
  } = props;
  return /* @__PURE__ */ jsxs33("div", { className: container3, style, children: [
    /* @__PURE__ */ jsx40(
      MantineDatePickerInput,
      {
        className,
        classNames: {
          root: clsx8(root3, rootClassName),
          day: clsx8(dates, dayClassName),
          calendarHeader: clsx8(dates, calendarHeaderClassName),
          input: clsx8(
            input({ size, error: !!errorMessage }),
            inputClassName
          ),
          placeholder: clsx8(placeholder, placeholderClassName)
        },
        size,
        ...rest
      }
    ),
    errorMessage && /* @__PURE__ */ jsxs33("div", { className: error, children: [
      /* @__PURE__ */ jsx40("div", { className: errorIcon, children: /* @__PURE__ */ jsx40(ExclamationTriangleIcon3, { label: "", size: 16 }) }),
      /* @__PURE__ */ jsx40(Text, { kind: "systemMd", as: "div", color: "failure", "aria-live": "assertive", children: errorMessage })
    ] })
  ] });
}

// src/date-input/time-picker-input.tsx
import { ClockIcon, ExclamationTriangleIcon as ExclamationTriangleIcon4 } from "@vericus/cadmus-icons";
import { TimePicker } from "@mantine/dates";
import clsx9 from "clsx";
import { jsx as jsx41, jsxs as jsxs34 } from "react/jsx-runtime";
var TimePickerInput = ({
  className,
  withError,
  errorMessage,
  rightSection,
  fullWidth,
  width,
  inputClassName,
  ...timePickerProps
}) => {
  const rightContent = rightSection || /* @__PURE__ */ jsx41(ClockIcon, { color: vars.colors.navy400, label: "Select time", size: 24 });
  return /* @__PURE__ */ jsxs34(
    "div",
    {
      className: container3,
      style: { width: fullWidth ? "100%" : width },
      children: [
        /* @__PURE__ */ jsx41(
          TimePicker,
          {
            className,
            classNames: {
              input: clsx9(
                input({ size: "md", error: withError || !!errorMessage }),
                inputClassName
              )
            },
            rightSection: rightContent,
            ...timePickerProps
          }
        ),
        errorMessage && /* @__PURE__ */ jsxs34("div", { className: error, children: [
          /* @__PURE__ */ jsx41("div", { className: errorIcon, children: /* @__PURE__ */ jsx41(ExclamationTriangleIcon4, { label: "", size: 16 }) }),
          /* @__PURE__ */ jsx41(Text, { kind: "systemMd", as: "div", color: "failure", "aria-live": "assertive", children: errorMessage })
        ] })
      ]
    }
  );
};

// src/date-input/legend.css.ts
var container4 = "_1ud6bmh0";
var dateLabel = "_1ud6bmh3";
var flag = "_1ud6bmh2";
var label3 = "_1ud6bmh1";
var singleDateContainer = "_1ud6bmh4";

// src/date-input/utils.ts
import dayjs2 from "dayjs";
var formatDateAndHour = (date) => {
  const day2 = date.getDate();
  const ordinal = day2 % 10 === 1 && day2 !== 11 ? "st" : day2 % 10 === 2 && day2 !== 12 ? "nd" : day2 % 10 === 3 && day2 !== 13 ? "rd" : "th";
  const month = date.toLocaleString("en-US", { month: "long" });
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day2}${ordinal} ${month}, ${hours}:${minutes}${ampm}`;
};
var dateColors = {
  dueDate: "indigo500",
  returnDate: "cyan500",
  draftDueDate: "pink500",
  extension: "redA500",
  scheduledDate: "blue500",
  today: "black200"
};

// src/date-input/legend.tsx
import { jsx as jsx42, jsxs as jsxs35 } from "react/jsx-runtime";
function Legend(props) {
  const dateProps = sortedDateProps(props);
  return /* @__PURE__ */ jsx42("div", { className: container4, children: dateProps.map((dp, i) => /* @__PURE__ */ jsx42(SingleDate, { ...dp }, i)) });
}
var SingleDate = (props) => {
  const { date, color: color2, label: label4 } = props;
  if (date === void 0) return null;
  return /* @__PURE__ */ jsxs35("div", { className: singleDateContainer, children: [
    /* @__PURE__ */ jsxs35(Text, { kind: "bodyLine", className: label3, children: [
      /* @__PURE__ */ jsx42(
        "span",
        {
          style: { background: vars.colors[color2] },
          className: flag
        }
      ),
      label4
    ] }),
    /* @__PURE__ */ jsx42(
      Text,
      {
        kind: "bodyLine",
        italic: true,
        textAlign: "right",
        color: "shade1",
        className: dateLabel,
        children: date ? formatDateAndHour(date) : "Unset"
      }
    )
  ] });
};
var sortedDateProps = (props) => {
  const { dueDate, returnDate, draftDueDate, extension, scheduledDate } = props;
  const datesWithExtraData = [
    {
      date: /* @__PURE__ */ new Date(),
      color: dateColors.today,
      label: "Today"
    },
    {
      date: dueDate,
      color: dateColors.dueDate,
      label: "Final due date"
    },
    {
      date: returnDate,
      color: dateColors.returnDate,
      label: "Feedback return date"
    }
  ];
  if (draftDueDate !== void 0)
    datesWithExtraData.push({
      date: draftDueDate,
      color: dateColors.draftDueDate,
      label: "Draft due date"
    });
  if (extension !== void 0)
    datesWithExtraData.push({
      date: extension,
      color: dateColors.extension,
      label: "Extension date"
    });
  if (scheduledDate !== void 0)
    datesWithExtraData.push({
      date: scheduledDate,
      color: dateColors.scheduledDate,
      label: "Scheduled date"
    });
  return datesWithExtraData.sort((a, b) => {
    if (!b.date) return -1;
    if (!a.date) return 1;
    return a.date.getTime() - b.date.getTime();
  });
};

// src/divider/divider.tsx
import { styled as styled30 } from "styled-components";
var Divider = styled30.hr`
  border: 0;
  height: 1px;
  min-height: 1px;
  width: 100%;
  margin-top: 0;
  margin-bottom: 0;
  background-color: ${(p) => p.theme.colors.navy200};
`;

// src/emoji/emoji.tsx
import { jsx as jsx43 } from "react/jsx-runtime";
var Emoji = (props) => /* @__PURE__ */ jsx43(
  "span",
  {
    className: "emoji",
    role: "img",
    "aria-label": props.label ? props.label : "",
    "aria-hidden": props.label ? "false" : "true",
    children: props.symbol
  }
);

// src/error-message/error-message.tsx
import { styled as styled31 } from "styled-components";
import { ExclamationTriangleIcon as ExclamationTriangleIcon5 } from "@vericus/cadmus-icons";

// src/error-message/error-message.css.ts
var icon2 = "jz6xgv0";

// src/error-message/error-message.tsx
import { jsx as jsx44, jsxs as jsxs36 } from "react/jsx-runtime";
var ErrorMessage = ({
  fullWidth,
  children,
  position = "absolute"
}) => {
  return /* @__PURE__ */ jsxs36(ErrorContainer, { fullWidth, position, "data-error": "true", children: [
    /* @__PURE__ */ jsx44("div", { className: icon2, children: /* @__PURE__ */ jsx44(ExclamationTriangleIcon5, { label: "", size: 16 }) }),
    /* @__PURE__ */ jsx44(Text, { kind: "systemMd", as: "div", color: "failure", "aria-live": "assertive", children })
  ] });
};
var ErrorContainer = styled31.div`
  position: ${(p) => p.position};
  top: 100%;
  align-items: flex-start;
  background-color: ${(p) => p.theme.colors.poppy200};
  box-sizing: border-box;
  font-weight: inherit;
  color: ${(p) => p.theme.fail};
  fill: ${(p) => p.theme.fail};
  display: flex;
  gap: 4px;
  min-width: 180px;
  padding: 5px 10px;
  width: ${(p) => p.fullWidth ? "100%" : "180px"};
`;

// src/filter-pill/index.ts
import * as RadixToggleGroup9 from "@radix-ui/react-toggle-group";

// src/filter-pill/filter-pill-item.tsx
import * as RadixToggleGroup8 from "@radix-ui/react-toggle-group";
import { clsx as clsx10 } from "clsx";

// src/filter-pill/filter-pill.css.ts
var button2 = "jxhkmt1";

// src/filter-pill/filter-pill-item.tsx
import { jsx as jsx45 } from "react/jsx-runtime";
function FilterPillItem(props) {
  const { className, children, ...itemProps } = props;
  return /* @__PURE__ */ jsx45(
    RadixToggleGroup8.Item,
    {
      className: clsx10(button2, className),
      ...itemProps,
      children
    }
  );
}

// src/filter-pill/index.ts
var FilterPill = {
  Root: RadixToggleGroup9.Root,
  Item: FilterPillItem
};

// src/grid/grid.tsx
import { styled as styled32 } from "styled-components";
var Grid = styled32.div`
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  grid-column-gap: 27px;
  width: 100%;
  max-width: 1440px;
  padding-left: 48px;
  padding-right: 48px;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
`;

// src/icon-button/icon-button.tsx
import { forwardRef as forwardRef22 } from "react";
import { css as css5, styled as styled33 } from "styled-components";
import { jsx as jsx46 } from "react/jsx-runtime";
var IconButton = forwardRef22(
  (props, ref) => {
    const tooltipContent = props["aria-label"];
    const tooltipPosition = props.tooltipPosition ?? "top";
    if (tooltipContent) {
      return /* @__PURE__ */ jsx46(
        Tooltip,
        {
          content: tooltipContent,
          width: "auto",
          size: "sm",
          side: tooltipPosition,
          children: /* @__PURE__ */ jsx46(InnerIconButton, { ref, ...props })
        }
      );
    }
    return /* @__PURE__ */ jsx46(InnerIconButton, { ref, ...props });
  }
);
var InnerIconButton = styled33.button`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: ${(p) => p.shape === "square" ? "2px" : "50%"};
  padding: 0;

  margin-left: ${(p) => p.marginLeft ? `${p.marginLeft}px` : 0};
  margin-right: ${(p) => p.marginRight ? `${p.marginRight}px` : 0};

  /* prevent button from growing or shrinking when flexed by a parent */
  flex: none;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  position: relative;

  /* A mixin to inject button fill styles */
  ${buttonFill}

  border: none;
  background: transparent;

  cursor: pointer;
  outline: ${(p) => p.outline ? `1px solid ${p.theme.colors.navy200}` : 0};
  transition:
    background 0.2s ${easeOutCirc},
    fill 0.2s ${easeOutCirc};

  &:not(:disabled) {
    /* The :before pseudo-element creates a focus and ping outline*/
    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      border-radius: ${(p) => p.shape === "square" ? "2px" : "50%"};
      width: 100%;
      height: 100%;
    }

    &:focus:before {
      ${FocusAnimation};
    }

    &:hover,
    &:focus {
      background: ${(p) => p.theme.background.action1};
    }

    /* Darken the background on-cLick */
    &:active,
    &[aria-expanded="true"] {
      background: ${(p) => p.theme.background.action2};
      transition: background 0.1s ${easeOutCirc};
    }
  }

  &:disabled {
    opacity: 0.54;
    cursor: not-allowed;
  }
`;
function buttonFill(props) {
  let fill = props.theme.text.shade1;
  if (props.solid) {
    fill = props.theme.text.heading;
  } else if (props.selected) {
    fill = props.theme.text.accent;
  }
  return css5`
    fill: ${fill};
  `;
}

// src/input/action-input.tsx
import { forwardRef as forwardRef24, useState } from "react";
import { styled as styled34 } from "styled-components";

// src/input/input.tsx
import { forwardRef as forwardRef23, useRef } from "react";
import {
  ExclamationTriangleIcon as ExclamationTriangleIcon6,
  XMarkIcon
} from "@vericus/cadmus-icons";
import { clsx as clsx11 } from "clsx";

// src/utils/set-html-input-value.ts
var set_html_input_value_default = (input6, value) => {
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value"
  )?.set;
  nativeInputValueSetter?.call(input6, value);
  input6.dispatchEvent(new Event("input", { bubbles: true }));
};

// src/input/input.css.ts
import { createRuntimeFn as _7a4686 } from "@vanilla-extract/recipes/createRuntimeFn";
var error2 = "a6vixk6";
var errorIcon2 = "a6vixk7";
var field = _7a4686({ defaultClassName: "a6vixk1", variantClassNames: { size: { sm: "a6vixk2", md: "a6vixk3" } }, defaultVariants: { size: "md" }, compoundVariants: [] });
var input2 = "a6vixk4";
var root4 = "a6vixk0";
var section = "a6vixk5";

// src/input/input.tsx
import { jsx as jsx47, jsxs as jsxs37 } from "react/jsx-runtime";
var Input = forwardRef23((props, ref) => {
  const innerRef = useRef(null);
  const inputElement = ref || innerRef;
  const {
    errorMessage,
    error: error4,
    className,
    style,
    fullWidth,
    leftSection: leftSection2,
    rightSection,
    iconName,
    clearable,
    width = 300,
    size = "md",
    getInputRef,
    ...inputProps
  } = props;
  let left = leftSection2;
  if (iconName) {
    left = /* @__PURE__ */ jsx47(Icon, { iconName });
  }
  let right = rightSection;
  if (clearable) {
    right = /* @__PURE__ */ jsx47(
      XMarkIcon,
      {
        size: 24,
        label: "Clear input",
        onClick: () => {
          if (typeof inputElement === "function") {
          } else {
            if (inputElement != null && inputElement.current) {
              set_html_input_value_default(inputElement.current, "");
            }
          }
        }
      }
    );
  }
  return /* @__PURE__ */ jsxs37("div", { className: root4, style: { width: fullWidth ? "100%" : width }, children: [
    /* @__PURE__ */ jsxs37(
      "div",
      {
        className: clsx11(field({ size }), className),
        "data-disabled": inputProps.disabled === true,
        "data-error": errorMessage !== void 0 || error4 === true,
        style,
        children: [
          left && /* @__PURE__ */ jsx47("div", { className: section, children: left }),
          /* @__PURE__ */ jsx47(
            "input",
            {
              ref: getInputRef ?? inputElement,
              className: input2,
              ...inputProps
            }
          ),
          right && /* @__PURE__ */ jsx47("div", { className: section, children: right })
        ]
      }
    ),
    errorMessage && /* @__PURE__ */ jsxs37("div", { className: error2, children: [
      /* @__PURE__ */ jsx47("div", { className: errorIcon2, children: /* @__PURE__ */ jsx47(ExclamationTriangleIcon6, { label: "", size: 16 }) }),
      /* @__PURE__ */ jsx47(Text, { kind: "systemMd", as: "div", color: "failure", "aria-live": "assertive", children: errorMessage })
    ] })
  ] });
});

// src/input/action-input.tsx
import { jsx as jsx48 } from "react/jsx-runtime";
var ActionInput = forwardRef24(
  ({
    value,
    onChange,
    onKeyDown,
    iconName,
    placeholder: placeholder2,
    // width,
    onAction,
    actionName,
    // className,
    autofocus,
    error: error4,
    disabled: disabled2,
    maxLength
  }, ref) => {
    const [editing, setEditing] = useState(!!autofocus);
    return /* @__PURE__ */ jsx48(
      Input,
      {
        ref,
        value,
        "data-testid": "actionable-input",
        onChange: (event) => {
          const targetValue = event.target.value;
          onChange(targetValue);
        },
        onKeyDown,
        autoFocus: autofocus,
        iconName,
        placeholder: placeholder2,
        fullWidth: true,
        maxLength,
        onFocus: () => {
          setEditing(true);
        },
        errorMessage: error4,
        rightSection: editing && actionName ? /* @__PURE__ */ jsx48(
          ActionButton,
          {
            kind: "secondary",
            size: "sm",
            "aria-label": "action-button",
            onClick: () => {
              onAction(value);
            },
            disabled: disabled2,
            children: actionName
          }
        ) : void 0
      }
    );
  }
);
var ActionButton = styled34(Button)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  margin-right: 4px;
`;

// src/input/formatted-display-input.tsx
import { forwardRef as forwardRef25, useState as useState2 } from "react";
import { styled as styled35 } from "styled-components";
import { jsx as jsx49, jsxs as jsxs38 } from "react/jsx-runtime";
var FormattedDisplayInput = forwardRef25((props, ref) => {
  const { value, onBlur, type, errorMessage, displayFunction, ...rest } = props;
  const [focused, setFocused] = useState2(false);
  return /* @__PURE__ */ jsxs38(RootContainer, { children: [
    !focused && /* @__PURE__ */ jsx49(Container, { children: /* @__PURE__ */ jsx49(
      StyledInput,
      {
        ref,
        onClick: () => {
          setFocused(!focused);
        },
        type: "text",
        readOnly: true,
        value: displayFunction?.(value ?? null) ?? void 0,
        onFocus: () => {
          setFocused(!focused);
        },
        ...rest
      }
    ) }),
    focused && /* @__PURE__ */ jsx49(Container, { children: /* @__PURE__ */ jsx49(
      StyledInput,
      {
        fullWidth: true,
        ref,
        type: type || "text",
        autoFocus: true,
        value,
        onBlur: (event) => {
          setFocused(!focused);
          onBlur?.(event);
        },
        onChange: (event) => {
          props.onChange?.(event);
        },
        ...rest
      }
    ) }),
    errorMessage && /* @__PURE__ */ jsx49(Alert, { title: errorMessage, size: "sm" })
  ] });
});
var RootContainer = styled35.div`
  position: relative;
`;
var Container = styled35.div`
  border: 1px solid ${colors.grey400};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  min-height: 1em;

  & > p {
    min-height: 1.5em;
    min-width: 1px;
  }
`;
var StyledInput = styled35(Input)`
  border: none;
  outline: none;
  font-size: 14px;
  & input {
    border: none;
    outline: none;
    padding: 0;
    min-height: unset;

    &:focus {
      border: none;
      outline: none;
      box-shadow: none;
    }
  }
`;

// src/link-button/link-button.tsx
import { forwardRef as forwardRef26 } from "react";
import { Icon as Icon4 } from "@vericus/cadmus-icons";
import clsx12 from "clsx";

// src/link-button/link-button.css.ts
import { createRuntimeFn as _7a4687 } from "@vanilla-extract/recipes/createRuntimeFn";
var button3 = _7a4687({ defaultClassName: "_1b2wmd33", variantClassNames: { kind: { solid: "_1b2wmd34", primary: "_1b2wmd35", error: "_1b2wmd36" }, inline: { true: "_1b2wmd37", false: "_1b2wmd38" } }, defaultVariants: { kind: "solid", inline: false }, compoundVariants: [] });
var color = "var(--_1b2wmd30)";
var icon3 = "_1b2wmd3c";
var marginLeft2 = "var(--_1b2wmd31)";
var marginRight2 = "var(--_1b2wmd32)";
var text = _7a4687({ defaultClassName: "_1b2wmd39", variantClassNames: { underlined: { true: "_1b2wmd3a", false: "_1b2wmd3b" } }, defaultVariants: {}, compoundVariants: [] });

// src/link-button/link-button.tsx
import { jsx as jsx50, jsxs as jsxs39 } from "react/jsx-runtime";
var LinkButton = forwardRef26(
  (props, ref) => {
    const {
      kind = "solid",
      inline = false,
      iconName,
      underlined = true,
      icon: icon5,
      rightIcon: rightIcon2,
      color: color2,
      children,
      className,
      marginLeft: marginLeft3 = "0",
      marginRight: marginRight3 = "0",
      ...buttonProps
    } = props;
    let left = null;
    let right = null;
    if (icon5) {
      left = icon5;
    } else if (iconName) {
      left = /* @__PURE__ */ jsx50("span", { className: icon3, children: /* @__PURE__ */ jsx50(Icon4, { iconName, style: { fill: "currentColor" } }) });
    }
    if (rightIcon2) {
      right = rightIcon2;
    }
    return /* @__PURE__ */ jsxs39(
      "button",
      {
        ref,
        className: clsx12(button3({ inline, kind }), className),
        style: assignInlineVars({
          [color]: color2 ? colors[color2] : null,
          [marginLeft2]: `${marginLeft3}px`,
          [marginRight2]: `${marginRight3}px`
        }),
        ...buttonProps,
        children: [
          left,
          /* @__PURE__ */ jsx50("span", { className: text({ underlined }), children }),
          right
        ]
      }
    );
  }
);

// src/link-button-v2/link-button-v2.tsx
import { forwardRef as forwardRef27 } from "react";
import { styled as styled36 } from "styled-components";
import { jsx as jsx51 } from "react/jsx-runtime";
var LinkButtonV2 = forwardRef27(
  (props, ref) => {
    const { children, ...buttonProps } = props;
    return /* @__PURE__ */ jsx51(Button8, { ref, ...buttonProps, children });
  }
);
var Button8 = styled36.button`
  border: 1px solid transparent;
  background-color: transparent;
  cursor: pointer;
  padding: 0;

  color: ${colors.blue500};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.54;
  }
`;

// src/modal/index.ts
import {
  Close as Close2,
  Description,
  Root as Root16,
  Title,
  Trigger as Trigger5
} from "@radix-ui/react-dialog";

// src/modal/content.tsx
import { forwardRef as forwardRef28 } from "react";
import { CloseIcon as CloseIcon2 } from "@vericus/cadmus-icons";
import * as RadixDialog from "@radix-ui/react-dialog";
import { clsx as clsx13 } from "clsx";

// src/modal/modal.css.ts
var closeActionContainer = "k6ixb67";
var content2 = "k6ixb66";
var contentWidth = "var(--k6ixb60)";
var overlayElement = "k6ixb65";
var slideOut = "k6ixb62";

// src/modal/content.tsx
import { jsx as jsx52, jsxs as jsxs40 } from "react/jsx-runtime";
var animationDurationMs = 300;
function Content8(props) {
  const {
    className,
    withOverlay = true,
    withCloseButton = true,
    onCloseButtonClick,
    withAnimation = true,
    children,
    overlayColor = "rgba(89, 115, 166, 0.45)",
    closeButtonColor = "black100",
    width,
    onExitComplete,
    portalChildren,
    style,
    ...contentProps
  } = props;
  return /* @__PURE__ */ jsxs40(RadixDialog.Portal, { children: [
    /* @__PURE__ */ jsx52(
      Overlay2,
      {
        withOverlay,
        withAnimation,
        overlayColor
      }
    ),
    portalChildren,
    /* @__PURE__ */ jsxs40(
      RadixDialog.Content,
      {
        className: clsx13(content2, className),
        style: {
          ...assignInlineVars({
            [contentWidth]: width ? typeof width === "number" ? `${width}px` : width : void 0
          }),
          ...style
        },
        "data-animation": withAnimation ? "true" : "false",
        onAnimationStart: (e) => {
          if (onExitComplete && withAnimation && e.animationName === slideOut) {
            setTimeout(() => onExitComplete(), animationDurationMs);
          }
        },
        ...contentProps,
        children: [
          children,
          withCloseButton && /* @__PURE__ */ jsx52(RadixDialog.Close, { asChild: true, children: /* @__PURE__ */ jsx52("div", { className: closeActionContainer, children: /* @__PURE__ */ jsx52(
            IconButton,
            {
              tooltipPosition: "left",
              "aria-label": "Close modal",
              style: { fill: closeButtonColor },
              children: /* @__PURE__ */ jsx52(
                Button,
                {
                  icon: /* @__PURE__ */ jsx52(CloseIcon2, { label: "copy", size: 24 }),
                  onClick: onCloseButtonClick
                }
              )
            }
          ) }) })
        ]
      }
    )
  ] });
}
var Overlay2 = forwardRef28((props, ref) => {
  const { withOverlay, withAnimation, overlayColor } = props;
  if (withOverlay === false) {
    return null;
  }
  return /* @__PURE__ */ jsx52(
    RadixDialog.Overlay,
    {
      asChild: true,
      ref,
      "data-animation": withAnimation ? "true" : "false",
      children: /* @__PURE__ */ jsx52(
        "div",
        {
          className: overlayElement,
          style: {
            background: vars.colors[overlayColor] || overlayColor
          }
        }
      )
    }
  );
});

// src/modal/index.ts
var Modal2 = {
  Root: Root16,
  Trigger: Trigger5,
  Content: Content8,
  Close: Close2,
  Title,
  Description
};

// src/pill/pill.tsx
import { forwardRef as forwardRef29 } from "react";
import clsx14 from "clsx";

// src/pill/pill.css.ts
var leftSection = "_15skffe6";
var pillActionIcon = "_15skffe7";
var pillBgVar = "var(--_15skffe0)";
var pillBorderVar = "var(--_15skffe2)";
var pillIcon = "_15skffe8";
var pillRoot = "_15skffe4";
var pillSeparator = "_15skffe5";
var pillTextVar = "var(--_15skffe1)";

// src/pill/pill.tsx
import { Fragment as Fragment15, jsx as jsx53, jsxs as jsxs41 } from "react/jsx-runtime";
var Pill = forwardRef29((props, ref) => {
  const {
    as,
    className,
    color: color2,
    lowerCase,
    leftSection: leftSection2,
    textColor: textColor2,
    borderColor,
    onDelete,
    children,
    basic = false,
    style,
    ...otherProps
  } = props;
  const Wrapper = as ?? "div";
  const isColored = Boolean(color2) && color2 !== colors.white200;
  const resolvedText = textColor2 ?? (isColored ? colors.black100 : colors.grey500);
  const rightContent = onDelete ? /* @__PURE__ */ jsxs41(Fragment15, { children: [
    /* @__PURE__ */ jsx53("span", { className: pillSeparator }),
    /* @__PURE__ */ jsx53(
      "span",
      {
        className: pillActionIcon,
        onClick: (e) => {
          e.stopPropagation();
          onDelete?.();
        },
        children: /* @__PURE__ */ jsx53(Icon, { className: pillIcon, iconName: "Trash" })
      }
    )
  ] }) : null;
  let leftContent = null;
  if (leftSection2) {
    leftContent = /* @__PURE__ */ jsx53("div", { className: leftSection, children: leftSection2 });
  }
  return /* @__PURE__ */ jsxs41(
    Wrapper,
    {
      ref,
      className: clsx14(pillRoot, className),
      "data-basic": basic ? "true" : "false",
      "data-colored": isColored ? "true" : "false",
      "data-lowercase": lowerCase ? "true" : "false",
      role: basic ? void 0 : "button",
      tabIndex: basic ? void 0 : 0,
      style: {
        ...assignInlineVars({
          [pillBgVar]: color2 ?? colors.white100,
          [pillTextVar]: resolvedText,
          [pillBorderVar]: borderColor ?? colors.grey100
        }),
        ...style
      },
      ...otherProps,
      children: [
        leftContent,
        children,
        rightContent
      ]
    }
  );
});

// src/range-slider/range-slider.tsx
import { forwardRef as forwardRef30 } from "react";
import {
  RangeSlider as MantineRangeSlider
} from "@mantine/core";
import clsx15 from "clsx";

// src/range-slider/range-slider.css.ts
var bar = "_16sq2u51";
var root5 = "_16sq2u50";
var thumb = "_16sq2u53";
var track = "_16sq2u52";

// src/range-slider/range-slider.tsx
import { jsx as jsx54 } from "react/jsx-runtime";
var RangeSlider = forwardRef30(
  (props, ref) => {
    const {
      thumbSize = 24,
      min = 0,
      max = 100,
      step = 1,
      minRange = 1,
      thumbFromLabel = "Range from thumber",
      thumbToLabel = "Range from label",
      thumbClassName,
      trackClassName,
      barClassName,
      rootClassName,
      ...otherProps
    } = props;
    return /* @__PURE__ */ jsx54(
      MantineRangeSlider,
      {
        ...otherProps,
        ref,
        thumbSize,
        minRange,
        min,
        max,
        step,
        classNames: {
          root: clsx15(root5, rootClassName),
          bar: clsx15(bar, barClassName),
          track: clsx15(track, trackClassName),
          thumb: clsx15(thumb, thumbClassName)
        },
        thumbFromLabel,
        thumbToLabel
      }
    );
  }
);

// src/responsive/styled-utils.ts
import { styled as styled37, css as css6 } from "styled-components";

// src/responsive/breakpoints.ts
var maxWidths = {
  sm: 40,
  md: 64,
  lg: 80,
  xl: 90
};
var sizeMaxWidths = {
  sm: `max-width: ${maxWidths.sm}rem`,
  md: `max-width: ${maxWidths.md}rem`,
  lg: `max-width: ${maxWidths.lg}rem`,
  xl: `max-width: ${maxWidths.xl}rem`
};

// src/responsive/styled-utils.ts
var mobile = (...args) => css6`
    @media screen and (${sizeMaxWidths.sm}) {
      ${css6(...args)}
    }
  `;
var tablet = (...args) => (
  // docs for nested media query syntax and their support:
  //    https://developer.mozilla.org/en-US/docs/Web/CSS/@media#syntax
  css6`
    @media screen and (${sizeMaxWidths.md}) {
      @media not (${sizeMaxWidths.sm}) {
        ${css6(...args)}
      }
    }
  `
);
var desktop = (...args) => css6`
    @media not screen and (${sizeMaxWidths.md}) {
      ${css6(...args)}
    }
  `;
var largeDesktop = (...args) => css6`
    @media not screen and (${sizeMaxWidths.lg}) {
      ${css6(...args)}
    }
  `;
var MobileOnly = styled37.div`
  display: none;
  @media screen and (${sizeMaxWidths.sm}) {
    display: initial;
  }
`;
var TabletOnly = styled37.div`
  display: none;
  @media screen and (${sizeMaxWidths.md}) {
    display: initial;
  }
  @media screen and (${sizeMaxWidths.sm}) {
    display: none;
  }
`;
var DesktopOnly = styled37.div`
  display: initial;
  @media screen and (${sizeMaxWidths.md}) {
    display: none;
  }
`;
var DesktopAndTablet = styled37.div`
  display: initial;
  @media screen and (${sizeMaxWidths.sm}) {
    && {
      display: none;
    }
  }
`;
var TabletAndMobile = styled37.div`
  display: none;
  @media screen and (${sizeMaxWidths.md}) {
    display: initial;
  }
`;

// src/resource-cards/file-card.tsx
import { forwardRef as forwardRef31 } from "react";
import { styled as styled40 } from "styled-components";

// src/resource-cards/actions.tsx
import { styled as styled38 } from "styled-components";
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  CopyIcon,
  PencilWithEraserIcon,
  TickIcon,
  TrashIcon,
  XMarkIcon as XMarkIcon2
} from "@vericus/cadmus-icons";
import { jsx as jsx55, jsxs as jsxs42 } from "react/jsx-runtime";
var actionButtonSizeInPx = 28;
var EditableActions = ({
  onCopyLink,
  onOpenInBrowser,
  onEdit
}) => {
  return /* @__PURE__ */ jsxs42(Container2, { children: [
    onCopyLink && /* @__PURE__ */ jsx55(
      ControlButton,
      {
        "aria-label": "Copy resource link",
        icon: /* @__PURE__ */ jsx55(CopyIcon, { size: 24, label: "" }),
        noPadding: true,
        size: actionButtonSizeInPx,
        onClick: stopBubbling(onCopyLink)
      }
    ),
    onOpenInBrowser && /* @__PURE__ */ jsx55(
      ControlButton,
      {
        "aria-label": "Open in browser",
        noPadding: true,
        size: actionButtonSizeInPx,
        icon: /* @__PURE__ */ jsx55(ArrowTopRightOnSquareIcon, { size: 24, label: "" }),
        onClick: stopBubbling(onOpenInBrowser)
      }
    ),
    onEdit && /* @__PURE__ */ jsx55(
      ControlButton,
      {
        "aria-label": "Edit",
        noPadding: true,
        size: actionButtonSizeInPx,
        icon: /* @__PURE__ */ jsx55(PencilWithEraserIcon, { size: 24, label: "" }),
        onClick: stopBubbling(onEdit)
      }
    )
  ] });
};
var EditingActions = ({
  onDone,
  onDelete,
  onCancel
}) => {
  return /* @__PURE__ */ jsxs42(Container2, { children: [
    onDone && /* @__PURE__ */ jsx55(
      ControlButton,
      {
        "aria-label": "Done",
        icon: /* @__PURE__ */ jsx55(TickIcon, { size: 24, label: "" }),
        noPadding: true,
        size: actionButtonSizeInPx,
        onClick: stopBubbling(onDone)
      }
    ),
    onDelete && /* @__PURE__ */ jsx55(
      ControlButton,
      {
        "aria-label": "Delete resource",
        icon: /* @__PURE__ */ jsx55(TrashIcon, { size: 24, label: "" }),
        noPadding: true,
        size: actionButtonSizeInPx,
        onClick: stopBubbling(onDelete)
      }
    ),
    onCancel && /* @__PURE__ */ jsx55(
      ControlButton,
      {
        "aria-label": "Cancel",
        icon: /* @__PURE__ */ jsx55(XMarkIcon2, { size: 24, label: "" }),
        noPadding: true,
        size: actionButtonSizeInPx,
        onClick: stopBubbling(onCancel)
      }
    )
  ] });
};
var ViewOnlyActions = ({
  onCopyLink,
  onOpenInBrowser,
  onDownload
}) => {
  return /* @__PURE__ */ jsxs42(Container2, { children: [
    onCopyLink && /* @__PURE__ */ jsx55(
      ControlButton,
      {
        "aria-label": "Copy resource link",
        icon: /* @__PURE__ */ jsx55(CopyIcon, { size: 24, label: "" }),
        noPadding: true,
        size: actionButtonSizeInPx,
        onClick: stopBubbling(onCopyLink)
      }
    ),
    onOpenInBrowser && /* @__PURE__ */ jsx55(
      ControlButton,
      {
        "aria-label": "Open in browser",
        icon: /* @__PURE__ */ jsx55(ArrowTopRightOnSquareIcon, { label: "", size: 24 }),
        noPadding: true,
        size: actionButtonSizeInPx,
        onClick: stopBubbling(onOpenInBrowser)
      }
    ),
    onDownload && /* @__PURE__ */ jsx55(
      ControlButton,
      {
        "aria-label": "Download",
        icon: /* @__PURE__ */ jsx55(ArrowDownTrayIcon, { label: "", size: 24 }),
        noPadding: true,
        size: actionButtonSizeInPx,
        onClick: stopBubbling(onDownload)
      }
    )
  ] });
};
var stopBubbling = (callback) => (e) => {
  e.stopPropagation();
  callback();
};
var Container2 = styled38.div`
  display: flex;
  gap: 2px;
  background-color: ${colors.white100};
  border-radius: 4px;
  padding: 4px 6px;
  box-shadow:
    0px 0px 1px rgba(71, 92, 133, 0.36),
    0px 1px 3px rgba(71, 92, 133, 0.15);
`;

// src/resource-cards/file-classifier.ts
function formatFileInfo({ size, extension }) {
  if (!(extension || size)) {
    return "";
  }
  if (extension && size) {
    return `${extension} \xB7 ${size}`;
  }
  return `${extension ?? ""}${size ?? ""}`;
}
function getFileType(filename) {
  if (!filename.includes(".")) {
    return "unknown";
  }
  const splitName = filename.split(".");
  const extension = splitName[splitName.length - 1].toLowerCase();
  if (!extension) {
    return "unknown";
  }
  const fileType = extensionToFileTypeMap[extension];
  if (!fileType) {
    return "unknown";
  }
  return fileType;
}
var fileTypeToSupportedExtensionsMap = {
  pdf: ["pdf"],
  document: ["doc", "docx", "pages"],
  spreadsheet: ["xls", "xlsx", "numbers", "csv"],
  presentation: ["ppt", "pptx", "pps", "keynote"],
  image: ["jpg", "jpeg", "gif", "raw", "png", "svg"],
  media: [
    "mp4",
    "mpg",
    "mpeg",
    "mov",
    "m4v",
    "asf",
    "avi",
    "ra",
    "ram",
    "rm",
    "swf"
  ],
  audio: ["mp3", "wav", "m4a"],
  text: ["rtf", "txt", "wpd", "xml"],
  webpage: ["html", "htm", "mht", "mhtml"],
  archive: ["zip", "rar", "7zip"],
  unknown: []
};
var extensionToFileTypeMap = flipArrayRecord(
  fileTypeToSupportedExtensionsMap
);
function flipArrayRecord(record) {
  const flippedEntries = Object.entries(record).reduce(
    (newObject, [key, array]) => {
      array.forEach((entry) => {
        newObject[entry] = key;
      });
      return newObject;
    },
    {}
  );
  return flippedEntries;
}

// src/resource-cards/icon-with-background.tsx
import { styled as styled39 } from "styled-components";
import { Icon as Icon5 } from "@vericus/cadmus-icons";
import { jsx as jsx56 } from "react/jsx-runtime";
var IconWithBackground = ({ fileType }) => {
  const backgroundColor = colors[fileTypeToBackgroundColorMap[fileType]];
  const borderColor = colors[fileTypeToBorderColorMap[fileType]];
  const iconName = fileTypeToIconMap[fileType];
  return /* @__PURE__ */ jsx56(Container3, { backgroundColor, borderColor, children: /* @__PURE__ */ jsx56(Icon5, { iconName }) });
};
var Container3 = styled39.div`
  width: 36px;
  height: 36px;
  border: 1px solid ${(p) => p.borderColor};
  border-radius: 4px;
  background-color: ${(p) => p.backgroundColor};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;
var fileTypeToBackgroundColorMap = {
  archive: "white100",
  audio: "indigo200",
  document: "blue200",
  image: "white100",
  media: "indigo200",
  pdf: "orange200",
  presentation: "orange200",
  spreadsheet: "green200",
  text: "white100",
  unknown: "grey100",
  webpage: "white100"
};
var fileTypeToBorderColorMap = {
  archive: "grey100",
  audio: "grey100",
  document: "grey100",
  image: "grey200",
  media: "grey100",
  pdf: "grey100",
  presentation: "grey100",
  spreadsheet: "grey100",
  text: "grey100",
  unknown: "grey100",
  webpage: "grey100"
};
var fileTypeToIconMap = {
  archive: "ArchiveFile",
  audio: "AudioFile",
  document: "DocumentFile",
  image: "ImageFile",
  media: "MediaFile",
  pdf: "PdfFile",
  presentation: "PresentationFile",
  spreadsheet: "SpreadsheetFile",
  text: "TextFile",
  unknown: "UnknownFile",
  webpage: "WebpageFile"
};

// src/resource-cards/shared.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
var resourceTypeToCardBackgroundColorMap = {
  archive: "white200",
  audio: "indigo100",
  document: "blue100",
  image: "indigo100",
  link: "white200",
  media: "indigo100",
  pdf: "orange100",
  presentation: "orange100",
  spreadsheet: "green100",
  text: "blue100",
  unknown: "white200",
  webpage: "blue100"
};
var ResourceNameInput = ({
  onConfirm,
  onCancel,
  ...props
}) => {
  const handleKeyPress = (e) => {
    switch (e.key) {
      case "Escape":
        onCancel?.();
        break;
      case "Enter":
        onConfirm?.();
        break;
    }
  };
  return /* @__PURE__ */ jsx57(Input, { size: "sm", ...props, onKeyDown: handleKeyPress });
};
var formatBytes = (bytes) => {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// src/resource-cards/file-card.tsx
import { jsx as jsx58, jsxs as jsxs43 } from "react/jsx-runtime";
var FileCard = forwardRef31(
  ({
    fileInfo,
    nameBeingEdited,
    isEditingName,
    onUpdateNameBeingEdited,
    onCancelEditing,
    onDelete,
    onDownload,
    onDoneEditing,
    onOpenInBrowser,
    onStartEditing,
    viewOnly,
    isUploading,
    focus,
    onFocusChange
  }, ref) => {
    const { name, type } = fileInfo;
    const backgroundColor = colors[resourceTypeToCardBackgroundColorMap[type]];
    const onCardClick = viewOnly ? onOpenInBrowser : void 0;
    const shouldShowEditingActions = isEditingName && !isUploading && onUpdateNameBeingEdited && onDelete && onCancelEditing;
    const shouldShowViewOnlyActions = viewOnly && !isUploading && (onDownload || onOpenInBrowser);
    const shouldShowEditableActions = !viewOnly && !isUploading && onOpenInBrowser && onStartEditing;
    return /* @__PURE__ */ jsxs43(
      Container4,
      {
        ref,
        backgroundColor,
        isUploading,
        onClick: isUploading ? void 0 : onCardClick,
        children: [
          /* @__PURE__ */ jsx58(IconWithBackground, { fileType: type }),
          /* @__PURE__ */ jsxs43(InfoContainer, { children: [
            isEditingName ? /* @__PURE__ */ jsx58(
              ResourceNameInput,
              {
                autoFocus: true,
                fullWidth: true,
                value: nameBeingEdited,
                onCancel: onCancelEditing,
                onConfirm: onDoneEditing,
                onChange: (e) => onUpdateNameBeingEdited?.(e.target.value)
              }
            ) : /* @__PURE__ */ jsx58(TrimmedFileTitle, { kind: "headingSix", as: "span", children: name }),
            /* @__PURE__ */ jsx58(FileDetails, { fileInfo, isUploading })
          ] }),
          /* @__PURE__ */ jsxs43(ActionsContainer, { absolute: !isEditingName, children: [
            shouldShowEditingActions && /* @__PURE__ */ jsx58(
              EditingActions,
              {
                onDone: onDoneEditing,
                onDelete,
                onCancel: onCancelEditing
              }
            ),
            !isEditingName && /* @__PURE__ */ jsxs43(
              OnCardHover,
              {
                onKeyDown: (e) => {
                  if (e.key === "Tab" && !e.shiftKey && !e.target.nextElementSibling) {
                    onFocusChange?.(false);
                  }
                },
                focus,
                children: [
                  shouldShowViewOnlyActions && /* @__PURE__ */ jsx58(
                    ViewOnlyActions,
                    {
                      onDownload,
                      onOpenInBrowser: () => onOpenInBrowser && onOpenInBrowser()
                    }
                  ),
                  shouldShowEditableActions && /* @__PURE__ */ jsx58(
                    EditableActions,
                    {
                      onOpenInBrowser,
                      onEdit: onStartEditing
                    }
                  )
                ]
              }
            )
          ] })
        ]
      }
    );
  }
);
var FileDetails = ({ isUploading, fileInfo }) => {
  const details = isUploading ? "Uploading\u2026" : formatFileInfo(fileInfo);
  return /* @__PURE__ */ jsx58(Text, { kind: "systemSm", textTransform: "uppercase", children: details });
};
var Container4 = styled40.div`
  align-items: flex-start;
  background-color: ${(p) => p.backgroundColor};
  box-sizing: border-box;
  cursor: ${(p) => !p.isUploading && "pointer"};
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: flex-start;
  min-width: 170px;
  opacity: ${(p) => p.isUploading && 0.7};
  padding: 16px;
  position: relative;
  width: 100%;
`;
var TrimmedFileTitle = styled40(Text)`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  display: -webkit-box;
  overflow: hidden;
  margin-top: -0.5em;
  padding-bottom: 0;
`;
var InfoContainer = styled40.div`
  flex-grow: 1;
  flex-basis: 100%;
  overflow: hidden;
`;
var ActionsContainer = styled40.div`
  align-self: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  position: ${(p) => p.absolute && "absolute"};
  right: ${(p) => p.absolute && "14px"};
  top: ${(p) => p.absolute && "12px"};
`;
var OnCardHover = styled40.div`
  display: ${(p) => p.focus ? "block" : "none"};
  ${Container4}:hover & {
    display: block;
  }
`;

// src/resource-cards/file-card-v2.tsx
import { forwardRef as forwardRef32 } from "react";
import {
  ArrowTopRightOnSquareIcon as ArrowTopRightOnSquareIcon2,
  CloseIcon as CloseIcon3,
  DownloadIcon,
  Icon as Icon6,
  LoaderIcon,
  TrashIcon as TrashIcon2,
  WarnIcon,
  WarnTriangleIcon
} from "@vericus/cadmus-icons";

// src/resource-cards/file-card-v2.css.ts
import { createRuntimeFn as _7a4688 } from "@vanilla-extract/recipes/createRuntimeFn";
var actions = "m6n238h";
var actionsWithDivider = "m6n238b";
var card2 = _7a4688({ defaultClassName: "m6n2381", variantClassNames: { state: { "default": "m6n2382", uploading: "m6n2383", error: "m6n2384" } }, defaultVariants: { state: "default" }, compoundVariants: [] });
var content3 = "m6n2386";
var divider = "m6n238a";
var errorMessageContainer = "m6n238f";
var fileNameRow = "m6n2387";
var iconWrapper = "m6n2385";
var inlineActions = "m6n238c";
var nameText = "m6n2389";
var progressBar = "m6n238j";
var progressFill = "m6n238k";
var spinningLoader = "m6n238g";
var statusRow = "m6n2388";

// src/resource-cards/file-card-v2.tsx
import { Fragment as Fragment16, jsx as jsx59, jsxs as jsxs44 } from "react/jsx-runtime";
var FileCardV2 = forwardRef32(
  (props, ref) => {
    const {
      fileInfo,
      isUploading = false,
      uploadProgress = 0,
      bytesUploaded,
      totalBytes,
      hasError = false,
      errorMessage = "Upload failed",
      onDelete,
      onDownload,
      onOpenInBrowser,
      onRetry,
      onCancelUpload
    } = props;
    const { name, type, size } = fileInfo;
    const cardState = hasError ? "error" : isUploading ? "uploading" : "default";
    const clampedProgress = Math.min(Math.max(uploadProgress, 0), 100);
    const progressText = bytesUploaded !== void 0 && totalBytes !== void 0 ? `${formatBytes(bytesUploaded)} of ${formatBytes(totalBytes)}` : void 0;
    return /* @__PURE__ */ jsxs44(Fragment16, { children: [
      /* @__PURE__ */ jsxs44("div", { ref, className: card2({ state: cardState }), children: [
        /* @__PURE__ */ jsx59("div", { className: iconWrapper, children: /* @__PURE__ */ jsx59(
          Icon6,
          {
            iconName: fileTypeToIconMap[type],
            style: { width: 24, height: 24 }
          }
        ) }),
        /* @__PURE__ */ jsxs44("div", { className: content3, children: [
          /* @__PURE__ */ jsxs44("div", { className: fileNameRow, children: [
            /* @__PURE__ */ jsx59(Tooltip, { content: name, size: "sm", width: "auto", children: /* @__PURE__ */ jsx59(Text, { kind: "headingSix", className: nameText, as: "span", children: name }) }),
            cardState === "default" && /* @__PURE__ */ jsxs44(Fragment16, { children: [
              /* @__PURE__ */ jsx59(Text, { kind: "systemSm", children: size }),
              (onDownload || onOpenInBrowser || onDelete) && /* @__PURE__ */ jsxs44("div", { className: actionsWithDivider, children: [
                /* @__PURE__ */ jsx59("div", { className: divider }),
                /* @__PURE__ */ jsxs44("div", { className: inlineActions, children: [
                  onDownload && /* @__PURE__ */ jsx59(
                    ControlButton,
                    {
                      onClick: onDownload,
                      "aria-label": "Download file",
                      icon: /* @__PURE__ */ jsx59(DownloadIcon, { size: 24, label: "download file" })
                    }
                  ),
                  onOpenInBrowser && /* @__PURE__ */ jsx59(
                    ControlButton,
                    {
                      onClick: onOpenInBrowser,
                      "aria-label": "Open in browser",
                      icon: /* @__PURE__ */ jsx59(
                        ArrowTopRightOnSquareIcon2,
                        {
                          size: 24,
                          label: "open in browser"
                        }
                      )
                    }
                  ),
                  onDelete && /* @__PURE__ */ jsx59(
                    ControlButton,
                    {
                      onClick: onDelete,
                      "aria-label": "Delete file",
                      icon: /* @__PURE__ */ jsx59(TrashIcon2, { size: 24, label: "remove file" })
                    }
                  )
                ] })
              ] })
            ] })
          ] }),
          isUploading && /* @__PURE__ */ jsxs44("div", { className: statusRow, children: [
            progressText && /* @__PURE__ */ jsxs44(Fragment16, { children: [
              /* @__PURE__ */ jsx59(Text, { kind: "systemSm", as: "span", children: progressText }),
              /* @__PURE__ */ jsx59(Text, { kind: "systemSm", as: "span", children: "\xB7" })
            ] }),
            /* @__PURE__ */ jsx59(
              LoaderIcon,
              {
                size: 16,
                label: "",
                className: spinningLoader
              }
            ),
            /* @__PURE__ */ jsx59(Text, { kind: "systemSm", as: "span", children: "Uploading..." })
          ] }),
          hasError && /* @__PURE__ */ jsxs44(Fragment16, { children: [
            /* @__PURE__ */ jsxs44("div", { className: statusRow, children: [
              progressText && /* @__PURE__ */ jsxs44(Fragment16, { children: [
                /* @__PURE__ */ jsx59(Text, { kind: "systemSm", as: "span", children: progressText }),
                /* @__PURE__ */ jsx59(Text, { kind: "systemSm", as: "span", children: "\xB7" })
              ] }),
              /* @__PURE__ */ jsx59(WarnIcon, { size: 16, label: "", color: vars.colors.red500 }),
              /* @__PURE__ */ jsx59(Text, { kind: "systemSm", as: "span", children: "Failed" })
            ] }),
            onRetry && /* @__PURE__ */ jsx59(LinkButton, { kind: "error", onClick: onRetry, children: /* @__PURE__ */ jsx59(
              Text,
              {
                kind: "systemSm",
                as: "span",
                style: { color: vars.colors.red500 },
                children: "Try Again"
              }
            ) })
          ] }),
          isUploading && /* @__PURE__ */ jsx59("div", { className: progressBar, children: /* @__PURE__ */ jsx59(
            "div",
            {
              className: progressFill,
              style: { width: `${clampedProgress}%` }
            }
          ) })
        ] }),
        isUploading && /* @__PURE__ */ jsx59("div", { className: actions, children: onCancelUpload && /* @__PURE__ */ jsx59(
          ControlButton,
          {
            onClick: onCancelUpload,
            "aria-label": "Cancel upload",
            icon: /* @__PURE__ */ jsx59(CloseIcon3, { size: 24, label: "cancel upload" })
          }
        ) }),
        hasError && onDelete && /* @__PURE__ */ jsx59("div", { className: actions, children: /* @__PURE__ */ jsx59(
          ControlButton,
          {
            onClick: onDelete,
            "aria-label": "Remove file",
            icon: /* @__PURE__ */ jsx59(TrashIcon2, { size: 24, label: "remove file" })
          }
        ) })
      ] }),
      hasError && errorMessage && /* @__PURE__ */ jsxs44("div", { className: errorMessageContainer, children: [
        /* @__PURE__ */ jsx59(WarnTriangleIcon, { size: 12, label: "error" }),
        /* @__PURE__ */ jsx59(Text, { kind: "systemSm", color: "red500", children: errorMessage })
      ] })
    ] });
  }
);

// src/resource-cards/link-card.tsx
import { forwardRef as forwardRef33 } from "react";
import { styled as styled41 } from "styled-components";
import { Icon as Icon7 } from "@vericus/cadmus-icons";
import { jsx as jsx60, jsxs as jsxs45 } from "react/jsx-runtime";
var LinkCard = forwardRef33(
  ({
    linkTitle,
    nameBeingEdited,
    onUpdateNameBeingEdited,
    onDoneEditing,
    onCancelEditing,
    onCopyLink,
    onDelete,
    onOpenInBrowser,
    onStartEditing,
    isEditingName,
    viewOnly,
    focus,
    onFocusChange
  }, ref) => {
    const backgroundColor = colors[resourceTypeToCardBackgroundColorMap["link"]];
    const onCardClick = viewOnly ? onOpenInBrowser : void 0;
    const shouldShowEditingActions = isEditingName && onUpdateNameBeingEdited && onDelete && onCancelEditing;
    const shouldShowViewOnlyActions = viewOnly && (onCopyLink || onOpenInBrowser);
    const shouldShowEditableActions = !viewOnly && onOpenInBrowser && onStartEditing;
    return /* @__PURE__ */ jsxs45(
      Container5,
      {
        ref,
        backgroundColor,
        clickable: !!viewOnly,
        onClick: onCardClick,
        children: [
          /* @__PURE__ */ jsx60(UnshrinkableIcon, { iconName: "Link" }),
          /* @__PURE__ */ jsx60(LinkNameContainer, { children: isEditingName ? /* @__PURE__ */ jsx60(
            ResourceNameInput,
            {
              fullWidth: true,
              autoFocus: true,
              value: nameBeingEdited,
              onCancel: onCancelEditing,
              onConfirm: onDoneEditing,
              onChange: (e) => onUpdateNameBeingEdited?.(e.target.value)
            }
          ) : /* @__PURE__ */ jsx60(TrimmedLinkName, { kind: "headingSix", as: "span", children: linkTitle }) }),
          /* @__PURE__ */ jsxs45(ActionsContainer2, { absolute: !isEditingName, children: [
            shouldShowEditingActions && /* @__PURE__ */ jsx60(
              EditingActions,
              {
                onDone: onDoneEditing,
                onDelete,
                onCancel: onCancelEditing
              }
            ),
            !isEditingName && /* @__PURE__ */ jsxs45(
              OnCardHover2,
              {
                focus,
                onKeyDown: (e) => {
                  if (e.key === "Tab" && !e.shiftKey && !e.target.nextElementSibling) {
                    onFocusChange?.(false);
                  }
                },
                children: [
                  shouldShowViewOnlyActions && /* @__PURE__ */ jsx60(
                    ViewOnlyActions,
                    {
                      onCopyLink,
                      onOpenInBrowser
                    }
                  ),
                  shouldShowEditableActions && /* @__PURE__ */ jsx60(
                    EditableActions,
                    {
                      onCopyLink,
                      onOpenInBrowser,
                      onEdit: onStartEditing
                    }
                  )
                ]
              }
            )
          ] })
        ]
      }
    );
  }
);
var Container5 = styled41.div`
  align-items: center;
  background-color: ${(p) => p.backgroundColor};
  box-sizing: border-box;
  cursor: ${(p) => p.clickable && "pointer"};
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  gap: 12px;
  min-height: 53px;
  min-width: 170px;
  padding: 8px 16px;
  position: relative;
  width: 100%;
  border-radius: 2px;
`;
var TrimmedLinkName = styled41(Text)`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  color: ${(p) => p.theme.link};
  cursor: pointer;
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  text-decoration: underline;
`;
var UnshrinkableIcon = styled41(Icon7)`
  flex-shrink: 0;
`;
var LinkNameContainer = styled41.div`
  flex-grow: 1;
  flex-basis: 100%;
  color: black;
`;
var ActionsContainer2 = styled41.div`
  align-self: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  position: ${(p) => p.absolute && "absolute"};
  right: ${(p) => p.absolute && "8px"};
  top: ${(p) => p.absolute && "8px"};
`;
var OnCardHover2 = styled41.div`
  display: ${(p) => p.focus ? "block" : "none"};
  ${Container5}:hover & {
    display: block;
  }
`;

// src/search/search.tsx
import { styled as styled42 } from "styled-components";
import { jsx as jsx61 } from "react/jsx-runtime";
var Search = (props) => /* @__PURE__ */ jsx61(Container6, { withFullWidth: props.withFullWidth, children: /* @__PURE__ */ jsx61(Input, { type: "search", iconName: "Search", fullWidth: true, ...props }) });
var Container6 = styled42.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: ${(p) => p.withFullWidth ? "100%" : "unset"};
`;

// src/search-list/search-list.tsx
import { styled as styled43 } from "styled-components";
import { MagnifyingGlassIcon } from "@vericus/cadmus-icons";
import { jsx as jsx62, jsxs as jsxs46 } from "react/jsx-runtime";
var SearchList = ({
  queryValue,
  onQueryValueChange,
  items,
  placeholder: placeholder2,
  onSelect,
  className,
  selectedItems,
  renderInput,
  renderMenuItem
}) => {
  const filtered = items.filter(
    (item3) => item3.label.toLowerCase().includes(queryValue.toLowerCase())
  );
  return /* @__PURE__ */ jsxs46(SearchListRoot, { className, children: [
    renderInput ? renderInput({
      "aria-label": "search-list",
      placeholder: placeholder2,
      value: queryValue,
      onChange: (event) => onQueryValueChange(event.target.value)
    }) : /* @__PURE__ */ jsx62(
      Input,
      {
        "aria-label": "search-list",
        fullWidth: true,
        placeholder: placeholder2,
        value: queryValue,
        onChange: (event) => onQueryValueChange(event.target.value),
        leftSection: /* @__PURE__ */ jsx62(MagnifyingGlassIcon, { size: 24, label: "" }),
        clearable: queryValue.length > 0
      }
    ),
    /* @__PURE__ */ jsx62(SearchListMenu, { children: filtered.map(
      (item3) => renderMenuItem ? renderMenuItem({
        searchListItem: item3,
        selectedItems,
        onSelect
      }) : /* @__PURE__ */ jsx62(
        StyledDropdownMenuItem,
        {
          onClick: () => onSelect(item3),
          selected: selectedItems?.includes(item3.value),
          as: "button",
          children: /* @__PURE__ */ jsxs46(SearchMenuItemContent, { children: [
            /* @__PURE__ */ jsx62(StyledText, { kind: "bodySm", as: "span", inline: true, children: item3.label }),
            item3.description && /* @__PURE__ */ jsx62(StyledText, { kind: "systemSm", inline: true, children: item3.description })
          ] })
        },
        item3.value
      )
    ) }),
    filtered.length == 0 && /* @__PURE__ */ jsxs46(NoResultContainer, { children: [
      /* @__PURE__ */ jsx62(Text, { kind: "bodyLine", children: "No search result found" }),
      /* @__PURE__ */ jsx62(NoResultClearFilter, { onClick: () => onQueryValueChange(""), children: /* @__PURE__ */ jsx62(Text, { kind: "bodyLine", children: "Clear search" }) })
    ] })
  ] });
};
var StyledText = styled43(Text)`
  color: inherit;
  fill: inherit;
`;
var SearchListMenu = styled43.div`
  height: 100%;
  overflow-y: auto;
`;
var StyledDropdownMenuItem = styled43(DropdownMenu.MenuItemDiv)`
  flex-direction: column;
  align-items: flex-start;
  gap: initial;
  width: 100%;
`;
var NoResultClearFilter = styled43.a`
  color: ${(p) => p.theme.text.accent};

  &:hover {
    cursor: pointer;
  }
`;
var SearchMenuItemContent = styled43.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px 4px;
`;
var NoResultContainer = styled43.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 250px;
`;
var SearchListRoot = styled43.div`
  display: flex;
  flex-direction: column;
  min-height: 320px;
  background-color: ${(p) => p.theme.colors.white100};
`;

// src/section-label/section-label.tsx
import { styled as styled44 } from "styled-components";
import { jsx as jsx63, jsxs as jsxs47 } from "react/jsx-runtime";
var SectionLabel = ({
  title,
  children,
  ...divProps
}) => /* @__PURE__ */ jsxs47(Container7, { ...divProps, children: [
  /* @__PURE__ */ jsx63(Label6, { kind: "label", children: title || children }),
  /* @__PURE__ */ jsx63(Divider, {})
] });
var Container7 = styled44.div`
  display: flex;
  align-items: baseline;
`;
var Label6 = styled44(Text)`
  flex: none;
  padding-right: 9px;
  box-sizing: border-box;
  text-transform: uppercase;
`;

// src/select-button/select-button.tsx
import { forwardRef as forwardRef34 } from "react";
import { ChevronDownIcon as ChevronDownIcon3 } from "@vericus/cadmus-icons";
import clsx16 from "clsx";

// src/select-button/select-button.css.ts
import { createRuntimeFn as _7a4689 } from "@vanilla-extract/recipes/createRuntimeFn";
var button4 = _7a4689({ defaultClassName: "_5hciem1", variantClassNames: { kind: { "default": "_5hciem2", selected: "_5hciem3" } }, defaultVariants: { kind: "default" }, compoundVariants: [] });
var icon4 = "_5hciem6";
var notOutlined = "_5hciem4";
var text2 = "_5hciem5";

// src/select-button/select-button.tsx
import { jsx as jsx64, jsxs as jsxs48 } from "react/jsx-runtime";
var SelectButton = forwardRef34(
  (props, ref) => {
    const {
      icon: icon5,
      iconName,
      children,
      disabled: disabled2,
      selected,
      /* activeColor, */
      withDownCaret = true,
      outlined = true,
      className,
      ...buttonProps
    } = props;
    let left = null;
    if (icon5) {
      left = icon5;
    } else if (iconName) {
      left = /* @__PURE__ */ jsx64("span", { className: icon4, children: /* @__PURE__ */ jsx64(Icon, { iconName, style: { fill: "currentColor" } }) });
    }
    return /* @__PURE__ */ jsxs48(
      "button",
      {
        className: clsx16(
          className,
          button4({ kind: selected ? "selected" : "default" }),
          !outlined && notOutlined
        ),
        ref,
        disabled: disabled2,
        ...buttonProps,
        children: [
          left,
          /* @__PURE__ */ jsx64("div", { className: text2, children: /* @__PURE__ */ jsx64(Text, { kind: "bodyLine", as: "span", children }) }),
          withDownCaret && /* @__PURE__ */ jsx64(ChevronDownIcon3, { label: "", size: 16, style: { flexShrink: 0 } })
        ]
      }
    );
  }
);

// src/skeleton/skeleton.tsx
import { styled as styled45, keyframes as keyframes2 } from "styled-components";
import { jsx as jsx65, jsxs as jsxs49 } from "react/jsx-runtime";
var moveGradient = keyframes2`
  0% {
  transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;
var Skeleton = styled45.div`
  height: ${(p) => p.height ? `${p.height}px` : "18px"};
  width: ${(p) => p.width ? `${p.width}%` : "100%"};
  margin-bottom: 9px;
  /* pill shape */
  border-radius: ${(p) => p.height ? `10px` : "10000px"};
  position: relative;

  background-color: #f2f4f8;
  box-sizing: border-box;
  overflow: hidden;

  &::after {
    box-sizing: border-box;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    will-change: transform;
    content: "";
    background-image: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0),
      #fafbfc,
      rgba(0, 0, 0, 0)
    );
    animation: ${moveGradient} 1.6s infinite;
  }
`;
var TitleSkeleton = styled45(Skeleton)`
  background-color: ${(p) => p.theme.background.action2};
`;
var SectionSkeleton = () => /* @__PURE__ */ jsxs49(SectionContainer, { children: [
  /* @__PURE__ */ jsx65(SkeletonLabel, {}),
  /* @__PURE__ */ jsx65(Line3, {})
] });
var SectionContainer = styled45.div`
  display: flex;
  align-items: baseline;
`;
var Line3 = styled45.div`
  flex: auto;
  height: 1px;
  background: ${(p) => p.theme.background.action3};
`;
var CardSkeleton = styled45(Skeleton)`
  border-radius: 8px;
  height: ${(p) => p.heightPx || 150}px;
`;
var RequirementsSkeleton = (props) => /* @__PURE__ */ jsxs49("div", { ...props, children: [
  SingleRequirementSkeleton(45),
  SingleRequirementSkeleton(54),
  SingleRequirementSkeleton(45),
  SingleRequirementSkeleton(36)
] });
var SingleRequirementSkeleton = (contentPercentage) => /* @__PURE__ */ jsxs49(ReqContainer, { children: [
  /* @__PURE__ */ jsx65(SkeletonLabel, {}),
  /* @__PURE__ */ jsx65(Skeleton, { width: contentPercentage })
] });
var ReqContainer = styled45.div`
  display: flex;
`;
var SkeletonLabel = styled45(Skeleton)`
  margin-right: 18px;
  width: 100px;
  flex: none;
  background-color: ${(p) => p.theme.background.action2};
`;
var ParagraphSkeleton = (props) => /* @__PURE__ */ jsxs49("div", { ...props, children: [
  /* @__PURE__ */ jsx65(Skeleton, { width: 85 }),
  /* @__PURE__ */ jsx65(Skeleton, { width: 83 }),
  /* @__PURE__ */ jsx65(Skeleton, { width: 88 }),
  /* @__PURE__ */ jsx65(Skeleton, { width: 95 }),
  /* @__PURE__ */ jsx65(Skeleton, { width: 93 }),
  /* @__PURE__ */ jsx65(Skeleton, { width: 90 }),
  /* @__PURE__ */ jsx65(Skeleton, { width: 20 })
] });

// src/spacer/spacer.tsx
import { styled as styled46 } from "styled-components";
var Spacer = styled46.div`
  margin-top: ${(p) => p.spacing}px;
  display: block;
`;

// src/tabs/index.ts
import { Content as Content9, Trigger as RadixTrigger2, Root as Root17 } from "@radix-ui/react-tabs";

// src/tabs/list.tsx
import { List as RadixList } from "@radix-ui/react-tabs";
import { clsx as clsx17 } from "clsx";

// src/tabs/tabs.css.ts
import { createRuntimeFn as _7a46810 } from "@vanilla-extract/recipes/createRuntimeFn";
var leftIcon = "r119z56";
var list = _7a46810({ defaultClassName: "r119z53", variantClassNames: { divider: { true: "r119z54", false: "r119z55" } }, defaultVariants: {}, compoundVariants: [] });
var rightIcon = "r119z57";
var tabButton = _7a46810({ defaultClassName: "r119z50", variantClassNames: { kind: { outlined: "r119z51", primary: "r119z52" } }, defaultVariants: {}, compoundVariants: [] });

// src/tabs/list.tsx
import { jsx as jsx66 } from "react/jsx-runtime";
var List = ({
  children,
  divider: divider2,
  gap = 24,
  className,
  ...rest
}) => /* @__PURE__ */ jsx66(
  RadixList,
  {
    className: clsx17(list({ divider: divider2 }), className),
    style: { gap },
    ...rest,
    children
  }
);

// src/tabs/trigger.tsx
import {
  Trigger as RadixTrigger
} from "@radix-ui/react-tabs";
import { jsx as jsx67, jsxs as jsxs50 } from "react/jsx-runtime";
var Trigger6 = ({
  leftIcon: leftIcon3,
  rightIcon: rightIcon2,
  children,
  kind = "outlined",
  ...radixTriggerProps
}) => /* @__PURE__ */ jsx67(RadixTrigger, { asChild: true, ...radixTriggerProps, children: /* @__PURE__ */ jsxs50("button", { className: tabButton({ kind }), children: [
  leftIcon3 && /* @__PURE__ */ jsx67("div", { className: leftIcon, children: leftIcon3 }),
  children,
  rightIcon2 && /* @__PURE__ */ jsx67("div", { className: rightIcon, children: rightIcon2 })
] }) });

// src/tabs/index.ts
var Tabs = { Root: Root17, Trigger: Trigger6, List, Content: Content9, RadixTrigger: RadixTrigger2 };

// src/utils/merge-refs.ts
function mergeRefs(refs) {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        ref.current = value;
      }
    });
  };
}

// src/video/embedded-video.tsx
import { useState as useState3 } from "react";
import { styled as styled47 } from "styled-components";
import { jsx as jsx68, jsxs as jsxs51 } from "react/jsx-runtime";
var EmbeddedVideo = (props) => {
  const [playState, setPlayState] = useState3("idle");
  let content5;
  switch (playState) {
    case "idle":
      if (props.thumbnail) {
        content5 = /* @__PURE__ */ jsxs51(
          VideoPlaceholderWithThumbnail,
          {
            style: {
              width: props.width,
              height: props.height
            },
            onClick: () => {
              setPlayState("play");
              if (props.onPlay) {
                props.onPlay();
              }
            },
            children: [
              props.thumbnail,
              /* @__PURE__ */ jsx68(
                PlayIcon,
                {
                  triangleColor: props.playButtonColor ? colors[props.playButtonColor] : "white"
                }
              )
            ]
          }
        );
        break;
      }
      content5 = /* @__PURE__ */ jsx68(
        VideoPlaceholder,
        {
          style: {
            width: props.width,
            height: props.height
          },
          onClick: () => {
            setPlayState("play");
            if (props.onPlay) {
              props.onPlay();
            }
          },
          children: /* @__PURE__ */ jsx68(
            PlayIcon,
            {
              triangleColor: props.playButtonColor ? colors[props.playButtonColor] : "white"
            }
          )
        }
      );
      break;
    case "play":
      content5 = props.video;
      break;
  }
  return /* @__PURE__ */ jsx68(
    Container8,
    {
      style: {
        width: props.width,
        height: props.height
      },
      children: content5
    }
  );
};
var PlayIcon = ({ triangleColor }) => /* @__PURE__ */ jsxs51(
  "svg",
  {
    width: "73",
    height: "72",
    viewBox: "0 0 73 72",
    fill: "none",
    style: { position: "absolute" },
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx68("circle", { cx: "36.5", cy: "36", r: "36", fill: "#071831", fillOpacity: "0.75" }),
      /* @__PURE__ */ jsx68(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M31.341 50.9744C29.2336 52.2915 26.5 50.7764 26.5 48.2913V23.7087C26.5 21.2236 29.2336 19.7085 31.341 21.0256L51.0071 33.3169C52.9899 34.5562 52.9899 37.4438 51.0071 38.6831L31.341 50.9744Z",
          fill: triangleColor
        }
      )
    ]
  }
);
var VideoPlaceholderWithThumbnail = styled47.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 15px;
  position: relative;
`;
var VideoPlaceholder = styled47.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: ${colors.navy200};
  border-radius: 15px;
`;
var Container8 = styled47.div``;

// src/text-area/auto-width-wrapper.tsx
import { Fragment as Fragment17, useState as useState4 } from "react";
import { styled as styled48 } from "styled-components";
import { jsx as jsx69, jsxs as jsxs52 } from "react/jsx-runtime";
var AutoWidthWrapper = ({
  renderTextArea,
  defaultValue = "",
  ...rest
}) => {
  const [content5, setContent] = useState4(defaultValue);
  return /* @__PURE__ */ jsxs52(AutoWidthContainer, { ...rest, children: [
    /* @__PURE__ */ jsx69(WidthHolder, { children: content5.split("\n").map(function(item3, index) {
      if (item3.length === 0) {
        return /* @__PURE__ */ jsxs52(Fragment17, { children: [
          /* @__PURE__ */ jsx69("div", { children: item3 }),
          /* @__PURE__ */ jsx69("br", {})
        ] }, `${index}-${item3}`);
      }
      return /* @__PURE__ */ jsx69("div", { children: item3 }, `${index}-item`);
    }) }),
    renderTextArea((text3) => {
      setContent(text3);
    })
  ] });
};
var AutoWidthContainer = styled48.div`
  position: relative;
  display: inline-block;
  border: none;
`;
var WidthHolder = styled48.div`
  word-break: break-word;
  visibility: hidden;
  white-space: pre-wrap;
`;

// src/text-area/input.tsx
import { forwardRef as forwardRef35 } from "react";
import { css as css7, styled as styled49 } from "styled-components";
import TextareaAutosize from "react-textarea-autosize";
import { jsx as jsx70 } from "react/jsx-runtime";
var Input2 = forwardRef35(
  (props, ref) => {
    const {
      hideFocusRing = false,
      withAutoWidthWrapper = false,
      ...rest
    } = props;
    return /* @__PURE__ */ jsx70(
      AutoHeightInput,
      {
        ref,
        $hideFocusRing: hideFocusRing,
        $withAutoWidthWrapper: withAutoWidthWrapper,
        ...rest
      }
    );
  }
);
var AutoHeightInput = styled49(TextareaAutosize)`
  width: 100%;
  resize: ${(p) => p.maxRows ? "both" : "none"};
  overflow: ${(p) => p.maxRows ? "auto" : "hidden"};
  word-break: break-word;
  border: ${(p) => p.$hideFocusRing ? "none" : `1px solid ${p.theme.colors.grey300}`};
  border-radius: 3px;
  padding: inherit;

  /* When textarea is used with AutoWidthWrapper, it needs to be absolute. */
  ${(p) => p.$withAutoWidthWrapper ? css7`
          position: absolute;
        ` : void 0}

  // Input font size and weight need to be the same as wrapper,
  // so wrapper can grow into the size that input needs
  font-size: inherit;
  font-weight: inherit;
  top: 0px;
  left: 0px;
  line-height: inherit;

  &:focus {
    outline: 0;
    border-color: transparent;
    box-shadow: ${(p) => p.$hideFocusRing ? "none" : `0 0 0 1px ${p.theme.colors.lilac500}`};
  }
`;

// src/text-area/index.ts
var TextArea = {
  Input: Input2,
  AutoWidthWrapper
};

// src/provider/cui-provider.tsx
import {
  ThemeProvider as StyledThemeProvider,
  StyleSheetManager
} from "styled-components";
import isPropValid from "@emotion/is-prop-valid";
import { jsx as jsx71 } from "react/jsx-runtime";
function CUIProvider(props) {
  const { children, theme, tooltip } = props;
  return /* @__PURE__ */ jsx71(
    StyleSheetManager,
    {
      enableVendorPrefixes: true,
      shouldForwardProp: (propName, elementToBeRendered) => {
        return typeof elementToBeRendered === "string" ? isPropValid(propName) : true;
      },
      children: /* @__PURE__ */ jsx71(StyledThemeProvider, { theme, children: /* @__PURE__ */ jsx71(TooltipProvider, { delayDuration: 0, ...tooltip, children }) })
    }
  );
}

// src/alert-dialog/index.ts
import { Root as Root18, Trigger as Trigger7 } from "@radix-ui/react-alert-dialog";

// src/alert-dialog/action.tsx
import * as RadixAlertDialog from "@radix-ui/react-alert-dialog";
import { jsx as jsx72 } from "react/jsx-runtime";
function Action2(props) {
  const { children, ...actionProps } = props;
  return /* @__PURE__ */ jsx72(RadixAlertDialog.Action, { asChild: true, ...actionProps, children: /* @__PURE__ */ jsx72(Button, { fullWidth: true, kind: "secondary", children }) });
}

// src/alert-dialog/actions.tsx
import { styled as styled50 } from "styled-components";
var Actions = styled50.div`
  display: grid;
  margin-top: 8px;
  gap: 12px;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 12px;
  grid-template-columns: 124px 196px;
`;

// src/alert-dialog/cancel.tsx
import * as RadixAlertDialog2 from "@radix-ui/react-alert-dialog";
import { jsx as jsx73 } from "react/jsx-runtime";
function Cancel2(props) {
  const { children, ...cancelProps } = props;
  return /* @__PURE__ */ jsx73(RadixAlertDialog2.Cancel, { asChild: true, ...cancelProps, children: /* @__PURE__ */ jsx73(Button, { fullWidth: true, children }) });
}

// src/alert-dialog/content.tsx
import { keyframes as keyframes3, styled as styled51 } from "styled-components";
import * as RadixAlertDialog3 from "@radix-ui/react-alert-dialog";
import { jsx as jsx74, jsxs as jsxs53 } from "react/jsx-runtime";
var animationDurationMs2 = 300;
function Content11(props) {
  const {
    withAnimation = true,
    withOverlay = true,
    withCloseButton = false,
    children,
    onExitComplete,
    closeOnEscapeDown = false,
    width = 568,
    ...contentProps
  } = props;
  return /* @__PURE__ */ jsxs53(RadixAlertDialog3.Portal, { children: [
    withOverlay && /* @__PURE__ */ jsx74(
      RadixAlertDialog3.Overlay,
      {
        asChild: true,
        "data-animation": withAnimation ? "true" : "false",
        children: /* @__PURE__ */ jsx74(OverlayElement, { style: { zIndex: contentProps.style?.zIndex } })
      }
    ),
    /* @__PURE__ */ jsxs53(
      ModalContent,
      {
        "data-animation": withAnimation ? "true" : "false",
        width,
        onAnimationStart: (e) => {
          if (onExitComplete && withAnimation && e.animationName === slideOut2.getName()) {
            setTimeout(() => onExitComplete(), animationDurationMs2);
          }
        },
        onEscapeKeyDown: (e) => {
          if (!closeOnEscapeDown) {
            e.preventDefault();
          }
        },
        ...contentProps,
        children: [
          children,
          withCloseButton && /* @__PURE__ */ jsx74(RadixAlertDialog3.Cancel, { asChild: true, children: /* @__PURE__ */ jsx74(CloseActionContainer, { children: /* @__PURE__ */ jsx74(
            ColoredIconButton,
            {
              tooltipPosition: "left",
              fill: "black100",
              "aria-label": "Cancel",
              children: CloseIcon4
            }
          ) }) })
        ]
      }
    )
  ] });
}
var slideIn = keyframes3`
  from {
    opacity: 0.75;
    transform: translateY(-45%) translateX(-50%);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) translateX(-50%);
  }
`;
var slideOut2 = keyframes3`
  from {
    opacity: 1;
    transform: translateY(-50%) translateX(-50%);
  }
  to {
    opacity: 0;
    transform: translateY(-45%) translateX(-50%);
  }
`;
var fadeIn2 = keyframes3`
  from {
    opacity: 0.75;
  }
  to {
    opacity: 1;
  }
`;
var fadeOut = keyframes3`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;
var OverlayElement = styled51.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  z-index: 15;
  background: rgba(89, 115, 166, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;

  &[data-state="open"]:not([data-animation="false"]) {
    animation: ${fadeIn2} ${animationDurationMs2}ms ease-out;
  }

  &[data-state="closed"]:not([data-animation="false"]) {
    animation: ${fadeOut} ${animationDurationMs2}ms ease-out;
  }
`;
var ModalContent = styled51(RadixAlertDialog3.Content)`
  z-index: 15;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  max-width: calc(100vw - 16px);
  width: ${(p) => p.width}px;
  min-height: 200px;
  max-height: calc(100vh - 16px);

  border-radius: 16px;
  background-color: ${(p) => p.theme.colors.white100};
  box-shadow:
    1px 2px 0px rgba(26, 31, 48, 0.06),
    0px 8px 40px rgba(89, 115, 166, 0.32);
  overflow: hidden;

  box-sizing: border-box;
  padding: 52px 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;

  &[data-state="open"]:not([data-animation="false"]) {
    animation: ${slideIn} ${animationDurationMs2}ms ease-out;
  }

  &[data-state="closed"]:not([data-animation="false"]) {
    animation: ${slideOut2} ${animationDurationMs2}ms ease-out;
  }
`;
var ActionsContainer3 = styled51.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;
var CloseActionContainer = styled51.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;
var ColoredIconButton = styled51(IconButton)`
  fill: ${(p) => p.theme.colors[p.fill]};
`;
var SVG = styled51.svg`
  cursor: pointer;
`;
var CloseIcon4 = /* @__PURE__ */ jsx74(
  SVG,
  {
    width: "28",
    height: "28",
    viewBox: "0 0 28 28",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx74("path", { d: "M14 12.6L6.30001 4.89995C5.91341 4.51335 5.28661 4.51335 4.90001 4.89995C4.51341 5.28655 4.51341 5.91335 4.90001 6.29995L12.6 14L4.90001 21.7C4.51341 22.0865 4.51341 22.7134 4.90001 23.1C5.28661 23.4865 5.91341 23.4865 6.30001 23.1L14 15.4L21.7 23.1C22.0866 23.4865 22.7134 23.4865 23.1 23.1C23.4866 22.7134 23.4866 22.0865 23.1 21.7L15.4 14L23.1 6.29995C23.4866 5.91335 23.4866 5.28655 23.1 4.89995C22.7134 4.51335 22.0866 4.51335 21.7 4.89995L14 12.6Z" })
  }
);

// src/alert-dialog/description.tsx
import { styled as styled52 } from "styled-components";
import * as RadixAlertDialog4 from "@radix-ui/react-alert-dialog";
var Description3 = styled52(RadixAlertDialog4.Description)`
  font-family: ${typography.bodyMd.fontFamily};
  font-size: ${typography.bodyMd.fontSize};
  font-weight: ${typography.bodyMd.fontWeight};
  line-height: ${typography.bodyMd.lineHeight};
  letter-spacing: ${typography.bodyMd.letterSpacing};
  margin: 0;
`;

// src/alert-dialog/title.tsx
import { styled as styled53 } from "styled-components";
import * as RadixAlertDialog5 from "@radix-ui/react-alert-dialog";
var Title3 = styled53(RadixAlertDialog5.Title)`
  font-weight: ${typography.headingFour.fontWeight};
  font-family: ${typography.headingFour.fontFamily};
  font-size: ${typography.headingFour.fontSize};
  line-height: ${typography.headingFour.lineHeight};
  letter-spacing: ${typography.headingFour.letterSpacing};
  margin: 0;
`;

// src/alert-dialog/index.ts
var AlertDialog = {
  Root: Root18,
  Actions,
  Trigger: Trigger7,
  Content: Content11,
  Title: Title3,
  Description: Description3,
  Cancel: Cancel2,
  Action: Action2
};

// src/editor-content-skeleton/editor-content-skeleton.tsx
import { styled as styled54 } from "styled-components";
import { Icon as Icon8, TickRoundIcon } from "@vericus/cadmus-icons";
import { jsx as jsx75, jsxs as jsxs54 } from "react/jsx-runtime";
var EditorContentSkeleton = () => /* @__PURE__ */ jsxs54(Root19, { children: [
  /* @__PURE__ */ jsxs54(ControlBar, { children: [
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledIcon, { iconName: "Bold" }) }),
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledIcon, { iconName: "Italic" }) }),
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledIcon, { iconName: "Underline" }) }),
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledIcon, { iconName: "More" }) }),
    /* @__PURE__ */ jsx75(ControlBarDivider, {}),
    /* @__PURE__ */ jsxs54(SelectionButton, { children: [
      /* @__PURE__ */ jsx75(Icon8, { iconName: "Color" }),
      /* @__PURE__ */ jsx75(Icon8, { iconName: "DownCaret" })
    ] }),
    /* @__PURE__ */ jsx75(ControlBarDivider, {}),
    /* @__PURE__ */ jsxs54(SelectionButton, { children: [
      /* @__PURE__ */ jsx75(Icon8, { iconName: "LeftAlign" }),
      /* @__PURE__ */ jsx75(Icon8, { iconName: "DownCaret" })
    ] }),
    /* @__PURE__ */ jsx75(ControlBarDivider, {}),
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledIcon, { iconName: "BulletList" }) }),
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledIcon, { iconName: "NumberList" }) }),
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledTickRoundIcon, { label: "Check list", size: 16 }) }),
    /* @__PURE__ */ jsx75(ControlBarDivider, {}),
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledIcon, { iconName: "Link" }) }),
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledIcon, { iconName: "Image" }) }),
    /* @__PURE__ */ jsx75(IconWrapper, { children: /* @__PURE__ */ jsx75(StyledIcon, { iconName: "Code" }) })
  ] }),
  /* @__PURE__ */ jsx75(Content12, { children: /* @__PURE__ */ jsx75(Text, { kind: "bodyMd", color: "neutralgrey500", children: "Write your answer here\u2026" }) })
] });
var Root19 = styled54.div`
  max-width: 710px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1px solid ${(p) => p.theme.colors.neutralgrey300};
`;
var IconWrapper = styled54.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  position: relative;
`;
var ControlBar = styled54.div`
  border-radius: 4px 4px 0px 0px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex-direction: row;
  background: ${(p) => p.theme.colors.grey50};
`;
var StyledIcon = styled54(Icon8)`
  fill: ${(p) => p.theme.colors.grey400};
`;
var SelectionButton = styled54.div`
  display: flex;
  flex-direction: row;
  fill: ${(p) => p.theme.colors.grey400};
  padding: 8px 10px;
`;
var ControlBarDivider = styled54.div`
  width: 1px;
  height: 20px;
  background: ${(p) => p.theme.colors.grey100};
  margin-left: 4px;
  margin-right: 4px;
`;
var StyledTickRoundIcon = styled54(TickRoundIcon)`
  opacity: 0.54;
`;
var Content12 = styled54.div`
  padding: 0px 20px;
  height: 68px;
  margin-top: 16px;
  margin-bottom: 12px;
`;

// src/phrase-input/phrase-input.tsx
import clsx18 from "clsx";

// src/limit-counter/limit-counter.tsx
import { jsx as jsx76, jsxs as jsxs55 } from "react/jsx-runtime";
function getColor(count, alertLimit, limit) {
  if (limit && count > limit) return "functionalFail";
  if (alertLimit && limit && limit - count < alertLimit)
    return "functionalAlert";
}
var LimitCounter = ({
  count = 0,
  limit,
  alertLimit,
  isCharacterCount
}) => /* @__PURE__ */ jsxs55(Text, { kind: "bodySm", color: "neutralgrey500", children: [
  count > limit ? `Excess ${isCharacterCount ? "Characters" : "words"}: ` : `Remaining ${isCharacterCount ? "Characters" : "words"}: `,
  /* @__PURE__ */ jsx76(
    Text,
    {
      kind: "bodySm",
      color: getColor(count, alertLimit, limit) ?? "primary",
      as: "span",
      children: count > 0 ? limit - count : ""
    }
  ),
  /* @__PURE__ */ jsx76(Text, { kind: "bodySm", color: "neutralgrey500", as: "span", children: count > 0 ? `/${limit}` : limit })
] });

// src/phrase-input/phrase-input.css.ts
var input3 = "_82wlq92";
var root6 = "_82wlq90";
var wrapper = "_82wlq91";

// src/phrase-input/phrase-input.tsx
import { jsx as jsx77, jsxs as jsxs56 } from "react/jsx-runtime";
function PhraseInput(props) {
  const {
    value,
    onChange,
    onBlur,
    alertLimit,
    charactersLimit,
    className,
    style,
    rightSection,
    hasError,
    hideLimit,
    type,
    color: _color,
    ...rest
  } = props;
  const charactersCount = value?.length ?? 0;
  return /* @__PURE__ */ jsxs56("div", { className: root6, children: [
    /* @__PURE__ */ jsxs56(
      "div",
      {
        className: clsx18(wrapper, className),
        style: {
          borderColor: vars.colors[getColor2(
            charactersCount,
            alertLimit,
            charactersLimit,
            hasError
          ) ?? "grey300"],
          ...style
        },
        children: [
          /* @__PURE__ */ jsx77(
            "input",
            {
              type,
              className: input3,
              value,
              onChange,
              onBlur,
              ...rest
            }
          ),
          rightSection
        ]
      }
    ),
    !hideLimit && charactersLimit && /* @__PURE__ */ jsx77(
      LimitCounter,
      {
        count: charactersCount,
        limit: charactersLimit,
        alertLimit,
        isCharacterCount: true
      }
    )
  ] });
}
function getColor2(charactersCount, alertLimit, charactersLimit, hasError) {
  if (hasError || charactersLimit && charactersCount > charactersLimit)
    return "functionalFail";
  if (alertLimit && charactersLimit && charactersLimit - charactersCount < alertLimit)
    return "functionalAlert";
}

// src/pin-input/pin-input.tsx
import { useEffect, useMemo as useMemo3, useRef as useRef2, useState as useState5 } from "react";
import { ErrorIcon } from "@vericus/cadmus-icons";

// src/pin-input/styles.tsx
import { styled as styled55, css as css8 } from "styled-components";
var Root20 = styled55.div`
  display: inline-flex;
  flex-direction: column;
  row-gap: 12px;
  align-items: center;
`;
var InputWrapper = styled55.div`
  display: flex;
  column-gap: ${(p) => `${p.gap}px`};
`;
var ErrorSection = styled55.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 4px;
`;
var IconWrapper2 = styled55.div`
  margin-top: 8px;
`;
var StyledInput2 = styled55.input`
  width: ${(p) => p.inputSize}px;
  min-width: ${(p) => p.inputSize}px;
  min-height: ${(p) => p.inputSize}px;
  height: ${(p) => p.inputSize}px;
  text-align: center;
  box-sizing: border-box;
  border-radius: 2.5px;
  border: 1px solid
    ${(p) => p.hasError ? p.theme.colors.functionalFail : p.theme.colors.neutralgrey300};
  color: ${(p) => p.theme.color};
  font-family: "acumin-pro";
  font-size: ${(p) => p.fontSize ? `${p.fontSize / 16}rem` : "unset"};
  font-weight: ${(p) => p.fontWeight};
  text-transform: ${(p) => p.textTransform ?? p.textTransform};
  &:focus-visible {
    ${(p) => p.readOnly || p.hasError ? css8`
            outline: none;
          ` : css8`
            ${p.theme.focus}
            outline: none;
            border: none;
          `}
  }
`;

// src/pin-input/utils/create-pin-array.ts
function createPinArray(length, value) {
  if (length < 1) return [];
  const values = new Array(length).fill("");
  return value ? [...value.trim().slice(0, length), ...values.slice(value.trim().length, length)] : values;
}

// src/pin-input/pin-input.tsx
import { jsx as jsx78, jsxs as jsxs57 } from "react/jsx-runtime";
var PinInput = (props) => {
  const {
    length,
    value,
    gap = 9,
    inputSize = 44,
    color: color2 = "blue800",
    fontSize,
    autoFocus,
    onChange,
    readOnly,
    ariaLabel,
    errorMsg,
    fontWeight,
    textTransform
  } = props;
  const [_value, setValues] = useState5(() => createPinArray(length, value));
  const inputsRef = useRef2([]);
  const [focusedIndex, setFocusedIndex] = useState5(-1);
  const hasError = useMemo3(
    () => errorMsg && errorMsg !== "" ? true : false,
    [errorMsg]
  );
  useEffect(() => {
    onChange?.(_value.join("").trim());
  }, [onChange, _value]);
  const setFieldValue = (val, index) => {
    const values = [..._value];
    values[index] = val;
    setValues(values);
  };
  const focusInputField = (dir, index, event) => {
    if (dir === "next") {
      const nextIndex = index + 1;
      const canFocusNext = nextIndex < (length ?? 0);
      if (canFocusNext) {
        event?.preventDefault();
        inputsRef.current[nextIndex].focus();
      }
    }
    if (dir === "prev") {
      const nextIndex = index - 1;
      const canFocusNext = nextIndex > -1;
      if (canFocusNext) {
        event?.preventDefault();
        inputsRef.current[nextIndex].focus();
      }
    }
  };
  const handleChange = (event, index) => {
    const inputValue = event.target.value;
    const nextCharOrValue = inputValue.length === 2 ? (
      // Get the last char of the inputValue, which is the latest value
      inputValue.split("")[inputValue.length - 1]
    ) : inputValue;
    if (nextCharOrValue.length < 2) {
      setFieldValue(nextCharOrValue, index);
      focusInputField("next", index);
    } else {
      setValues(createPinArray(length ?? 0, inputValue));
    }
  };
  const handleKeyDown = (event, index) => {
    const { key, shiftKey, target } = event;
    const inputValue = target.value;
    if (key === "ArrowLeft" || shiftKey && key === "Tab") {
      focusInputField("prev", index, event);
    } else if (key === "ArrowRight" || key === "Tab" || key === " ") {
      focusInputField("next", index, event);
    } else if (key === "Delete") {
      setFieldValue("", index);
    } else if (key === "Backspace") {
      setFieldValue("", index);
      if (length === index + 1) {
        if (event.target.value === "") {
          focusInputField("prev", index, event);
        }
      } else {
        focusInputField("prev", index, event);
      }
    } else if (inputValue.length > 0 && key === _value[index]) {
      focusInputField("next", index, event);
    }
  };
  const handlePaste = (event) => {
    event.preventDefault();
    const copyValue = event.clipboardData.getData("text/plain").replace(/[\n\r\s]+/g, "");
    const copyValueToPinArray = createPinArray(length ?? 0, copyValue);
    setValues(copyValueToPinArray);
    focusInputField("next", copyValueToPinArray.length - 2);
  };
  const handleFocus = (event, index) => {
    event.target.select();
    setFocusedIndex(index);
  };
  const handleBlur = () => {
    setFocusedIndex(-1);
  };
  return /* @__PURE__ */ jsxs57(Root20, { children: [
    /* @__PURE__ */ jsx78(InputWrapper, { gap, children: _value.map((pin, index) => /* @__PURE__ */ jsx78(
      StyledInput2,
      {
        fontWeight,
        value: pin,
        placeholder: focusedIndex === index && !readOnly ? "" : "\u25CB",
        inputSize,
        color: color2,
        fontSize,
        ref: (node) => {
          if (node) inputsRef.current[index] = node;
        },
        onFocus: (event) => handleFocus(event, index),
        onBlur: handleBlur,
        onPaste: (e) => handlePaste(e),
        onChange: (e) => handleChange(e, index),
        onKeyDown: (e) => handleKeyDown(e, index),
        autoFocus: autoFocus && index === 0,
        "aria-label": ariaLabel,
        readOnly,
        hasError,
        textTransform
      },
      index
    )) }),
    hasError && /* @__PURE__ */ jsxs57(ErrorSection, { children: [
      /* @__PURE__ */ jsx78(IconWrapper2, { children: /* @__PURE__ */ jsx78(ErrorIcon, { label: "", size: 16 }) }),
      /* @__PURE__ */ jsx78(Text, { kind: "bodySm", color: "functionalFail", children: errorMsg })
    ] })
  ] });
};

// src/limited-number-input/limited-number-input.tsx
import { useId as useId3 } from "react";
import clsx19 from "clsx";

// src/limited-number-input/limited-number-input.css.ts
var background2 = { white: "obroay3", grey: "obroay4" };
var error3 = "obroay5";
var input4 = "obroay7";
var inputBase = "obroay8";
var inputWrapper = "obroay6";
var root7 = { "default": "obroay0", withFocusOutline: "obroay2 obroay0" };
var widthHolder = { integer: "obroay9", decimal: "obroayb obroay9" };

// src/limited-number-input/limited-number-input.tsx
import { jsx as jsx79, jsxs as jsxs58 } from "react/jsx-runtime";
var LimitedNumberInput = (props) => {
  const {
    max,
    className,
    bgVariant = "grey",
    value,
    withError,
    hideMaxLabel = false,
    ...rest
  } = props;
  const id = useId3();
  const inputId = `limited-number-input-${id}`;
  const isInteger = value === "" || value === void 0 || Number.isInteger(parseFloat(value));
  const maxStr = formatDecimalNumber(max);
  return /* @__PURE__ */ jsxs58(
    "div",
    {
      className: clsx19(
        {
          [root7["default"]]: rest.readOnly,
          [root7["withFocusOutline"]]: !rest.readOnly,
          [background2[bgVariant]]: true,
          [error3]: withError
        },
        className
      ),
      children: [
        /* @__PURE__ */ jsxs58("div", { className: inputWrapper, children: [
          /* @__PURE__ */ jsx79(
            Text,
            {
              kind: "headingSix",
              as: "span",
              className: widthHolder[isInteger ? "integer" : "decimal"],
              children: maxStr
            }
          ),
          /* @__PURE__ */ jsx79(
            Text,
            {
              kind: "headingSix",
              as: "span",
              color: "grey500",
              className: input4,
              children: /* @__PURE__ */ jsx79(
                "input",
                {
                  id: inputId,
                  type: "number",
                  className: inputBase,
                  readOnly: rest.readOnly,
                  value,
                  max,
                  ...rest
                }
              )
            }
          )
        ] }),
        !hideMaxLabel && /* @__PURE__ */ jsxs58(
          Text,
          {
            htmlFor: inputId,
            kind: "headingSix",
            as: "label",
            color: rest.readOnly ? "grey500" : "navy300",
            children: [
              "\xA0/ ",
              maxStr
            ]
          }
        )
      ]
    }
  );
};
function formatDecimalNumber(num) {
  return num.toFixed(2).replace(/([0-9]+(\.[0-9]+[1-9])?)(\.?0+$)/, "$1");
}

// src/scroll-area/scroll-area.tsx
import { forwardRef as forwardRef36 } from "react";
import * as RadixScrollArea from "@radix-ui/react-scroll-area";
import clsx20 from "clsx";

// src/scroll-area/scroll-area.css.ts
var root8 = "hkeu7y0";
var scrollbar = "hkeu7y3";
var scrollbarThumb = "hkeu7y2";
var viewPort = "hkeu7y1";

// src/scroll-area/scroll-area.tsx
import { jsx as jsx80, jsxs as jsxs59 } from "react/jsx-runtime";
var ScrollArea = forwardRef36(
  ({
    className,
    type = "always",
    verticalOverflow = true,
    horizontalOverflow,
    children,
    ...rest
  }, ref) => /* @__PURE__ */ jsxs59(
    RadixScrollArea.Root,
    {
      className: clsx20(root8, className),
      type,
      ...rest,
      children: [
        /* @__PURE__ */ jsx80(
          RadixScrollArea.Viewport,
          {
            tabIndex: 0,
            className: viewPort,
            ref,
            children
          }
        ),
        verticalOverflow && /* @__PURE__ */ jsx80(
          RadixScrollArea.Scrollbar,
          {
            className: scrollbar,
            orientation: "vertical",
            children: /* @__PURE__ */ jsx80(RadixScrollArea.Thumb, { className: scrollbarThumb })
          }
        ),
        horizontalOverflow && /* @__PURE__ */ jsx80(
          RadixScrollArea.Scrollbar,
          {
            className: scrollbar,
            orientation: "horizontal",
            children: /* @__PURE__ */ jsx80(RadixScrollArea.Thumb, { className: scrollbarThumb })
          }
        ),
        verticalOverflow && horizontalOverflow && /* @__PURE__ */ jsx80(RadixScrollArea.Corner, {})
      ]
    }
  )
);

// src/tablet-orientation-warning/tablet-orientation-warning.tsx
import { TabletPortrait } from "@vericus/cadmus-icons";
import clsx21 from "clsx";

// src/tablet-orientation-warning/tablet-orientation-warning.css.ts
var description = "_1whd4ka2";
var img = "_1whd4ka3";
var modal = "_1whd4ka1";

// src/tablet-orientation-warning/tablet-orientation-warning.tsx
import { jsx as jsx81, jsxs as jsxs60 } from "react/jsx-runtime";
function TabletOrientationWarning({
  open,
  onClose
}) {
  return /* @__PURE__ */ jsx81(Modal2.Root, { open, onOpenChange: onClose, children: /* @__PURE__ */ jsxs60(Modal2.Content, { className: clsx21(modal), children: [
    /* @__PURE__ */ jsx81(Modal2.Title, { asChild: true, children: /* @__PURE__ */ jsx81(Text, { kind: "headingThree", children: "Please rotate your device" }) }),
    /* @__PURE__ */ jsx81("div", { className: clsx21(img), children: /* @__PURE__ */ jsx81(TabletPortrait, { label: "Tablet Portrait Icon", size: 75 }) }),
    /* @__PURE__ */ jsx81(Modal2.Description, { className: clsx21(description), children: /* @__PURE__ */ jsx81(Text, { kind: "bodyMd", color: "navy400", inline: true, children: "Cadmus is best used in landscape mode on your screen-size. Some of the functionality might not work as intended when using portrait mode." }) })
  ] }) });
}

// src/combobox/button.tsx
import { forwardRef as forwardRef37 } from "react";
import { jsx as jsx82 } from "react/jsx-runtime";
var Button9 = forwardRef37(
  (props, ref) => {
    const { className, children, ...buttonProps } = props;
    return /* @__PURE__ */ jsx82(
      SelectButton,
      {
        ref,
        role: "combobox",
        className,
        ...buttonProps,
        children
      }
    );
  }
);

// src/combobox/combobox.tsx
import { forwardRef as forwardRef38 } from "react";
import { clsx as clsx22 } from "clsx";

// src/combobox/combobox.css.ts
var content4 = "_1ta4pt30";

// src/combobox/combobox.tsx
import { jsx as jsx83 } from "react/jsx-runtime";
var Root22 = Popover.Root;
var Trigger8 = Popover.Trigger;
var Anchor2 = Popover.Anchor;
var Card3 = Popover.Card;
var Content13 = forwardRef38((props, ref) => {
  const { className, ...rest } = props;
  return /* @__PURE__ */ jsx83(
    Popover.Content,
    {
      ...rest,
      ref,
      className: clsx22(content4, className)
    }
  );
});

// src/combobox/index.tsx
var Combobox = {
  Root: Root22,
  Trigger: Trigger8,
  Content: Content13,
  Button: Button9,
  Anchor: Anchor2,
  Card: Card3
};

// src/command/index.ts
import { useCommandState } from "cmdk";

// src/command/command.tsx
import * as React5 from "react";
import clsx23 from "clsx";
import { Command as CMDK } from "cmdk";

// src/command/command.css.ts
var command = "_22vz8o2";
var empty = "_22vz8ob";
var group = "_22vz8od";
var input5 = { base: "_22vz8o7 _22vz8o6", iconVariant: "_22vz8o8 _22vz8o6" };
var inputContainer = { base: "_22vz8o3", withMargin: "_22vz8o5 _22vz8o3" };
var item2 = "_22vz8oa";
var leftIcon2 = "_22vz8oi";
var list2 = "_22vz8o9";
var loading = "_22vz8oc";
var root9 = "_22vz8o0";
var section2 = "_22vz8ok";
var separator = "_22vz8oj";

// src/command/command.tsx
import { jsx as jsx84 } from "react/jsx-runtime";
var Command = React5.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx84(CMDK, { ref, className: clsx23(root9, className), ...props }));
Command.displayName = CMDK.displayName;
var CommandList = React5.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx84(CMDK.List, { ref, className: clsx23(list2, className), ...props }));
CommandList.displayName = CMDK.List.displayName;
var CommandEmpty = React5.forwardRef((props, ref) => /* @__PURE__ */ jsx84(CMDK.Empty, { ref, className: empty, ...props }));
CommandEmpty.displayName = CMDK.Empty.displayName;
var CommandLoading = React5.forwardRef((props, ref) => /* @__PURE__ */ jsx84(CMDK.Loading, { ref, className: loading, ...props }));
CommandLoading.displayName = CMDK.Loading.displayName;

// src/command/dialog.tsx
import { jsx as jsx85 } from "react/jsx-runtime";
function CommandDialog({ children, ...props }) {
  return /* @__PURE__ */ jsx85(Modal2.Root, { ...props, children: /* @__PURE__ */ jsx85(Modal2.Content, { withCloseButton: false, children: /* @__PURE__ */ jsx85(Command, { className: command, children }) }) });
}

// src/command/group.tsx
import * as React6 from "react";
import clsx24 from "clsx";
import { Command as CMDK2 } from "cmdk";
import { jsx as jsx86, jsxs as jsxs61 } from "react/jsx-runtime";
var CommandGroup = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx86(CMDK2.Group, { ref, className: clsx24(group, className), ...props }));
CommandGroup.displayName = CMDK2.Group.displayName;
function GroupHeading({
  heading,
  className
}) {
  return /* @__PURE__ */ jsxs61("div", { className: clsx24(section2), children: [
    /* @__PURE__ */ jsx86(Text, { kind: "label", as: "div", children: heading }),
    /* @__PURE__ */ jsx86("span", { className: clsx24(separator, className) })
  ] });
}

// src/command/input.tsx
import {
  forwardRef as forwardRef41
} from "react";
import clsx25 from "clsx";
import { Command as CommandPrimitive } from "cmdk";
import { jsx as jsx87, jsxs as jsxs62 } from "react/jsx-runtime";
var CommandInput = forwardRef41(
  ({ className, icon: leftIcon3, withMargin = true, ...props }, ref) => /* @__PURE__ */ jsxs62(
    "div",
    {
      className: clsx25(
        inputContainer[withMargin ? "withMargin" : "base"],
        className
      ),
      children: [
        leftIcon3 && /* @__PURE__ */ jsx87("div", { className: clsx25(leftIcon2), children: leftIcon3 }),
        /* @__PURE__ */ jsx87(
          CommandPrimitive.Input,
          {
            ref,
            className: clsx25(input5[leftIcon3 ? "iconVariant" : "base"]),
            ...props
          }
        )
      ]
    }
  )
);
CommandInput.displayName = CommandPrimitive.Input.displayName;

// src/command/item.tsx
import { forwardRef as forwardRef42 } from "react";
import clsx26 from "clsx";
import { Command as CommandPrimitive2 } from "cmdk";
import { jsx as jsx88, jsxs as jsxs63 } from "react/jsx-runtime";
var CommandItem = forwardRef42(
  ({
    className,
    checkable,
    icon: icon5,
    children,
    contentNode,
    checkBoxColor,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx88(CommandPrimitive2.Item, { ref, asChild: true, ...props, children: contentNode ?? /* @__PURE__ */ jsxs63("div", { className: clsx26(item2, className), children: [
      checkable && /* @__PURE__ */ jsx88(
        Checkbox,
        {
          checked: props["data-selected"] !== void 0,
          readOnly: true,
          style: { marginRight: -4 },
          colorVariant: checkBoxColor
        }
      ),
      icon5 && icon5,
      /* @__PURE__ */ jsx88(Text, { kind: "bodyLine", as: "span", color: "inherit", children })
    ] }) });
  }
);
CommandItem.displayName = CommandPrimitive2.Item.displayName;

// src/command/radio-item.tsx
import { forwardRef as forwardRef43 } from "react";
import clsx27 from "clsx";
import { Command as CommandPrimitive3 } from "cmdk";
import { jsx as jsx89, jsxs as jsxs64 } from "react/jsx-runtime";
var CommandRadioItem = forwardRef43(
  ({ className, icon: icon5, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx89(CommandPrimitive3.Item, { ref, asChild: true, ...props, children: /* @__PURE__ */ jsxs64("div", { className: clsx27(item2, className), children: [
      /* @__PURE__ */ jsx89(
        RadioIndicator,
        {
          $size: "md",
          $color: "primary",
          "data-state": props["data-selected"] === "true" ? "checked" : void 0
        }
      ),
      icon5 && icon5,
      /* @__PURE__ */ jsx89(Text, { kind: "bodyLine", as: "span", color: "inherit", children })
    ] }) });
  }
);
CommandRadioItem.displayName = CommandPrimitive3.Item.displayName;

// src/command/separator.tsx
import { forwardRef as forwardRef44 } from "react";
import clsx28 from "clsx";
import { Command as CMDK3 } from "cmdk";
import { jsx as jsx90, jsxs as jsxs65 } from "react/jsx-runtime";
var CommandSeparator = forwardRef44(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx90(CMDK3.Separator, { ref, asChild: true, ...props, children: children ? /* @__PURE__ */ jsxs65("div", { className: clsx28(section2), children: [
  /* @__PURE__ */ jsx90(Text, { kind: "label", as: "div", children }),
  /* @__PURE__ */ jsx90("span", { className: clsx28(separator, className) })
] }) : /* @__PURE__ */ jsx90("div", { className: clsx28(separator, className) }) }));
CommandSeparator.displayName = CMDK3.Separator.displayName;

// src/command/index.ts
var Command2 = {
  Root: Command,
  Dialog: CommandDialog,
  Empty: CommandEmpty,
  Group: CommandGroup,
  GroupHeading,
  Input: CommandInput,
  Item: CommandItem,
  List: CommandList,
  Separator: CommandSeparator,
  Loading: CommandLoading,
  RadioItem: CommandRadioItem
};

// src/progress-dots/progress-dots.css.ts
var dot = "xrpkng1";
var slotCell = "xrpkng0";

// src/progress-dots/progress-dots.tsx
import { jsx as jsx91, jsxs as jsxs66 } from "react/jsx-runtime";
function ProgressDots(props) {
  const {
    filledCount,
    filledColor,
    unfilledCount,
    unfilledColor = colors.bgrey500
  } = props;
  return /* @__PURE__ */ jsxs66("div", { className: slotCell, children: [
    Array.from({
      length: filledCount
    }).map((_, index) => /* @__PURE__ */ jsx91(
      "div",
      {
        className: dot,
        style: { background: filledColor }
      },
      index
    )),
    unfilledCount > 0 && Array.from({ length: unfilledCount }).map((_, index) => /* @__PURE__ */ jsx91(
      "div",
      {
        className: dot,
        style: { background: unfilledColor }
      },
      index
    ))
  ] });
}

// src/animated-success/animated-success.css.ts
var circleBase = "_146mfk45";
var pulseCircle = "_146mfk46";
var successCircle = "_146mfk47";
var svgContainer = "_146mfk43";
var svgStyle = "_146mfk44";
var tick = "_146mfk48";

// src/animated-success/index.tsx
import { jsx as jsx92, jsxs as jsxs67 } from "react/jsx-runtime";
var AnimatedSuccess = ({
  success
}) => {
  return /* @__PURE__ */ jsx92("div", { className: svgContainer, children: /* @__PURE__ */ jsxs67(
    "svg",
    {
      className: svgStyle,
      xmlns: "http://www.w3.org/2000/svg",
      width: "100",
      height: "100",
      viewBox: "0 0 48 48",
      "aria-hidden": "true",
      children: [
        /* @__PURE__ */ jsx92(
          "circle",
          {
            cx: "24",
            cy: "24",
            r: "18",
            className: `${circleBase} ${success ? successCircle : pulseCircle}`
          }
        ),
        success && /* @__PURE__ */ jsx92(
          "path",
          {
            className: tick,
            fill: "none",
            stroke: "#FFF",
            strokeWidth: "6",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeMiterlimit: "10",
            d: "M14 27l5.917 4.917L34 17"
          }
        )
      ]
    }
  ) });
};

// src/number-input/number-input.tsx
import { useRef as useRef3 } from "react";
import {
  NumericFormat
} from "react-number-format";
import { jsx as jsx93 } from "react/jsx-runtime";
function NumberInput(props) {
  const {
    width = "auto",
    isAllowed,
    max = Number.MAX_VALUE,
    allowNegative = true,
    min = allowNegative ? -Number.MAX_VALUE : 0,
    ...other
  } = props;
  const numberInputRef = useRef3(null);
  const handleIsAllowedValidator = (values) => {
    const { floatValue } = values;
    if (floatValue === void 0) return true;
    if (floatValue < min || floatValue > max) return false;
    return isAllowed ? isAllowed(values) : true;
  };
  return /* @__PURE__ */ jsx93(
    NumericFormat,
    {
      width,
      customInput: Input,
      getInputRef: numberInputRef,
      allowNegative,
      isAllowed: handleIsAllowedValidator,
      ...other
    }
  );
}

// src/index.ts
import { createGlobalTheme as createGlobalTheme2 } from "@vanilla-extract/css";
import { AccessibleIcon } from "@radix-ui/react-accessible-icon";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import * as ToggleGroup3 from "@radix-ui/react-toggle-group";
import * as Toolbar2 from "@radix-ui/react-toolbar";
export {
  AccessibleIcon,
  Accordion2 as Accordion,
  AccordionContent,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  ActionInput,
  Alert,
  AlertDialog,
  Anchored,
  AnimatedSuccess,
  AvatarIcon,
  Block,
  Breadcrumb,
  Button,
  CRITERION_COLORS,
  CUIProvider,
  Card,
  CardSkeleton,
  Checkbox,
  Chip,
  ColorPicker,
  ColorPickerMenu,
  Colored,
  Combobox,
  Command2 as Command,
  ControlAction,
  ControlButton,
  ControlButtonText,
  Controlbar,
  CssBaseline,
  DEFAULT_HIGHLIGHT_COLOR,
  DEFAULT_TEXT_COLOR,
  DatePicker,
  DatePickerInput,
  DateTimePicker,
  Desk,
  DesktopAndTablet,
  DesktopOnly,
  Divider,
  DropdownMenu,
  EditorContentSkeleton,
  EmbeddedVideo,
  Emoji,
  ErrorMessage,
  FadeIn,
  FadeInLeft,
  FadeInTop,
  FileCard,
  FileCardV2,
  FilterPill,
  FocusAnimation,
  FormattedDisplayInput,
  Grid,
  HIGHLIGHT_COLORS,
  Icon,
  IconButton,
  Input,
  Legend,
  LimitCounter,
  LimitedNumberInput,
  LinkButton,
  LinkButtonV2,
  LinkCard,
  MobileOnly,
  Modal2 as Modal,
  NumberInput,
  ParagraphSkeleton,
  PhraseInput,
  Pill,
  PinInput,
  Popover,
  ProgressDots,
  Radio,
  RadioGroup,
  RadioIndicator,
  RangeSlider,
  RequirementsSkeleton,
  ScrollArea,
  Search,
  SearchList,
  SectionLabel,
  SectionSkeleton,
  SelectButton,
  Skeleton,
  Spacer,
  Switch,
  TEXT_COLORS,
  TabletAndMobile,
  TabletOnly,
  TabletOrientationWarning,
  Tabs,
  Text,
  TextArea,
  TimePickerInput,
  TitleSkeleton,
  ToggleGroup3 as ToggleGroup,
  Toolbar2 as Toolbar,
  Tooltip,
  TooltipProvider,
  VisuallyHidden,
  avatarColoursets,
  buildRootTheme,
  colors,
  createCuiTheme,
  createGlobalTheme2 as createGlobalTheme,
  defaultColourset,
  defaultTheme,
  desktop,
  easeInExpo,
  easeOutCirc,
  easeOutQuart,
  fadeIn,
  fileTypeToIconMap,
  fontFamily,
  formatDecimalNumber,
  getAvatarColourset,
  getFileType,
  installGlobalTheme,
  largeDesktop,
  levels,
  mergeRefs,
  mobile,
  screens,
  tablet,
  typography,
  useCommandState,
  vars
};
//# sourceMappingURL=index.mjs.map